// English locale, structurally mirrored from nb.ts (the canonical
// source): every key in nb.ts has an entry here. The i18n parity test
// (src/i18n/__tests__/parity.spec.ts) fails the build on any drift.
// Edit nb.ts first, then add the matching English string here.

export default {
  portalFinancial: {
    refreshFailed:
      'Your cancellation is registered, but we could not retrieve the updated refund status. Try again or reopen the customer portal.',
    heading: 'Invoices, receipts and refunds',
    explanation:
      'Documents appear in number order. A credit note corrects a document, but does not confirm that the money has reached your account.',
    refundWithoutDocument: 'No documents were issued for this booking.',
    refundWithoutDocumentBody:
      'The status below is what we know about the refund. Contact the provider if you need documentation.',
    reverses: 'Reverses document {number}',
    download: 'Download PDF {number}',
    downloadFailed:
      'Could not retrieve the document. Try again, or reopen the customer portal if your sign-in has expired.',
    durableAccess:
      'Find available invoices, receipts, credit notes and current refund status in the customer portal. You may need to sign in by email.',
    openPortal: 'Open the customer portal',
    kind: {
      sale: 'Receipt',
      invoice: 'Invoice',
      credit_note: 'Credit note',
      credit_note_reversal: 'Credit note reversal',
    },
    refund: {
      pending: 'The refund is awaiting processing. The money has not been confirmed returned yet.',
      succeeded:
        'The refund has completed. Your bank may take a few days to show the amount in your account.',
      failed: 'The refund failed. Contact the provider for help.',
      canceled: 'The refund was stopped. Contact the provider for help.',
      unknown: 'We have not confirmed the refund status. Contact the provider if you need help.',
    },
  },
  paymentDisputes: {
    title: 'Payment cases',
    subtitle: 'Stripe disputes, response deadlines and resolved history.',
    caseTitle: 'Payment dispute',
    bookingTitle: 'Payment cases for this meeting',
    searchLabel: 'Find a case',
    searchPlaceholder: 'Customer, email, meeting title or case reference',
    search: 'Search',
    loading: 'Loading payment cases …',
    empty: 'No payment cases yet',
    emptyBody: 'Disputes from Stripe appear here when they are recorded.',
    searchEmpty: 'No cases found',
    searchEmptyBody: 'No recorded cases match this search.',
    notFound: 'Case not found',
    unknown: 'Not reported by Stripe',
    back: 'Back to payment cases',
    viewAll: 'View all cases',
    pages: 'Payment case pages',
    previous: 'Previous',
    next: 'Next',
    caseId: 'Stripe case reference',
    reason: 'Stripe reason',
    stripeStatus: 'Stripe status',
    deadline: 'Response deadline',
    noDeadline: 'The bank does not accept a response',
    opened: 'Opened',
    closed: 'Closed',
    evidence: 'Evidence in Stripe',
    evidenceSaved: 'Evidence has been saved',
    evidenceNone: 'No evidence has been saved',
    submissions: 'Number of submissions in Stripe',
    updated: 'Last update from Stripe',
    evidenceHelp:
      'Saved evidence does not necessarily mean a response has been submitted. Submission and review take place in Stripe.',
    deletedBooking: 'Meeting deleted',
    deletedBookingHelp:
      'The meeting has been deleted. The payment case is retained without the meeting’s customer information.',
    openBooking: 'Open meeting',
    handoffTitle: 'Respond in Stripe',
    handoff:
      'Open Stripe and find the case using the reference above. Respond and submit evidence in Stripe. Stemne does not submit evidence for you.',
    followTitle: 'Check the case in Stripe',
    follow:
      'Open Stripe and find the case using the reference above. Check its latest status and any available options in Stripe.',
    testMode: 'This case belongs to Stripe test mode.',
    openStripe: 'Open Stripe',
    stripeLogin: 'Sign in directly to Stripe',
    reasonLabel: {
      fraudulent: 'The customer does not recognise the charge',
      duplicate: 'The customer believes they were charged twice',
      product_not_received: 'The customer says they did not receive the service',
      product_unacceptable: 'The customer says the service was not as agreed',
      subscription_canceled: 'The customer says the arrangement had been cancelled',
      credit_not_processed: 'The customer is waiting for a refund',
      unrecognized: 'The customer does not recognise the charge',
      general: 'No further reason given',
      bank_cannot_process: 'The bank cannot process the payment',
    },
    statusLabel: {
      won: 'Decided in your favour',
      lost: 'Decided in the customer’s favour',
      warning_closed: 'Inquiry closed without a formal dispute',
      prevented: 'Dispute prevented',
      charge_refunded: 'Charge refunded',
    },
    state: {
      response_required: 'Waiting for your response',
      response_overdue: 'Response deadline has passed',
      response_not_allowed: 'The bank does not accept a response',
      under_review: 'Under review',
      resolved: 'Resolved case',
      unknown: 'Check the status in Stripe',
    },
    guidance: {
      response_required: 'Check the response deadline and respond in Stripe.',
      response_overdue:
        'The recorded response deadline has passed. Check Stripe for the latest status and any available options.',
      response_not_allowed:
        'Stripe reports that the customer’s bank does not accept a response in this case. You can follow the case in Stripe, but you cannot submit evidence.',
      under_review: 'The case is under review. Check Stripe for updates.',
      resolved: 'The case is closed. Stripe’s outcome is shown below.',
      unknown: 'Stemne does not recognise this status yet. Open Stripe to see what it means.',
    },
  },
  a11y: {
    carousel: 'carousel',
    carouselSlide: 'slide',
    carouselSlideLabel: '{position} of {total}',
  },

  imageLightbox: {
    label: 'Enlarged image',
    previous: 'Previous',
  },
  common: {
    back: 'Back',
    continue: 'Continue',
    next: 'Next',
    cancelBooking: 'Cancel meeting',
    abort: 'Cancel',
    clear: 'Clear',
    hours: 'Hour',
    minutes: 'Min',
    now: 'Now',
    save: 'Save',
    selectTime: 'Select time',
    copy: 'Copy',
    copied: 'Copied',
    copyLink: 'Copy link',
    copiedLink: '✓ Copied',
    loading: 'Loading...',
    loadingBooking: 'Loading meeting...',
    loadingSlots: 'Loading available times...',
    loadingMeetings: 'Loading your meetings...',
    loadingServices: 'Loading services',
    privacy: 'privacy',
    terms: 'terms',
    faq: 'faq',
    termsLink: 'terms of service',
    privacyLink: 'privacy policy',
    stemneUrl: 'stemne.no',
    copyright: '© 2026 Stemne AS. All rights reserved.',
    priceVatIncludedHint: 'Enter the price including VAT.',
    noSlotsToday: 'No available times this day.',
    min: '{mins} min',
    online: 'online',
    withHost: 'with {host}',
    inClinic: 'at the clinic',
    onlineOrInClinic: 'online or at the clinic',
    howToJoin: 'How to join',
    joinLinkHint: 'The meeting link appears here and in your email before the meeting starts.',
    remove: 'Remove',
    edit: 'Edit',
    close: 'Close',
    chooseLanguage: 'Choose language',
    language: 'Language',
    login: 'Log in',
    goToDashboard: 'Go to dashboard',
    backToHome: 'Back to home',
    bookingWith: "you're booking with",
    switchPerson: 'switch person',
  },

  relativeTime: {
    justNow: 'just now',
    minsAgo: '{n} min ago',
    hoursAgo: '{n} h ago',
    daysAgo: '{n} day ago | {n} days ago',
    inMins: 'in {n} min',
    inHours: 'in {n} h',
    inDays: 'in {n} day | in {n} days',
    countdownDays: '{count} day | {count} days',
    countdownHourUnit: 'h',
    countdownMinuteUnit: 'm',
    countdownSecondUnit: 's',
  },
  loadError: {
    title: 'Could not load this',
    retry: 'Try again',
    // Whole-page variant: a lazy route chunk failed to load (#3874).
    pageTitle: "We couldn't load the page",
    pageBody: "Try reloading the page. If that doesn't help, wait a little and try again.",
  },

  errors: {
    generic: 'Something went wrong. Try again.',
    network: 'Network error. Check your connection and try again.',
    server: 'Something went wrong on our end. Try again shortly.',
    badRequest: 'Something in the request was invalid. Check the fields and try again.',
    validation: 'Check the fields and try again.',
    unauthorized: 'You need to log in again.',
    forbidden: "You don't have access to this.",
    notFound: "We couldn't find what you were looking for.",
    conflict: 'This is already in use or was just changed.',
    video_start_too_early: 'The video meeting can start 15 minutes before its scheduled time.',
    tooLarge: 'The file is too large.',
    rateLimited: 'Too many attempts. Try again shortly.',
    gone: 'The link has expired. Ask for a new one.',
    // Group departures (stories/09).
    departure_full:
      'There are not enough seats left on this departure. Pick another departure or fewer seats.',
    already_booked: 'You already have a booking at this time.',
    departure_closed:
      'This departure has been cancelled or has already left. Pick another departure.',
    invalid_seats: 'Choose between 1 and 20 seats.',
    departure_payments_unsupported:
      'This service takes payment at booking, which is not supported for scheduled departures yet.',
    departure_booking:
      'This is a booking on a scheduled departure. Cancel it and book another departure instead.',
    departure_required: 'This service is booked through scheduled departures. Pick a departure.',
    departure_service_mismatch:
      'That departure belongs to another service. Pick the departure again.',
    departure_exists: 'There is already a departure for this service at this time.',
    departure_has_payments:
      'This departure has paid bookings. Refund and cancel them one by one before you cancel the departure.',
    departure_cancel_reason: 'Write a short reason (up to 500 characters).',
    departure_cancel_forbidden: 'Only the departure’s guide or an administrator can cancel it.',
    guide_has_departures:
      'This advisor leads upcoming departures. Cancel them or change the guide before you deactivate or remove the advisor.',
    departure_service_in_use:
      'This service has upcoming departures. Cancel them before you hide or delete the service.',
    booking_model_locked:
      'This service has active bookings or upcoming departures, so scheduled departures cannot be switched on or off right now.',
    email_required: 'Enter an email address.',
    invalid_email: "That email address doesn't look right.",
    // Signup verification code (#3316). signup_code_invalid deliberately does
    // not say how many attempts remain — a countdown is a progress bar for
    // whoever is guessing.
    // Not a failure: the link and the code open the SAME signup, so whichever
    // is used second meets a signup that already completed. Its own code, so
    // the copy says "you are done" rather than "this link is broken".
    signup_already_verified: 'This email is already confirmed',
    signup_code_invalid: 'That code does not match. Check the email and try again.',
    signup_code_locked: 'Too many attempts on this code. Request a new code.',
    signup_code_unavailable: 'This signup was not issued a code. Choose Resend to get a code.',
    name_required: 'Enter a name.',
    slot_taken: 'That time is no longer available. Pick another.',
    series_slot_conflict:
      'That time is not available for every meeting in the series. Pick another.',
    booking_not_found: "We couldn't find this meeting.",
    meeting_not_finished: 'This meeting has not finished yet. You can send the summary afterwards.',
    invitee_is_primary: 'This person is already a participant in the meeting.',
    invitee_exists: 'This person has already been invited.',
    too_many_invitees: 'The meeting has too many participants.',
    group_full: 'The meeting is full.',
    video_duration_limit_exceeded:
      'The video meeting is longer than your subscription allows. Choose a shorter duration.',
    availability_invalid_time: 'Write the time as hours and minutes, for example 09:00.',
    availability_invalid_range: 'The end time must be after the start time.',
    vat_status_unknown:
      'We do not know whether your business is registered in the Norwegian VAT register, so we cannot produce correct receipts. Add your organisation number under Company and we will fetch the status from Brønnøysundregisteret.',
    vat_status_unknown_not_owner:
      'We do not know whether your business is registered in the Norwegian VAT register, so we cannot produce correct receipts. Ask an owner to add the organisation number under Company.',
    vat_registry_unavailable:
      'Brønnøysundregisteret did not respond, so we could not check your VAT registration. Try again shortly.',
    signup_token_not_found: 'The confirmation is invalid or expired. Request a new code.',
    signup_token_used: 'The confirmation has already been used, or replaced by a newer one.',
    signup_token_expired: 'The confirmation has expired.',
    signup_session_mismatch: 'We could not confirm your email in this browser.',
    signup_provisioning_failed: 'We could not create the account. Try confirming again shortly.',
  },
  // Illustrative props for the hero browser mockups. Practitioner names,
  // cities and kr prices stay literal in landing-data.ts — only the role,
  // service names and the free label are language-dependent.
  landingMockup: {
    roleCouplesTherapist: 'Couples therapist',
    roleNaprapath: 'Naprapath',
    roleLeadershipCoach: 'Leadership coach',
    rolePhysiotherapist: 'Physiotherapist',
    rolePersonalTrainer: 'Personal trainer',
    serviceIntroCall: 'Intro call',
    serviceCouplesFirst: 'Couples therapy, first session',
    serviceFollowUp: 'Follow-up',
    serviceFirstSession: 'First session',
    serviceFirstConsultation: 'First consultation',
    serviceTreatment: 'Treatment',
    serviceStrategySession: 'Strategy session',
    serviceSparring: 'Sparring',
    serviceIntroSession: 'Intro session',
    serviceStrengthSession: 'Strength session',
    priceFree: 'free',
  },
  landing: {
    hero: {
      eyebrow: 'For people who work with people',
      headingFirst: 'More time for',
      headingSecond: 'the people you meet.',
      intro:
        'Booking, notes and follow-up in one place. From the first appointment to the next meeting.',
      secondary: 'See how it works',
      trust: 'No commitment. Norwegian customer support.',
      detailFirst: 'Your client finds a time.',
      detailSecond: 'You arrive prepared.',
      carouselLabel: 'Who uses Stemne?',
      audienceLink: 'Stemne for {audience}',
      seeAll: 'View all',
      ptDescription:
        'From the first training session to the next goal. Keep track of appointments and follow-up.',
      practitionerDescription:
        'Give the next treatment your full attention. Your appointments are all here.',
      advisorDescription:
        'Good conversations deserve thoughtful follow-up. Keep meetings, notes and next steps together.',
      ptAlt: 'A personal trainer guides a client at the gym',
      practitionerAlt: 'A practitioner and client in a bright treatment room',
      advisorAlt: 'An advisor and client talking around a table',
    },
    audiences: {
      heading: 'Find Stemne for your profession',
      ptTitle: 'Personal trainers',
      practitionerTitle: 'Practitioners',
      advisorTitle: 'Advisors',
      ptDescription: 'Keep appointments, training notes and follow-up with each client.',
      practitionerDescription:
        'Let clients book themselves, and find what you agreed on last time.',
      advisorDescription:
        'Keep meetings, notes and next steps together before your next conversation.',
    },
    nav: {
      features: 'Features',
      pricing: 'Pricing',
      advisors: 'Advisors',
      login: 'Log in',
      getStarted: 'Get started',
    },
    meta: 'meetings · for people who take meetings',
    heading: 'A quiet, ',
    headingItalic: 'beautiful',
    headingEnd: ' way to welcome the people you meet.',
    // Capital S in prose. The wordmark is lowercase, but that's a logo — in a
    // sentence Stemne is a proper noun, and the same page already writes
    // "Stemne keeps the books" and "Stemne AS".
    body: 'Stemne is a booking page for therapists, naprapaths, physios, coaches, advisors, and anyone else with planned conversations or treatments. On video or in the clinic. You get your own address at {subdomain}, and people book, meet, and follow up in one place.',
    heroCallout: 'Booking, video, and follow-up in one place.',
    subdomain: 'yourname.stemne.no',
    ctaPrimary: 'Create your page, free for 3 months',
    ctaSecondary: 'See how it works →',
    trustMeta: 'no commitment · Norwegian support · hosted in the EU',
    featuresHeading: 'Fewer emails. Better conversations.',
    feature1Title: 'Your own address',
    feature1Body:
      'Each page lives on a quiet, stable domain. yourname.stemne.no is easy to remember and natural to share.',
    feature2Title: 'Prepare, meet, follow up',
    feature2Body:
      'Ask the customer what they want to discuss before the meeting, keep prep notes open while you meet, send an encrypted recap afterwards. Everything on one screen.',
    feature3Title: 'Video, calendar, notifications',
    feature3Body:
      "Video rooms built in, no downloads for the customer. Connect Google Calendar. Quiet hours and per-kind notification controls so your inbox doesn't overflow.",
    feature4Title: 'Encrypted notes you own',
    feature4Body:
      'Meeting notes, recaps, and calendar credentials are encrypted at rest (AES-256). You pass the privacy test when a customer asks.',
    feature5Title: 'Remember every athlete between sessions',
    feature5Body:
      'Stemne keeps notes and session history with the athlete. You or a colleague open the history when you need to see what you worked on last time.',
    feature6Title: 'Made in Norway, hosted in the EU',
    feature6Body:
      "Data lives with Hetzner in Germany, inside the EU and subject to the GDPR, and never leaves the EU/EEA without a valid transfer basis. When a customer asks where it's kept, you have the answer ready.",
    continuityMeta: 'continuity',
    continuityHeading: 'The same advisor every time',
    continuityLede:
      'Clients never have to choose. When someone comes back, they land with the advisor they met last time.',
    continuity1Title: 'The relationship follows the client.',
    continuity1Body:
      'Book a client again and they are set up with the same advisor as last time. Without anyone having to remember to arrange it.',
    continuity2Title: 'The team shares the clients.',
    continuity2Body:
      'Several advisors on one page, but each client keeps theirs. If the advisor is busy, a colleague takes the session, and the history is already there.',
    continuity3Title: 'Clients skip the choosing.',
    continuity3Body:
      'The client picks a service and a time, not a person. You decide who takes what, behind the scenes.',
    whoMeta: 'who uses stemne',
    whoHeading: 'One tool. Many ways to meet.',
    whoTagPaid: 'with price',
    whoTagFree: 'no price',
    whoTagOptional: 'optional',
    who1Title: 'Therapists',
    who1Body:
      'Clients book themselves. You control breaks, follow-up, and invoicing, if you invoice.',
    who10Title: 'Personal trainers & coaches',
    who10Body:
      'Athletes book sessions themselves. You plan and log the session, and find notes and history when you or a colleague need them.',
    who2Title: 'Key account managers',
    who2Body:
      'Recurring quarterly check-ins and status meetings with your customers. One link to share, one calendar that always lines up.',
    who3Title: 'Customer success & support',
    who3Body:
      'Onboarding calls, health checks, escalations. Customers pick a time that works for them.',
    who4Title: 'Mentors & advisors',
    who4Body: 'Offer a fixed number of sessions per month, free or paid. Stemne keeps the books.',
    who5Title: 'Recruiters & headhunters',
    who5Body:
      'First-round screenings without the email back-and-forth. Calendar always visible, never overbooked.',
    who6Title: 'Freelancers & strategists',
    who6Body: 'Packages, projects, follow-ups: one address, one entry point for every request.',
    who7Title: 'Clinicians & practitioners',
    who7Body:
      "Naprapaths, osteopaths, physios, chiropractors. Clinic bookings without unnecessary friction, without a video system you don't need.",
    who8Title: 'Lawyers and legal advisors',
    who8Body:
      'Client meetings with discreet booking. Notes stay at the workplace, not in the inbox. Encrypted calendar connections so the history stays where it belongs.',
    who9Title: 'Accountants and tax advisors',
    who9Body:
      "Quarterly meetings and year-end closings with your clients. Stemne remembers last year's history when you're sitting there in March trying to recall what you agreed on.",
    pricingMeta: 'pricing',
    pricingHeading: 'Three plans. You pick the size.',
    pricingLede:
      'Start with what fits today. Switch plans as your practice grows. Everything moves with you.',
    tier: {
      solo: {
        name: 'Solo',
        price: '99 kr',
        period: 'per month',
        seats: 'For you alone.',
        body: 'Your own address, unlimited bookings, encrypted notes, prep view, encrypted recaps. Free for 3 months.',
        cta: 'Start free',
      },
      team: {
        name: 'Team',
        price: '449 kr',
        period: 'per month',
        seats: 'Up to 5 users.',
        body: 'Invite advisors, share encrypted notes, redistribute unowned meetings in two clicks. Your own email sender and shared customer list included.',
        cta: 'Start free',
      },
      vekst: {
        name: 'Growth',
        price: '899 kr',
        period: 'per month',
        seats: 'Up to 15 users.',
        body: 'Audit log over admin actions, API access for integrations, priority support. Trusted by established practices.',
        cta: 'Start free',
      },
    },
    pricingExplainer: 'No per-booking fees. No surprises. Switch plans anytime.',
    pricingMva: 'Prices exclude VAT.',
    pricingSecurity:
      'Meeting notes and calendar credentials are encrypted at rest (AES-256) on every plan.',
    pricingVideoCap:
      '* Video meetings last up to 60 minutes. Need longer meetings? You can buy extended duration of up to 120 minutes.',
    pricingEnterprise: 'Bigger team than 15? Contact us for a custom plan.',
    pricingFeatures: [
      'Your address at yourname.stemne.no',
      'Passwordless login with passkeys',
      'Unlimited meetings and meeting types',
      'Price per meeting is optional, always',
      'Video rooms built in, no downloads *',
      'Google Calendar sync',
      'Prep view with "ask about this" and encrypted recap',
      'Quiet hours and per-kind notification controls',
      'Email confirmations, reminders, and automatic rebooking',
      'Import existing customers from Excel or your CRM',
      'Customer support in Norwegian, weekdays',
      'Your data, in the EU',
    ],
    allFeaturesToggle: 'See everything included',
    finalHeading: 'Ready when you are.',
    finalBody: 'You can be live before the coffee gets cold.',
    finalCta: 'Get started',
    footerUrl: 'stemne.no',
  },
  signup: {
    back: '← Back',
    heading: 'Create your page.',
    body: "We'll send you a link by email. No password to remember. You can be live within five minutes.",
    nameLabel: 'Name',
    namePlaceholder: 'First Last',
    emailLabel: 'Email',
    submit: 'Send me a link',
    termsPrefix: 'By continuing you accept the',
    termsAnd: 'and',
    termsSuffix: '.',
  },
  verify: {
    meta: 'check your inbox',
    // Hedged, like resentHeading: /api/signup returns 200 on the
    // silently-throttled path too, so the SPA cannot truthfully assert a
    // send happened. Reads as "Look for a code to <address>".
    heading: 'Look for a code',
    body: 'Enter the six-digit code from the email here to continue. It is valid for 24 hours. If nothing shows up within a couple of minutes, check your spam folder and try sending a new one.',
    confirmed: 'I have entered the code',
    resend: 'Resend',
    resendAlert: "We're sending a new code shortly.",
    helpPrefix: "Can't find it? Check your spam folder, or",
    helpLink: 'try a different address',
  },
  subdomain: {
    back: '← Back',
    heading: 'Choose your address.',
    body: "This will be your public page, where customers find and book you. You can change it later, but preferably don't.",
    placeholder: 'yourname',
    suffix: '.stemne.no',
    statusEmpty: 'start typing to check ...',
    statusShort: 'at least {min} characters',
    statusLong: 'max {max} characters',
    statusChecking: 'checking ...',
    statusTaken: '{slug}{suffix} is taken, try something else',
    statusTransient: "we can't check that address right now, try again in a moment",
    statusBlocked: 'this address is not allowed, pick another',
    statusBadFormat: 'invalid address: lowercase letters, digits and hyphens only',
    statusAvailable: 'available, {slug}{suffix} is yours',
    tipLabel: 'Tip:',
    tipBody:
      'use your first name or company name. Short addresses are easier to remember and share.',
    submit: 'Continue →',
  },
  login: {
    heading: 'Log in',
    emailLabel: 'Email',
    submit: 'Continue',
    continueTo: 'Continue to {host}',
    pickWorkspace: 'Choose workspace',
    signOutOf: 'Sign out of {host}',
    signOutBody: 'The shortcut leaves this device. If you are signed in here, we end that session.',
    signOutConfirm: 'Sign out',
    signOutError: 'We could not sign you out. Try again.',
    orUseEmail: 'or get a login link by email',
    emptyError: 'Enter your email address.',
    sentHeading: 'Check your inbox',
    sentBody:
      "If the account exists, we've sent a login link to {email}. Click the link to log in to your page.",
    sentHint: "Can't find it? Check your spam folder.",
    sendAgain: 'Send again',
    sendAgainCountdown: 'Send again in {seconds}s',
    tryAnotherEmail: 'Try another address',
    rateLimited: 'Too many attempts. Wait a bit and try again.',
    submitError: 'Something went wrong. Try again.',
    noAccount: 'No account?',
    createAccount: 'Create a new account',
  },
  findBookings: {
    eyebrow: 'Find your meetings',
    heading: 'Where do you have meetings?',
    body: 'Enter your email and we will send you an overview of the advisors you have meetings with.',
    emailLabel: 'Email',
    emailPlaceholder: "you{'@'}email.com",
    submit: 'Send me the overview',
    passwordless: 'No password. You get a link by email.',
    sentEyebrow: 'Check your email',
    sentHeading: 'The overview is on its way.',
    sentBody:
      'If you have meetings with an advisor, we have sent an email to {email} with a link to each of them.',
    sentBack: 'Use a different address',
    unavailable: 'This feature is not available yet.',
  },
  setup: {
    back: '← Back',
    continue: 'Continue →',
    profile: {
      heading: 'Tell us who you are.',
      body: 'We only need your name now. You can make the page more personal later.',
      optionalSummary: 'Add more to your profile (optional)',
      uploadPhoto: 'Upload photo',
      nameLabel: 'Name',
      namePlaceholder: 'First and last name',
      titleLabel: 'Title',
      titlePlaceholder: 'e.g. Couples therapist',
      birthdayLabel: 'Birthday',
      phoneLabel: 'Phone',
      phonePlaceholder: '+47 999 99 999',
      bioLabel: 'Short introduction',
      bioPlaceholder: 'What do you do, and for whom? Two to four sentences.',
      bioCount: '{count} characters',
      bioShort: 'Recommended: at least 20 characters',
      cityLabel: 'Location',
      brandLabel: 'Workspace',
      brandHint:
        'Shown next to the logo at the top of your booking page. Leave as-is if it is just you.',
      logoLabel: 'Logo',
      logoHint: 'PNG, JPEG, WebP or SVG. Transparent background recommended. Max 10 MB.',
      logoUpload: 'Upload logo',
      logoChange: 'Replace logo',
      logoRemove: 'Remove',
      logoNone: 'No logo uploaded',
      logoTooBig: 'File too large. 10 MB max.',
      saveError: "Couldn't save the profile. Try again.",
    },
    team: {
      heading: 'Who else is on board?',
      body: 'Add colleagues who also take meetings. This can be done later, you can skip.',
      addPerson: '+ Add person',
      namePlaceholder: 'e.g. Maren Lien',
      rolePlaceholder: 'e.g. Family therapist',
      colorLabel: 'Color',
      you: 'you',
      youUnnamed: 'You',
      titleField: 'Title (optional)',
      titleHint: 'Click Edit to add your title',
      titlePlaceholder: 'e.g. Couples therapist',
      roleField: 'Role',
      roleLockedHint: 'You are administrator in your own org.',
      skip: 'Skip',
      empty: 'No colleagues yet, you are solo.',
      validation: 'Name and role are required.',
    },
    company: {
      heading: 'Who are we invoicing?',
      body: "Enter your organisation number, we'll fetch the name and address from Brønnøysundregisteret. You can edit anything, or skip and fill it in later.",
      orgnrLabel: 'Organisation number',
      orgnrPlaceholder: '987 654 321',
      orgnrHint: '9 digits.',
      lookupBusy: 'fetching from Brønnøysundregisteret ...',
      lookupError: "couldn't find that company, check the number or fill in manually",
      lookupUpstream: 'Brønnøysundregisteret is unavailable right now, you can fill in manually',
      lookupInvalid: 'invalid organisation number',
      foundLabel: 'from Brønnøysundregisteret',
      nameLabel: 'Company name',
      namePlaceholder: 'fetched from Brønnøysundregisteret',
      nameHint: 'From Brønnøysundregisteret, cannot be edited here.',
      addressLabel: 'Business address',
      addressPlaceholder: 'fetched from Brønnøysundregisteret',
      addressHint: 'From Brønnøysundregisteret, change in Settings if needed.',
      industryLabel: 'Industry',
      industryHint: 'NACE code from Brønnøysundregisteret, not shown to customers.',
      inactiveWarning:
        'This entity is not active ({reason}). You can still continue, but double-check the number.',
      inactiveReasonKonkurs: 'bankruptcy',
      inactiveReasonAvvikling: 'winding down',
      inactiveReasonSlettet: 'deleted',
      skip: 'Skip',
    },
    services: {
      heading: 'Add your services.',
      body: "This is what people book. Charging for it? Add a price. If not, leave it blank, we won't show one. You can add more later.",
      defaults: 'We will open Monday–Friday, 09:00–17:00 for you. You can change the hours later.',
      nameLabel: 'Name',
      namePlaceholder: 'e.g. Strategy session',
      durationLabel: 'Duration',
      priceLabel: 'Price (optional)',
      pricePlaceholder: '',
      addService: '+ Add service',
      remove: 'Remove',
    },
    availability: {
      heading: 'When are you available?',
      body: 'Customers only see available times within these windows. You can always block off or open up later.',
      notAvailable: 'not available',
      timezoneHeading: 'Timezone',
      timezoneBody: 'Used to show the correct time to customers in other timezones.',
      days: {
        man: 'Monday',
        tir: 'Tuesday',
        ons: 'Wednesday',
        tor: 'Thursday',
        fre: 'Friday',
        lor: 'Saturday',
        son: 'Sunday',
      },
    },
    calendar: {
      heading: 'Connect your calendar.',
      body: "We'll automatically see when you're busy, and add new meetings to the calendar you already use. You can connect more later.",
      google: 'Google Calendar',
      googleDesc: 'The most common. Works with Gmail and Workspace.',
      apple: 'Apple Calendar',
      appleDesc: 'For those living in the Apple ecosystem.',
      outlook: 'Microsoft 365',
      outlookDesc: 'Outlook, Exchange and Office accounts.',
      connecting: 'connecting ...',
      connect: 'connect →',
      statusConnected: '✓ connected',
      statusFailed: 'failed, try again',
      skip: 'Skip for now →',
      privacyNote: 'We only read busy/free, never the content of meetings or appointments.',
    },
    done: {
      meta: "you're live",
      heading: 'Your page is ',
      headingItalic: 'alive',
      headingEnd: '.',
      body: 'Share the link with your customers, or add it to your email signature. Your first meeting is coming soon.',
      viewSite: 'View your page →',
      goToDashboard: 'Go to dashboard',
      checkingMeta: 'checking agreement',
      checkingHeading: 'We are checking the final details.',
      checkingBody: 'We are confirming that everything is ready before the page can be shared.',
      checkFailedBody: 'We could not confirm the agreement. Try again before sharing the page.',
      retry: 'Try again',
      outstandingMeta: 'before your page can open',
      outstandingHeading: 'Your page is ready, but closed for bookings.',
      outstandingBody:
        'First create a passkey. Then you will find the data processing agreement in the dashboard. Once it is accepted, customers can book.',
      outstandingAction: 'Continue with passkey',
    },
    steps: {
      profile: 'Profile',
      company: 'Company',
      services: 'Services',
      team: 'Team',
      availability: 'Availability',
      calendar: 'Calendar',
      done: 'Done',
    },
    stepOf: 'step {current} of {total}',
    progressLabel: 'Setup progress',
  },
  dashboard: {
    greetingMorning: 'Good morning, {name}.',
    // 10:00–11:59. Norwegian says "God dag" here; English is still morning.
    greetingLateMorning: 'Good morning, {name}.',
    greetingAfternoon: 'Good afternoon, {name}.',
    greetingEvening: 'Good evening, {name}.',
    greetingNight: 'Working late, {name}?',
    todayMeetings: "today's meetings",
    emptyTitle: 'No meetings today, yet.',
    emptyBody: "Your first meeting will show up here. Share your link and it'll come soon.",
    shareLabel: 'share your page',
    shareBody: 'Add the link to your email signature or your social media profiles.',
    sharePreview: 'Preview →',
    progressLabel: 'go further',
    progressTitle: 'Make your page yours.',
    checkResume: 'Finish setup. Next: {step}',
    checkAcceptDpa: 'Accept the data processing agreement and open for bookings',
    checkUploadPhoto: 'Upload a profile photo',
    checkAddServices: 'Add more services ({count} added)',
    checkConnectCalendar: 'Connect calendar',
    checkWriteAbout: 'Write a longer "about me"',
    siteLabel: 'your site',
    nav: {
      overview: 'Overview',
      meetings: 'Meetings',
      notater: 'Notes',
      documents: 'Documents',
      services: 'Services',
      availability: 'Availability',
      calendar: 'Calendar',
      departures: 'Departures',
      customers: 'Customers',
      innsikt: 'Insights',
      rapporter: 'Invoice basis',
      disputes: 'Payment cases',
      account: 'My account',
      settings: 'Settings',
      integrasjoner: 'Integrations',
      help: 'Help',
    },
    meetings: {
      title: 'Your meetings',
      kicker: 'meetings',
      sub: 'Everything booked through your page. Click a meeting for prep, link and notes.',
      newCta: '+ New meeting',
      instantCta: 'Meet now',
      filterUpcoming: 'Upcoming',
      filterPending: 'Unconfirmed',
      filterPendingHint: "The customer hasn't confirmed the time yet.",
      filterUnpaid: 'Unpaid',
      filterPast: 'Past',
      filterCancelled: 'Cancelled',
      filterAll: 'All',
      teamStripAlle: 'all',
      teamStripDeg: 'you',
      teamStripIngenEier: 'unassigned',
      assignOwnerCta: 'assign',
      noCustomerYet: 'No customer yet',
      sortCta: 'Sort',
      searchPlaceholder: 'Search',
      resetFilter: 'Reset filter ↺',
      footerShowing: 'Showing {n} of {total}',
      footerScopeAll: 'the whole company',
      footerScopeMine: 'yours',
      footerScopeNone: 'unassigned',
      headerTime: 'time',
      headerMeeting: 'meeting',
      headerCustomer: 'customer',
      headerOwner: 'assignee',
      headerStatus: 'status',
      statusSoon: 'soon',
      statusUpcoming: 'upcoming',
      statusPrivate: 'private',
      paymentRemainder: 'Partially paid',
      paymentAfter: 'Not paid',
      paymentOutstandingAmount: '{amount} remaining',
      mobileTitleAll: 'The whole company',
      mobileTitleMine: 'Your meetings',
      mobileTitleNone: 'Unassigned',
      mobileTitleMember: "{name}'s meetings",
      mobileSubAll: 'Everything booked through {host}.',
      mobileSubMine: 'Meetings assigned to you.',
      mobileSubNone: 'These are missing a consultant. Assign one before they start.',
      mobileSubMember: 'Meetings assigned to {name}.',
      mobileUnassignedAlertSingular: 'unassigned meeting',
      mobileUnassignedAlertPlural: 'unassigned meetings',
      mobileUnassignedAlertSub: 'Booked when no one was free. Take it, or hand it off.',
      fabNewMeeting: 'New meeting',
      fabInstant: 'Meet now',
      fabSchedule: 'New meeting',
      emptyUpcomingTitle: 'No upcoming meetings.',
      emptyUpcomingBody: 'When customers book or you add meetings manually, they show up here.',
      emptyPendingTitle: 'No unconfirmed meetings.',
      emptyPendingBody: 'Meetings waiting for confirmation appear here.',
      emptyUnpaidTitle: 'No unpaid meetings.',
      emptyUnpaidBody: 'Meetings with an outstanding amount appear here.',
      emptyPastTitle: 'No past meetings.',
      emptyPastBody: 'Completed meetings collect here over time.',
      emptyCancelledTitle: 'No cancelled meetings.',
      emptyCancelledBody: 'Meetings that get cancelled land here.',
      emptyUnassignedTitle: 'Every meeting has an assignee.',
      emptyUnassignedBody: 'No meetings need assigning right now.',
      emptyMineTitle: 'No meetings assigned to you.',
      emptyMineBody:
        "Meetings you're responsible for show up here when you're assigned or book one yourself.",
      emptyAllTitle: 'No meetings yet.',
      emptyAllBody:
        "Once you have meetings, you'll see them here. Tap + to add a meeting manually.",
      groupToday: 'Today',
      groupTomorrow: 'Tomorrow',
      groupWeek: 'Later this week',
      groupLater: 'Later',
      countSingular: '{n} meeting',
      countPlural: '{n} meetings',
      upcoming: 'upcoming',
      past: 'past',
      emptyTitle: 'No upcoming meetings.',
      emptyBody: 'New meetings show up here automatically.',
      emptyGroupTitle: 'No meetings in this period.',
      modeVideo: 'Video',
      modeOffice: 'In person',
      statusConfirmed: 'confirmed',
      statusPending: 'pending',
      statusCancelled: 'cancelled',
      error: "Couldn't load meetings.",
    },
    services: {
      title: 'What you offer',
      kicker: 'services',
      // Failed load, NOT an empty list (#2663).
      loadErrorTitle: "We couldn't load your services.",
      sub: 'Each service becomes its own booking button on your page.',
      newCta: '+ New service',
      label: 'all services',
      sortManually: 'Sort manually ↕',
      statActive: 'active',
      statActiveSub: 'of {total}',
      statBookings: 'meetings this year',
      statBookingsSub: 'across services',
      statAvgDuration: 'avg. duration',
      statAvgDurationSub: 'across all services',
      gratis: 'free',
      priceUnset: '·',
      modeVideo: '◉ video',
      modeOffice: '⌂ in person',
      modeBoth: '◉⌂ video + in person',
      bufferLabel: '+{n} min buffer',
      hidden: 'hidden',
      private: 'private',
      bookingsLabel: 'meetings',
      bookingsCount: '{n} meeting | {n} meetings',
      bookingsThisYear: 'this year',
      durationLabel: 'duration',
      priceLabel: 'price',
      colService: 'service',
      introTip:
        "Tip: leave price blank for meetings you don't charge for. We won't show a price column on your page either.",
      emptyTitle: 'No services added.',
      emptyBody: 'Add services in setup to get started.',
      error: "Couldn't load services.",
      fabNewService: 'New service',
      searchPlaceholder: 'Search services',
      noMatchTitle: 'No matches for "{q}".',
      noMatchBody: 'Try a different word, or clear the search to see all.',
      readinessKicker: 'no visible services',
      readinessTitle: 'All your services are hidden',
      readinessBody:
        'Hidden services do not appear on your booking page and cannot be booked. Make a service visible again to receive meetings. The booking page also requires an accepted data processing agreement.',
      readinessCta: 'Make a service visible',
    },
    availability: {
      title: 'When customers can book you',
      kicker: 'availability',
      sub: "Set weekly hours, and override individual days when life doesn't follow the pattern. Calendar conflicts are hidden automatically.",
      weeklyLabel: 'weekly pattern',
      weeklyHelp:
        'Default for each weekday. Use + block to split a day, e.g. for lunch or an evening session.',
      addBlock: '+ block',
      addBlockTitle: 'Add a new time block (e.g. lunch)',
      removeBlock: 'Remove time block',
      copyPrefix: 'Copy',
      copySuffix: 'to all',
      copySourceLabel: 'Source day',
      copyAction: 'Use selected day as template for all others',
      noBookingsThisDay: 'No meetings this day',
      timeOffLabel: 'periods · vacation and longer breaks',
      addTimeOff: '+ Add period',
      noTimeOff: 'No blockers set.',
      removeTimeOff: 'remove',
      timeOffRemoved: 'The period has been removed.',
      timeOffRemoveError: "Couldn't remove the period. Try again.",
      previewTitle: 'Bookable times',
      previewKicker: 'how customers see it · {range}',
      previewAction: 'Preview',
      previewHint: 'Confirmed meetings and connected-calendar conflicts are also hidden.',
      previewServiceLabel: 'Preview for service',
      previewServiceHint:
        'Pick a service to see buffer time between meetings. Without one, the grid shows raw availability.',
      previewAnyService: 'all services',
      previewClosed: 'off',
      previewLeadToday: 'too late to book today',
      previewFullyBooked: 'fully booked',
      overridesLabel: 'single days · exceptions',
      overrideAdd: '+ Override a day',
      overridesEmpty: 'No single-day exceptions set.',
      overrideKindClosed: 'closed',
      overrideKindReplace: 'custom hours',
      overrideSaved: 'Exception saved',
      overrideSaveError: "Couldn't save exception. Try again.",
      overrideDeleteError: "Couldn't delete exception. Try again.",
      free: 'off',
      hoursSuffix: 'hours',
      hoursShort: 'h',
      dayMon: 'Monday',
      dayTue: 'Tuesday',
      dayWed: 'Wednesday',
      dayThu: 'Thursday',
      dayFri: 'Friday',
      daySat: 'Saturday',
      daySun: 'Sunday',
      saved: 'Availability saved.',
      error: "Couldn't load availability.",
      saveError: "Couldn't save availability.",
      validationOverlap: "Time blocks can't overlap on {day}.",
      validationOrder: 'Start time must come before end time on {day}.',
      fab: 'Add',
      fabOverride: 'Override a day',
      fabTimeOff: 'Block a period',
    },
    calendarManage: {
      kicker: 'calendar · manage',
      title: 'Manage calendar',
      sub: 'See which account is connected, and disconnect if you want to switch users or stop syncing.',
      back: 'Back to calendar',
      loading: 'Fetching status ...',
      loadError: "Couldn't load the calendar status. Try reloading the page.",
      syncHeading: 'Sync meetings to Google',
      syncBody:
        'When this is on, new Stemne meetings show up in your Google calendar automatically.',
      syncVideoLabel: 'Video meeting',
      syncVideoDesc:
        'We set the video link as the location so you can start the meeting straight from your calendar. The service, customer and any message from the customer are in the description.',
      syncOfficeLabel: 'At the office',
      syncOfficeDesc:
        'The location is set to your office address, so Google shows a map link. The rest of the details are in the description.',
      syncFineprint:
        "The customer still gets a confirmation and reminders from Stemne. We don't add the customer as a guest in Google. When you turn this off, meetings already in Google stay put.",
      syncOneWayHeading: 'Sync goes one way',
      syncOneWayBody:
        "Changes you make in your Google calendar aren't sent back to Stemne. If you move or cancel a meeting in Google, Stemne doesn't know about it, and the customer isn't notified. Change meetings from here if the customer should be notified.",
      syncTwoWayHeading: 'Changes in Google are sent back',
      syncTwoWayBody:
        'If you move or delete a Stemne meeting in your Google calendar, the meeting in Stemne updates automatically, and the customer is notified. It can take a few minutes before the change takes effect.',
      syncToggleLabel: 'Sync new meetings',
      syncToggleOn: 'On · new meetings show up in Google',
      syncToggleOff: "Off · new meetings aren't written to Google",
      syncError: "Couldn't save the setting. Try again.",
      accountsHeading: 'Connected accounts',
      connectAnother: 'Connect another account',
      primaryBadge: 'Receives new meetings',
      makePrimary: 'Use for new meetings',
      readOnlyHint: 'Read for busy/free',
      primarySet: 'New meetings now go in this account.',
      primaryError: "Couldn't change which account receives meetings. Try again.",
      disconnectHeading: 'Disconnect Google',
      disconnectBody:
        'We stop reading busy/free from your Google calendar, and new Stemne meetings are no longer written there. Existing calendar entries stay put. You can delete them manually if you want.',
      disconnectCta: 'Disconnect',
      confirmTitle: 'Disconnect Google calendar?',
      confirmBody:
        "You're disconnecting {email}. You can reconnect any time. Your settings won't be remembered.",
      confirmCancel: 'Cancel',
      confirmConfirm: 'Disconnect',
      disconnectError: "Couldn't disconnect. Try again.",
    },
    calendarPicker: {
      heading: 'Choose calendars',
      sub: 'Decide which calendars you want to see in the Stemne calendar, which ones block available time, and where new meetings go. The first two are independent of each other. You can write to only one calendar.',
      loading: 'Getting calendars ...',
      loadError: "Couldn't get your calendars. Try reloading the page.",
      empty:
        "We didn't find any calendars on this account. Check the permissions in Google and try again.",
      primaryBadge: 'primary',
      orphanBadge: 'removed',
      showInCalendar: 'Show in the calendar',
      syncBusy: 'Blocks available time',
      writeTo: 'Write new meetings here',
      colorLabel: 'Colour for {calendar}',
      colorPanelTitle: 'Choose a colour',
      colorOption: 'Colour {hex}',
      colorReset: 'Use the Google colour',
      reset: 'Reset',
      save: 'Save choices',
      saveSuccess: 'Calendar choices saved',
      saveError: "Couldn't save your choices. Try again.",
      errorPickWriteTarget: 'Choose one calendar that new meetings should be written to.',
    },
    calendar: {
      title: 'Calendar',
      kicker: 'calendar',
      sub: 'Stemne meetings alongside your connected calendars. We read busy/free. Content of other calendars stays private.',
      todayCta: 'Today',
      blockCta: '+ Block time',
      manage: 'Manage →',
      settingsCta: 'Calendar settings',
      weekHeading: 'Week {n} · {range}',
      weekPrev: 'Previous week',
      weekNext: 'Next week',
      dayPrev: 'Previous day',
      dayNext: 'Next day',
      monthPrev: 'Previous month',
      monthNext: 'Next month',
      readOnlyHint:
        'Click a block for details. Only stemne meetings can be edited here. External calendars are sync-only.',
      providerGoogle: 'Google',
      providerApple: 'Apple',
      providerOutlook: 'Outlook',
      statusActive: '· connected',
      statusNotConnected: '· not connected',
      statusComingSoon: '· coming soon',
      connectCta: 'Connect',
      lastSyncedPrefix: 'last synced',
      connectError: 'Could not connect {provider}. Try again.',
      connectErrorMissingScope:
        "Google didn't grant Stemne access to your calendar. Try again and make sure the calendar permission is ticked on Google's consent screen.",
      label: 'integrations',
      notConnected: 'Not connected',
      comingSoon: 'Integration with calendar services is coming soon.',
    },
    documents: {
      title: 'Documents',
      kicker: 'shared documents',
      sub: 'A shared space for files between you and the customer. Private, signed in and can be revoked, not like an email attachment.',
      shareCta: 'Share document',
      searchPlaceholder: 'Search file names or customers...',
      tabAll: 'All',
      tabActive: 'Active',
      tabExpired: 'Expired',
      tabRevoked: 'Revoked',
      tabReceived: 'Received from customer',
      colDocument: 'document',
      colCustomer: 'customer',
      colShared: 'shared',
      colExpiry: 'expiry',
      colStatus: 'status',
      statusActive: 'active',
      statusExpired: 'expired',
      statusRevoked: 'revoked',
      statusReceived: 'received',
      policyView: 'view only',
      policyDownload: 'downloadable',
      noExpiry: 'no expiry',
      emptyTitle: 'No shared documents yet',
      emptyBody:
        'When you share a file with a customer, it shows up here with its status and expiry.',
      emptyFilteredTitle: 'Nothing to show',
      emptyFilteredBody: "No documents match what you're looking for.",
      errorTitle: "Couldn't load documents",
      search: {
        sectionTitle: 'Search and category',
        makeSearchable: 'Make searchable',
        makeSearchableDesc:
          'Make this document searchable in document search. Off by default, and only visible to you.',
        category: 'Category',
        noCategory: 'No category',
        newCategory: 'New category',
        newCategoryPlaceholder: 'e.g. Invoice',
        addCategory: 'Add',
        manageCategories: 'Manage categories',
        deleteCategory: 'Delete category',
        categoryDuplicate: 'That category already exists',
        errorGeneric: 'Something went wrong. Try again.',
        toastSearchableOn: 'Document is now searchable',
        toastSearchableOff: 'Document is no longer searchable',
        toastCategorySet: 'Category updated',
        toastCategoryCreated: 'Category added',
        toastCategoryDeleted: 'Category deleted',
        searchableTag: 'searchable',
        searchableTitle: 'This document is searchable in document search',
        modeArchive: 'Archive',
        modeSearch: 'Search content',
        modeSwitchAria: 'Choose view',
        contentPlaceholder: 'Search filenames and content…',
        facetAll: 'All',
        prompt: 'Type to search document content across all customers.',
        searching: 'Searching…',
        noResults: 'No documents match your search.',
        unavailable: 'Search is not available right now.',
        directionOut: 'shared by you',
        directionIn: 'received',
        internal: 'internal',
        openAria: 'Open {name}',
      },
      manage: {
        title: 'Manage sharing',
        kicker: 'sharing',
        sharedAt: 'shared',
        expiresAt: 'expires',
        noExpiry: 'no expiry',
        recipient: 'recipient',
        policy: 'access',
        size: 'size',
        message: 'message',
        actionsHeader: 'Actions',
        activityHeader: 'Events',
        activityLoading: 'Loading events...',
        activityEmpty: 'No events recorded yet.',
        preview: 'Open document',
        previewBusy: 'Getting link...',
        previewError: "Couldn't open the document.",
        previewBlocked: 'Your browser blocked a new tab. Allow pop-ups and try again.',
        revoke: 'Revoke',
        revokeBusy: 'Revoking...',
        revokeConfirmTitle: 'Revoke this share?',
        revokeConfirmBody:
          'The link stops working for the customer immediately. You can share the document again later.',
        revokeConfirmCta: 'Yes, revoke',
        revokeConfirmCancel: 'Cancel',
        extend: 'Extend expiry',
        extend7: '+7 days',
        extend30: '+30 days',
        extend90: '+90 days',
        extendNever: 'No expiry',
        extendBusy: 'Updating...',
        policyView: 'Set to view only',
        policyDownload: 'Allow download',
        policyBusy: 'Updating...',
        toastRevoked: 'Access revoked',
        toastExtended: 'Expiry updated',
        toastPolicy: 'Access updated',
        notManageable:
          'This share can no longer be changed. You can share the document again from the archive.',
        reshare: 'Share again',
        changeRecipient: 'Share with a different customer',
        encryption: 'Encryption',
        encryptedBadge: 'Encrypted',
        encEnable: 'Encrypt file',
        encDisable: 'Turn off encryption',
        encBusy: 'Updating...',
        encDisableTitle: 'Turn off encryption',
        encDisableBody:
          'The file is then stored unencrypted in cloud storage. Its contents are protected only by access control. Do not turn this off for documents with health information.',
        encDisableConfirm: 'Turn off encryption',
        encToastEnabled: 'The file is now encrypted',
        encToastDisabled: 'Encryption turned off',
        encErrIndexable: 'The file is searchable. Turn off content search before encrypting.',
        encErrDpaStale:
          'The data processing agreement must be accepted again before you can encrypt.',
        encErrInProgress: 'Another change is in progress, please try again.',
      },
    },
    customers: {
      title: 'Your customers',
      kicker: 'customers',
      sub: "Everyone who's ever booked a meeting. Only you and the people you've given advisor access see this.",
      newCta: '+ Add manually',
      label: 'all customers',
      searchPlaceholder: 'Search customers...',
      tabAll: 'All',
      tabActive: 'Active',
      tabNew: 'New',
      tabPaused: 'Paused',
      colCustomer: 'customer',
      colMeetings: 'bookings',
      colLast: 'last',
      colNext: 'next',
      colValue: 'Price',
      statTotal: 'total',
      statTotalSub: 'customers',
      statNew: 'new this month',
      statNewSub: 'last 30 days',
      statRevenue: 'revenue so far',
      statRevenueSub: 'excl. tax',
      statMeetingsThisMonth: 'meetings this month',
      statMeetingsThisMonthSub: 'across customers',
      noNext: '·',
      nextRequested: 'requested',
      emptyMatch: 'No customers match "{q}".',
      emptyMatchBody: 'Try a different search term or clear the filter.',
      emptyFilterTitle: 'No customers in this view.',
      emptyFilterBody: 'Switch filter to see other customers, or clear it entirely.',
      emptyTitle: 'No customers yet.',
      emptyBody: 'Customers show up here after their first meeting.',
      tagPar: 'couple',
      tagIndivid: 'solo',
      tagIntro: 'intro',
      tagNy: 'new',
      tagFast: 'regular',
      tagPause: 'paused',
      name: 'Name',
      email: 'Email',
      bookings: 'Bookings',
      lastBooking: 'Last booking',
      error: "Couldn't load customers.",
      fabNewCustomer: 'New customer',
    },
    customerDetail: {
      kicker: 'customer',
      crumb: 'Customers',
      since: 'customer since {when}',
      sinceActive: 'customer · active',
      writeNote: 'Write a note',
      bookMeeting: 'Book a meeting',
      historyKicker: 'history',
      historyTitle: 'Everything about {name}.',
      historyOrder: 'newest first',
      filterAll: 'all',
      filterMeetings: 'meetings',
      filterNotes: 'notes',
      filterComments: 'comments',
      searchPlaceholder: "search {name}'s history ...",
      personalKicker: 'personal notes',
      personalAdd: '+ add',
      personalSub:
        "What you've noticed about {name} across meetings. The customer does not see them in the portal, but can request access.",
      personalAuthorYou: 'noted by you',
      personalAuthorOther: 'noted by {who}',
      personalEdit: 'Edit',
      personalDelete: 'Delete',
      nextKicker: 'next meeting',
      nextJoin: 'Join the meeting',
      nextPrep: 'Open the prep notes',
      contactKicker: 'contact',
      contactEmail: 'email',
      contactPhone: 'phone',
      contactSince: 'since',
      rowMeeting: 'meeting',
      rowNote: 'note',
      rowComment: 'commented on',
      rowAuthorYou: 'written by you',
      rowAuthorOther: 'written by {who}',
      rowOpenMeeting: 'Open meeting',
      rowReplies: '{n} replies',
      emptyTitle: 'No history yet.',
      emptyBody:
        'Meetings, notes and comments about this customer appear here as you work together.',
      loadError: "Couldn't load the history.",
      notFound: "Couldn't find the customer.",
      referenceFallback: 'this customer',
      notFoundBody: 'Check the URL, or head back to the customer list and pick the right one.',
      notFoundBack: 'Back to customers',
      noMatchTitle: 'No matches in this view.',
      noMatchBody: 'Try a different search or switch the filter.',
      resetFilter: 'Reset filter',
    },
    settings: {
      title: 'Settings',
      profileLabel: 'profile',
      profileName: 'Name',
      profileEmail: 'Email',
      profileSaved: 'Profile saved.',
      profileError: 'Could not save profile.',
      accountLabel: 'account',
      currentPassword: 'Current password',
      newPassword: 'New password',
      passwordChanged: 'Password changed.',
      passwordError: 'Could not change password.',
    },
  },
  services: {
    heading: 'Book a time',
    headingNoPrice: 'Schedule a meeting',
    subheading: 'Choose a service and find an available time',
    empty: 'No services are available.',
    loadFailed: "Couldn't load the services. Reload the page.",
    gratis: 'free',
    // Norwegian mva, stated in English for a non-Norwegian reader of the
    // same page. See the nb string for why this is never inlined.
    inklMva: 'incl. VAT',
    privateTitle: 'This page is private.',
    privateBody:
      "Bookings happen by invitation. If you're expecting a link, check your email, or reach out to the operator directly.",
    unavailableTitle: 'Booking is not available.',
    unavailableBody:
      'New meetings cannot be booked here right now. If you already have a meeting, it still stands, and the link in your email works as before.',
  },
  booking: {
    depositNote: 'You pay {dueNow} now · {remaining} remaining',
    afterNote: 'Paid after the session',
    stepTime: 'time',
    stepDetails: 'details',
    stepConfirm: 'confirm',
    stepPayment: 'payment',
    stepperAriaLabel: 'Booking progress',
    pickTime: {
      backToProfile: 'Back to profile',
      heading: 'Choose a time',
      sub: 'Available times are shown in your timezone. The meeting lasts {duration} minutes.',
      sidebarLabel: 'you are booking',
      morning: 'morning',
      afternoon: 'afternoon',
      slotCount: '{count} slot available | {count} slots available',
      slotCountEmpty: 'full',
      lastSeat: 'few spots left',
      emptyDayHeading: 'No available times this day.',
      emptyDayHint: 'pick another date in the calendar',
      nesteLedige: 'next available: {date}',
    },
    bookCalendar: {
      heading: 'Pick a date',
      backToTime: 'Back to times',
      today: 'today',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      gridAria: 'Calendar, {month} {year}',
      cellAria: '{date} {month}, {count} available time | {date} {month}, {count} available times',
      legendAvailable: 'available',
      legendLow: 'few slots',
      legendClosed: 'full or closed',
      blockedPast: 'This day has passed. Pick a day in the future.',
      blockedLoading: 'Fetching available times. Try again in a moment.',
      blockedFull: '{date}. {month} is full or closed. Pick another day.',
      retry: 'Try again',
    },
    details: {
      backToTime: 'Back to time',
      heading: 'About you',
      sub: 'We need your name and email so we can send you the confirmation.',
      nameLabel: 'Name',
      namePlaceholder: 'Type your name',
      emailLabel: 'Email',
      emailPlaceholder: "you{'@'}email.com",
      emailHint: "We'll send the confirmation here. No marketing.",
      phoneLabel: 'Phone (optional)',
      // {role} is the definite role noun for the picked service's kind
      // (useRoleNoun, #2014) — "the trainer" for a PT, not "the advisor".
      phoneHint:
        'Used only for an SMS reminder 24 hours before the meeting, if {role} has enabled it.',
      addressLabel: 'Address (optional)',
      addressPlaceholder: 'Search for an address',
      addressHint: 'Add your address if {role} is coming to you.',
      // The label communicates requiredness without native validation (#3503).
      billingAddressLabel: 'Billing address (required)',
      billingAddressReceiptHint: 'Needed for the receipt.',
      billingAddressHint: 'Used on the invoice after the service has been delivered.',
      billingAddressRequired: 'Enter your billing address.',
      subjectLabel: 'Subject',
      noteLabel: 'What would you like to talk about?',
      noteOptional: '(optional)',
      notePlaceholder: 'Briefly about the context. A sentence or two will do.',
      trainingNoteLabel: 'A bit about you and your goal',
      trainingNotePlaceholder:
        'Goals, experience, injuries or issues the trainer should know about. A sentence or two will do.',
      recurrenceLabel: 'Repeat',
      recurrenceHint:
        'Book a series. Each session lands in your calendar, you can cancel the whole series or one occurrence at a time.',
      recurrenceCountLabel: 'How many',
      recurrenceNone: 'Just this once',
      recurrenceWeekly: 'Weekly',
      recurrenceBiweekly: 'Every other week',
      recurrenceMonthly: 'Monthly',
      recurrenceCountOption: '{n} times',
      submit: 'Go to confirmation',
      reviewPayment: 'Review before payment',
    },
    confirm: {
      backToDetails: 'Back to details',
      heading: 'Review and confirm',
      paymentHeading: 'Review before payment',
      paymentSub:
        'We hold your time while you pay. Your meeting is confirmed once payment succeeds, and you receive confirmation by email.',
      paymentSubmit: 'Go to payment',
      sub: "Everything look right? Press confirm, we'll send the confirmation by email straight away.",
      labelService: 'service',
      labelTime: 'time',
      labelWith: 'with',
      labelYou: 'you',
      labelNote: 'note',
      labelPrice: 'price',
      timePrefix: 'at',
      edit: 'Edit',
      submit: 'Confirm meeting',
      submitting: 'Sending...',
      ackTitle: 'Read before you confirm',
      ackConfirm: 'I have read and understood',
      // Terms and right of withdrawal (#3043). Replaces the old flat
      // disclaimer, which threatened a hardcoded 24-hour charge regardless of
      // what the operator had actually set.
      termsTitle: 'Terms and right of withdrawal',
      termsChangeLead:
        'You can change or cancel the appointment up to {hours} hours before it starts.',
      termsChangeAnytime: 'You can change or cancel the appointment at any time.',
      angrerettSummary:
        'You have 14 days to withdraw from the contract. The right of withdrawal is lost once the service has been performed in full.',
      // See the nb note: /angreskjema holds the form and the § 8 information,
      // not terms. The operator's own terms are the lines above this link.
      angrerettLink: 'Read about the right of withdrawal and get the form',
      sellerDisclosure:
        '{name} is the seller and holds the agreement with you. Stemne provides the booking system and is not a party to the agreement.',
      sellerFallback: 'The provider',
      angrerettConsentTitle: 'Before you confirm',
      angrerettConsent:
        'If the appointment falls before the 14-day withdrawal period has expired, I ask that the service be delivered anyway, and I acknowledge that the right of withdrawal is lost once the service has been performed in full.',
    },

    // Right of withdrawal (#3043). The two long paragraphs are the model
    // text from Directive 2011/83/EU Annex I A and the model withdrawal form
    // in Annex I B, reproduced verbatim rather than written.
    angreskjema: {
      title: 'Model withdrawal form',
      subtitle: 'for goods and services other than financial services',
      print: 'Print',
      formIntro: 'Complete and return this form only if you wish to withdraw from the contract.',
      sendTo: 'Send the completed form to:',
      declaration: 'I/We hereby give notice that I/We withdraw from my/our contract for:',
      optionGoods: 'goods',
      optionServices: 'services (specify on the lines below)',
      fieldSpecify: 'Specification',
      fieldContractDate: 'Contract concluded on (date)',
      fieldConsumerName: 'Name of consumer(s)',
      fieldConsumerAddress: 'Address of consumer(s)',
      fieldDate: 'Date',
      fieldSignature: 'Signature of consumer(s) (only if this form is notified on paper)',
      infoTitle: 'Information on the right of withdrawal',
      rightTitle: 'Right of withdrawal.',
      rightBody:
        'You have the right to withdraw from this contract within 14 days without giving any reason. The withdrawal period will expire after 14 days from the day of the conclusion of the contract. To exercise the right of withdrawal, you must inform us ({contact}) of your decision to withdraw from this contract by an unequivocal statement (for example a letter sent by post or e-mail). You may use the attached model withdrawal form, but it is not obligatory. To meet the withdrawal deadline, it is sufficient for you to send your communication concerning your exercise of the right of withdrawal before the withdrawal period has expired.',
      rightBodyNoContact:
        'You have the right to withdraw from this contract within 14 days without giving any reason. The withdrawal period will expire after 14 days from the day of the conclusion of the contract. To exercise the right of withdrawal, you must inform the trader of your decision to withdraw from this contract by an unequivocal statement (for example a letter sent by post or e-mail). You may use the attached model withdrawal form, but it is not obligatory. To meet the withdrawal deadline, it is sufficient for you to send your communication concerning your exercise of the right of withdrawal before the withdrawal period has expired.',
      effectsTitle: 'Effects of withdrawal.',
      effectsBody:
        'If you withdraw from this contract, we shall reimburse to you all payments received from you without undue delay and in any event not later than 14 days from the day on which we are informed about your decision to withdraw from this contract. We will carry out such reimbursement using the same means of payment as you used for the initial transaction, unless you have expressly agreed otherwise. In any event, you will not incur any fees as a result of such reimbursement. If you requested to begin the performance of services during the withdrawal period, you shall pay us an amount which is in proportion to what has been provided until you have communicated to us your withdrawal from this contract, in comparison with the full coverage of the contract.',
      orgnrLabel: 'Organisation number',
    },

    // A cancelled payment lands on the manage page now
    // (manage.payment.cancelledNotice), which owns the booking and can reopen
    // Checkout for it — so only the could-not-start case remains here.
    payment: {
      startFailed: 'We could not start the payment. Please try again shortly.',
    },
    confirmed: {
      meta: 'meeting confirmed',
      heading: 'See you {day}.',
      sub: 'A confirmation has been sent to {email}. You can manage the meeting from here at any time.',
      meetingPlace: 'Location',
      manage: 'Manage meeting',
      addToCalendar: 'Add to calendar',
      portalHintLede: 'Want to see all your meetings, or download or delete your data?',
      portalHintLink: 'Open the customer portal',
      portalHintTail: 'sign in with your email.',
    },
  },
  portal: {
    entry: {
      headerLink: 'My meetings',
      footerLink: 'my meetings',
    },
    eyebrow: 'customer portal',
    heading: 'View your meetings',
    body: "Enter the email address you used when booking, and we'll send you a link to view your meetings.",
    emailLabel: 'Email',
    submit: 'Send me a link',
    passwordless:
      "We don't use passwords. The link is valid for 15 minutes and can only be used once.",
    sentEyebrow: 'check your inbox',
    sentHeading: 'Check your inbox',
    sentBody: 'We sent a login link to {email}. Click the link in the email to see your meetings.',
    sentTipsTitle: "can't find it?",
    sentTipSpam: 'Check your spam folder.',
    sentTipAddress: 'Maybe you used a different email address when you booked?',
    sentTipResend: 'Send again after 30 seconds.',
    sentResendCountdown: 'send again in {n}s',
    sentResendReady: 'Send the link again',
    sentBack: 'Use a different email',
    authHeading: 'Welcome back, {name}.',
    authSummary:
      'You have {upcoming} upcoming meetings and {past} past ones. Click a meeting to see details, cancel or reschedule.',
    authSummaryNoneUpcoming: 'You have no upcoming meetings. {past} past meetings are here.',
    authSummaryNonePast: 'You have {upcoming} upcoming meetings. No past meetings yet.',
    authBookNew: 'Book a new session',
    upcoming: 'Upcoming meetings',
    past: 'Past meetings',
    upcomingCount: '{n} meeting | {n} meetings',
    pastCount: '{n} meetings · last 3 years',
    pastFootnote: 'meetings older than 3 years show up in the data export · not in this list',
    emptyUpcoming: 'No upcoming meetings.',
    emptyPast: 'No past meetings.',
    noneEyebrow: 'my meetings',
    noneHeading: 'No meetings yet.',
    noneBody:
      "We can't find any meetings linked to {email}. Meetings older than 3 years aren't shown here, but they're in the data export.",
    noneBookCta: 'Book your first meeting',
    noneTryOther: 'Try a different email address',
    expiredEyebrow: 'link has expired',
    expiredTitle: 'This link has been used or has expired.',
    expiredBody: 'You can request a new link below.',
    expiredCta: 'Request new link',
    logout: 'Log out',
    account: {
      eyebrow: 'account & privacy',
      exportTitle: 'Download my data',
      exportBody:
        'A copy of everything we have about you: contact info, all meetings, payment history. Sent as a zip to your email.',
      exportCta: 'Request an export',
      exportSent: 'Link sent. Check your inbox.',
      deleteTitle: 'Delete my account',
      deleteBody:
        "Deletes everything we have about you at {tenant}. Upcoming meetings will be cancelled. Can't be undone.",
      deleteCta: 'Delete account',
      deleteSent: 'Confirmation link sent.',
      fallbackTenantName: 'us',
      controllerNote:
        '{tenant} is the data controller for your data. Questions? Contact the operator directly.',
    },
    deleteModal: {
      eyebrow: 'delete account',
      heading: 'Are you sure?',
      lead: "This deletes everything we hold about you at {tenant}. It can't be undone.",
      listTitle: 'what gets deleted',
      listAccountKey: 'Account',
      listAccountValue: 'email, name, phone',
      listBookingsKey: 'Meetings',
      listBookingsValue: '{upcoming} upcoming cancelled · {past} past removed',
      listNotesKey: 'Notes',
      listNotesValue: 'questionnaire answers and messages to {tenant}',
      listPaymentKey: 'Payment',
      listPaymentValue: 'we keep anonymised receipts for 5 years (accounting requirement)',
      typePrompt: 'type delete to confirm',
      typePlaceholder: 'delete',
      cancel: 'Cancel',
      confirm: 'Delete account permanently',
    },
  },
  privacy: {
    myData: {
      loading: 'Fetching your data',
      eyebrow: 'your data',
      heading: 'Here is everything we have about you',
      lede: "This is what we've stored against {email}. You can download it as a JSON file below.",
      download: 'Download as JSON',
      bookings: 'Meetings ({n})',
      bookingsEmpty: 'No meetings on file.',
      account: 'Account details',
      accountFields: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        address: 'Address',
        postal: 'Postcode and city',
        locale: 'Language',
        tags: 'Labels',
        note: 'Note your adviser added to your card',
        created: 'Registered',
      },
      personalNotes: 'Notes your adviser has written about you',
      personalNotesLede:
        'Notes your adviser has written about you are included here. Notes tied to treatment and health are not part of this export. Ask your adviser for those directly, they are the controller for them.',
      personalNotesEmpty: 'No notes have been written about you.',
      thirdParties: 'Who we share data with',
      thirdPartiesLede:
        'These providers process parts of your data on our behalf, as GDPR article 15 requires us to disclose.',
      thirdPartiesShares: 'Receives',
      status: {
        pending: 'awaiting confirmation',
        confirmed: 'confirmed',
        rescheduled: 'rescheduled',
        cancelled: 'cancelled',
        completed: 'completed',
        no_show: 'no-show',
        expired: 'expired',
      },
      expiredEyebrow: 'link has expired',
      expiredTitle: 'This link has been used or has expired.',
      expiredBody:
        'Privacy email links are valid for 24 hours and may only be used once. Request a new one below.',
      expiredCta: 'Back to customer portal',
      errorEyebrow: 'something went wrong',
      errorTitle: "We couldn't fetch your data.",
      errorBody:
        "Try opening the link again. If the problem persists, contact privacy{'@'}stemne.no.",
    },
    confirmDelete: {
      eyebrow: 'confirm deletion',
      heading: 'Erase all your data?',
      body: "You're one click away from deleting everything. We won't be able to bring it back afterwards.",
      bullet1: 'All meetings, upcoming and past, will be erased',
      bullet2: 'The contact info and notes tied to your email will be gone',
      bullet3: 'You will be signed out of the customer portal',
      cta: 'Erase everything about me',
      cancel: 'Cancel',
      doneEyebrow: 'done',
      doneHeading: 'Your data has been erased',
      doneBody:
        "We deleted {n} record. Thank you for using us. We're sorry to see you go. | We deleted {n} records. Thank you for using us. We're sorry to see you go.",
      doneCta: 'To the home page',
      expiredEyebrow: 'link has expired',
      expiredTitle: 'This link has been used or has expired.',
      expiredBody: 'Request a new deletion link from your customer portal.',
      expiredCta: 'Back to customer portal',
      errorEyebrow: 'something went wrong',
      errorTitle: 'Deletion failed.',
      errorBody:
        "Try opening the link again. If the problem persists, contact privacy{'@'}stemne.no.",
    },
  },
  manage: {
    eyebrow: 'your meeting',
    heading: 'Meeting with {name}',
    upcomingMeeting: 'upcoming meeting',
    joinMeeting: 'Join the meeting',
    opensIn: 'Meeting opens in',
    addToCalendar: 'Add to calendar',
    remindersHeading: 'Reminders',
    remindersBody:
      "We'll send a reminder before the meeting starts. You can choose when, or turn it off if you prefer.",
    getReminder: 'Get a reminder',
    reminder24h: '24 hours before',
    reminder1h: '1 hour before',
    reminder15m: '15 minutes before',
    participantsHeading: 'Participants',
    participantsBody: 'Invite others to join the meeting.',
    inviteColleagues: 'Invite colleagues',
    seatsUsed: '{used}/{capacity} seats used',
    addParticipant: 'Add',
    invite: {
      loading: 'Loading participants',
      empty: 'Nobody else has been invited yet.',
      nameLabel: 'Name',
      emailLabel: 'Email',
      remove: 'Remove',
      statusPending: 'pending',
      statusConfirmed: 'confirmed',
      full: 'The meeting is full. Remove someone to invite more.',
      loadFailed: 'We could not load the participants.',
      addFailed: 'We could not add the participant. Try again.',
      removeFailed: 'We could not remove the participant. Try again.',
    },
    reschedule: 'Reschedule',
    contactToReschedule: 'Contact your advisor to change the time',
    suggestTime: 'Suggest a new time',
    suggestDate: 'Date',
    suggestTimeLabel: 'Time',
    suggestNote: 'Message to the adviser (optional)',
    suggestSubmit: 'Send suggestion',
    suggestTimeSent: 'Your suggestion has been sent. The adviser will reply by email.',
    cancel: 'Cancel',
    // Default late-change warning (#2290). Shown to the customer on any
    // reschedule/cancel inside 24 hours when the service has no operator-
    // authored text. Operator copy wins when present.
    lateChange: {
      defaultWarning:
        'You are changing less than 24 hours before the meeting. You may still be charged for the session.',
    },
    cancelModal: {
      heading: 'Cancel the meeting?',
      body: 'The meeting with {name} ({slot}) will be removed from the calendar. {name} will be notified.',
      bodyUnassigned:
        'Your meeting ({slot}) will be removed from the calendar. {org} will be notified.',
      hint: 'You can book a new time at any time.',
      keep: 'Keep the meeting',
      confirm: 'Cancel meeting',
    },
    meeting: 'Meeting',
    consultant: 'Advisor',
    sharedSummary: 'Summary from your advisor',
    youMeet: "You're meeting",
    alsoWith: 'Also joining',
    participants: 'Participants',
    groupTag: 'Group',
    messageFrom: 'Message from {name}',
    payment: {
      label: 'payment',
      labelPaid: 'paid',
      paidWithAmount: '{amount} has been paid.',
      paid: 'The payment is registered.',
      pending: 'We are confirming your payment now. This usually takes a few seconds.',
      failed: 'The payment did not go through. Get in touch and we will sort it out.',
      refunded: 'The amount has been refunded.',
      refundPending:
        'The refund has been sent to your bank and is being processed. The money is back on your card within 5-10 working days.',
      refundFailed:
        'The bank could not complete the refund. {org} will get in touch to sort it out.',
      refundFailedNoOrg:
        'The bank could not complete the refund. Get in touch with the provider if you do not hear anything.',
      receivedBanner: 'Payment received.',
      receiptLabel: 'Receipt',
      receiptDownload: 'Download receipt',
      creditNoteLabel: 'Credit note',
      creditNoteDownload: 'Download credit note {number}',
      creditNoteReverses: 'Credits receipt {number}',
      // Collection AFTER the meeting (#3063) — the remainder after a deposit,
      // or the whole price on a «betaling etterpå» service.
      outstandingLabel: 'to pay',
      outstandingRemainder:
        'You have {amount} left to pay for this meeting. The payment link is in the email we sent you.',
      outstandingAfter:
        'You have {amount} to pay for this meeting. The payment link is in the email we sent you.',
      outstandingRemainderPayable: 'You have {amount} left to pay for this meeting.',
      outstandingAfterPayable: 'You have {amount} to pay for this meeting.',
      outstandingExpired:
        'The {amount} payment link has expired. Get in touch and we will send you a new one.',
      // Money owed AT BOOKING time — the customer left Stripe without paying.
      // Unlike the post-service ask above, the link exists here (the booking's
      // magic token is in the address), so this one is a button.
      cancelledNotice:
        'The payment was not completed, so the meeting is not confirmed yet. You can pay here.',
      dueLabel: 'to pay',
      dueLede: 'The meeting is confirmed as soon as the payment goes through.',
      payNow: 'Pay now',
      requestLede:
        'You will be sent to a secure payment page. The status updates once payment is confirmed.',
      requestPayNow: 'Pay now',
      requestRetry: 'Try the payment again',
      requestRetryLede: 'If you try again, we close the old payment page first.',
      requestConfirmationPending:
        'The payment is registered. Your confirmation will arrive shortly.',
      requestStartFailed: 'Could not start the payment. Refresh the page and try again.',
      requestReturned:
        'You are back from the payment page. The status updates once payment is confirmed.',
      requestCancelled: 'The payment was not completed. You can try again from the meeting.',
      requestUnavailable:
        'The payment cannot be started right now. Wait for the status to update before trying again.',
      // One per terminal refusal code from the checkout handler. Three of
      // these exist so a customer is never told to wait for a state that
      // cannot change; only refusedChargeInFlight invites a retry.
      outstandingNotCollectable:
        'This payment cannot be settled here. Get in touch with the provider.',
      refusedSettled: 'This payment has already been settled. You do not owe anything more.',
      refusedSetupIncomplete:
        'This provider cannot take payment yet. Get in touch with them to settle up.',
      refusedChargeInFlight:
        'The payment is already under way. Finish it in the window you opened, or wait a few minutes and try again.',
    },
    agendaLabel: "What we'll talk about",
    fromTeam: '{names} from the team',
    groupSeats: '{filled} of {total} seats filled',
    groupCaptionOne: 'You and one other are signed up',
    groupCaptionMany: 'You and {count} others are signed up',
    consultantTBA: 'To be assigned',
    atOffice: 'In person',
    addressTBA: 'Address will be shared in the invitation.',
    seriesLabel: 'the whole series',
    seriesCount: '{n} meeting remaining | {n} meetings remaining',
    seriesItemCurrent: 'this one',
    seriesItemCancelled: 'cancelled',
    seriesCancelAll: 'Cancel the whole series',
    seriesCancelTitle: 'Cancel the whole series?',
    seriesCancelBody:
      'The remaining meeting will be cancelled. You can always book new ones. | All {n} remaining meetings will be cancelled. You can always book new ones.',
    seriesCancelKeep: 'Keep the series',
    seriesCancelConfirm: 'Cancel all',
  },
  invitee: {
    eyebrow: 'your invitation',
    confirm: "Confirm that you're attending",
    decline: 'Decline',
    confirmed: "You're confirmed",
    declined: 'You have declined',
    declinedHint: 'The advisor has been notified. The meeting stands for the others.',
    documents: 'Documents to review',
    portalLink: 'See all your meetings',
    suggestTime: 'Suggest a new time',
    suggestDate: 'Date',
    suggestTimeLabel: 'Time',
    suggestNote: 'Message to the advisor (optional)',
    suggestNoSlots: 'No open times that day. Pick another date.',
    suggestSubmit: 'Send suggestion',
    suggestTimeSent: 'Your suggestion has been sent. The advisor will reply by email.',
  },
  proposal: {
    someone: 'A participant',
    proposed: 'has suggested a new time',
    accept: 'Accept',
    decline: 'Decline',
    accepted: 'The meeting has been moved to the new time.',
    declined: 'The suggestion was declined. The meeting keeps its time.',
  },
  coHostSuggest: {
    cta: 'Suggest a new time',
    title: 'Suggest a new time',
    intro:
      "The host gets the suggestion and chooses to accept or decline. The meeting isn't moved until the host accepts.",
    date: 'Date',
    time: 'Time',
    note: 'Message to the host (optional)',
    noSlots: 'No open times that day. Pick another date.',
    submit: 'Send suggestion',
    sent: 'The suggestion was sent to the host.',
    error: "Couldn't send the suggestion. Try again.",
    exists: "There's already an open suggestion for this meeting.",
    notNow: "The meeting can't be changed right now.",
  },
  reschedule: {
    backToBooking: 'Back to the meeting',
    backToReschedule: 'Back to times',
    heading: 'Reschedule',
    currentTime: 'Current time:',
    pickNew: 'Choose a new time below.',
    autoNotify: '{name} will be notified automatically.',
    seriesNoticeLabel: 'part of a series',
    seriesNoticeBody:
      'This only changes this one occurrence. The rest of the series stays as is. If you want to move all of them, contact us or cancel the series and book again.',
    confirmTitle: 'Switch to {time}?',
    confirmBody: 'You will get an updated confirmation by email, and {name} will be notified.',
    confirmKeep: 'Keep the time',
    confirmSwitch: 'Switch time',
  },
  cancelled: {
    meta: 'meeting cancelled',
    heading: 'The cancellation has been registered.',
    body: '{name} has been notified. You can book a new time whenever you like.',
    bookNew: 'Book a new meeting',
  },
  rescheduled: {
    meta: 'the time has changed',
    heading: 'You have a new time.',
    body: "{name} has been notified. We've sent you an updated confirmation by email.",
    newTimeLabel: 'new time:',
    addToCalendar: 'Add to calendar',
    portal: 'Go to the customer portal',
    home: 'Back to the home page',
    manageHint:
      'A new manage link is in the email. You can also find the meeting under "My meetings" in the customer portal.',
  },
  meeting: {
    lobby: {
      eyebrow: 'meeting starts soon',
      heading: 'Ready to meet {name}?',
      cameraOnLabel: 'camera on · preview',
      cameraOff: 'camera off',
      micOn: 'microphone on',
      micOff: 'microphone off',
      deviceCheck: 'check equipment',
      camera: 'Camera',
      microphone: 'Microphone',
      deviceNote: 'you can toggle during the meeting.',
      join: 'Join now',
      waiting: '{name} is already waiting.',
    },
    inMeeting: {
      you: 'You',
      youLabel: 'you',
      speaker: 'speaker',
      meetingLabel: 'meeting',
      live: 'live',
      participants: '{count} participants',
      micOn: 'mic on',
      micOff: 'mic off',
      cameraOn: 'camera on',
      cameraOff: 'camera off',
      end: 'End',
    },
    after: {
      meta: 'meeting is over',
      heading: 'Thanks for the conversation.',
      body: 'We hope you got the answers you were looking for. If you have questions afterwards, send an email.',
      followupEyebrow: 'want to meet again?',
      followupBody: "Book a follow-up with one tap. We remember that we've talked.",
      bookFollowup: 'Book a follow-up',
    },
  },
  chat: {
    title: 'Chat',
    titleGroup: 'Chat with participants',
    upload: {
      unsupportedType:
        'That file type is not supported. Choose a PDF, Word, spreadsheet, presentation or image.',
      empty: 'The file is empty or corrupt.',
      tooLarge: 'The file is too large. Max 25 MB.',
      expired: 'The link has expired.',
      notFound: "We couldn't find the meeting.",
      unavailable: 'Uploads are temporarily unavailable. Please try again.',
      generic: 'Something went wrong with the upload.',
    },
  },
  edges: {
    expired: {
      eyebrow: 'link has expired',
      heading: "We can't find this meeting.",
      body: 'The link no longer works. The meeting may have passed or been cancelled, or the confirmation email was resent. Check your inbox, or book a new time.',
      bookNew: 'Book a new meeting',
      contact: 'Contact us',
      contactHelperBefore: 'Or write directly to ',
      contactHelperAfter: " and we'll help you further.",
      contactCopied: 'The email address {email} was copied.',
    },
    offByConsultant: {
      eyebrow: 'meeting cancelled by host',
      heading: '{name} unfortunately had to cancel.',
      body: 'The meeting is no longer available. You can find a new time in the calendar.',
      findNew: 'Find a new time',
      backToProfile: 'Back to profile',
    },
    fullyBooked: {
      eyebrow: 'no available times',
      heading: 'Fully booked this day.',
      body: '{name} has no available times this day. Try another day.',
      pickAnother: 'Choose another day',
      notify: 'Notify me',
    },
    errorPages: {
      topbarLabel: 'support',
      footerUrlLabel: 'requested',
      footerTimeLabel: 'time',
      footerSporIdLabel: 'trace id',
      notFound: {
        eyebrowCode: 'error 404',
        eyebrowText: 'page not found',
        documentTitle: 'Page not found, stemne',
        heading: "We can't find the page you're looking for.",
        body: 'The page may have moved, or the address may be misspelled. Check that the address is correct, or go back to the start page.',
        primary: 'Back to start',
        secondary: 'Write to support',
        helperBefore: 'Still stuck? Send us a line at ',
        helperAfter: '. We usually reply within an hour on weekdays.',
      },
      forbidden: {
        eyebrowCode: 'error 403',
        eyebrowText: 'no access',
        documentTitle: 'No access, stemne',
        heading: 'This link is not for you.',
        body: 'The link may have expired, or it belongs to a different meeting. Magic links are tied to the email address used at booking. Request a new one to that same address.',
        primary: 'Request new link',
        secondary: 'Back to start',
        helperBefore: 'Using the right email but still seeing this page? Write to ',
        helperAfter: ' with the link from your confirmation and we will help.',
      },
      gone: {
        eyebrowCode: 'error 410',
        eyebrowText: 'meeting is over',
        documentTitle: 'Meeting is over, stemne',
        heading: 'This meeting is already over.',
        body: 'The link is no longer active, but you can book a new meeting with the same advisor, or view the receipt and notes from the previous one.',
        primary: 'Book a new meeting',
        secondary: 'View receipt',
        helperPlain:
          'Planning a follow-up? Your advisor gets a heads-up if you book within seven days and will not have to ask for context again.',
      },
      serverError: {
        eyebrowCode: 'error 500',
        eyebrowText: 'something went wrong on our side',
        documentTitle: 'Something went wrong, stemne',
        heading: 'Something went wrong on our side.',
        body: 'We have been notified and are working on it. Your meeting is safe. The meeting time and participants are not affected by this page failing to load. Try again in a minute.',
        primary: 'Try again',
        secondary: 'Check status',
        helperPlain:
          'On your way to a meeting right now? The join link is in your confirmation email. It works independently of this page.',
      },
    },
  },
  bookingCard: {
    confirmed: 'confirmed',
    pending: 'pending',
    rescheduled: 'rescheduled',
    cancelled: 'cancelled',
    expired: 'expired',
    completed: 'completed',
    noShow: 'no-show',
    joinMeeting: 'Join meeting',
    viewDetails: 'View details',
    reschedule: 'Change time',
    cancel: 'Cancel',
    sharedSummary: 'Summary from your advisor',
    meetingPlace: 'Location',
  },
  footer: {
    siteName: 'stemne.no',
    helpPrompt: 'Need help?',
    helpEmail: "support{'@'}stemne.no",
    copyright: '© 2026 Stemne AS. All rights reserved.',
    privacy: 'privacy',
    terms: 'terms',
  },
  spinner: {
    loading: 'Loading...',
  },
  linkRow: {
    copy: 'Copy',
    copied: 'Copied',
  },
  adminConfirmEmailView: {
    invalidLink: 'The confirmation link is invalid.',
    expiredLink: 'The link is invalid or expired. Request a new one from your account settings.',
    genericError: 'Something went wrong. Try again later.',
    confirming: 'Confirming...',
    errorKicker: 'the link is invalid',
    errorTitle: 'We could not confirm the email',
    backToLogin: 'Back to sign in',
    successKicker: 'confirmed',
    successTitle: 'The email is updated',
    successBody: 'Use the new address the next time you sign in. The old one no longer works.',
    toAccountSettings: 'To account settings',
  },
  ownershipDisclosure: {
    title: 'What happens when you take over',
    clearsTerms: 'The operator agreement entered into during setup ends',
    clearsTermsBy: 'The operator agreement {who} entered into ends',
    clearsDpa: 'The data processing agreement entered into during setup ends',
    clearsDpaBy: 'The data processing agreement {who} entered into ends',
    clearsSubscription: 'The subscription is cancelled',
    clearsCard: 'The payment card is removed: {card}',
    removesOwner: '{who} loses access to the organisation',
    newTrial:
      'The organisation is placed on a new {days}-day trial, and you must choose a plan and add payment before it runs out',
    agreements:
      'You enter into the operator agreement (version {terms}) and the data processing agreement (version {dpa}) in your own name when you complete the handover.',
    consentLabel: 'I have read what the handover involves, and accept the agreements above',
  },
  ownershipTransfer: {
    title: 'Transfer ownership',
    body: 'Give the organisation to someone else. You lose access once they accept.',
    pending: 'A handover has been sent to {email}. It is awaiting a reply.',
    cta: 'Transfer ownership',
    modalTitle: 'Transfer ownership',
    modalLede:
      'We send a link to the person taking over. Nothing happens until they have read it and accepted.',
    consequenceAgreements:
      'The agreements you entered into end. The new owner enters into them again.',
    consequencePayment:
      'Subscription and payment details are removed, and the organisation gets a new trial.',
    consequenceAccess: 'You lose access to the organisation once the handover completes.',
    emailLabel: 'Email of the new owner',
    emailPlaceholder: "name{'@'}company.com",
    reasonLabel: 'Reason',
    reasonPlaceholder: 'Why is ownership being transferred?',
    cancel: 'Cancel',
    confirm: 'Send handover',
    errEmailRequired: 'Email is required',
    errReasonRequired: 'A reason is required',
    errGeneric: 'Could not send the handover. Try again.',
  },
  adminInviteAcceptView: {
    handoverLandingTitle: 'You can take over {org}',
    handoverLandingBodyPre: 'You have been invited to take over',
    handoverLandingBodyOn: 'at',
    handoverLandingBodyPost: 'Read what the handover involves before you continue.',
    handoverFinalTitle: 'Complete the handover',
    handoverFinalBody:
      'Enter your name and confirm. You then become the owner of the organisation.',
    errDisclosureRequired: 'You must confirm what the handover involves',
    roleAdmin: 'administrator',
    roleAdvisor: 'advisor',
    errInvalidLink: 'Invalid invitation link.',
    errInviteInvalidOrExpired: 'The invitation is invalid or expired. Ask for a new one.',
    errLoadInvite: 'Could not load the invitation. Try again.',
    errTooManyRequests: 'Too many requests. Wait a while and try again.',
    errInviteInvalidOrExpiredShort: 'The invitation is invalid or expired.',
    errSendCode: 'Could not send the code. Try again.',
    errOtpFormat: 'Enter the six digits from the email.',
    errOtpWrong: 'Wrong code. Check the email and try again.',
    errOtpTooManyAttempts: 'Too many attempts. Ask for a new code.',
    errOtpExpired: 'The code has expired. Ask for a new code.',
    errOtpVerifyFailed: 'Could not verify the code.',
    errGeneric: 'Something went wrong. Try again.',
    errNameRequired: 'Enter your name.',
    errAcceptFailed: 'Could not accept the invitation.',
    loadingLabel: 'Fetching invitation...',
    failedTitle: 'The invitation is no longer valid',
    backToLogin: 'Back to login',
    landingKicker: 'welcome',
    landingTitle: 'Hi. {inviter} has invited you to {org}.',
    someone: 'Someone',
    landingBodyPre: "You'll be added as",
    landingBodyAt: 'at',
    landingBodyOn: 'on',
    landingBodyPost: "To confirm the email is yours, we'll send a code to this address.",
    sendCodeCta: 'Send code →',
    otpKicker: 'confirm email',
    otpTitle: 'Enter the code we sent you.',
    otpBodyPre: 'We sent a 6-digit code to',
    otpBodyPost: 'The code is valid for 10 minutes.',
    otpInputLabel: '6-digit code',
    verifyCodeCta: 'Confirm code →',
    finalKicker: 'last step',
    finalTitle: "What's your name?",
    finalBody:
      "We use your name on your profile card. When you're done, we'll create a passkey for you.",
    nameInputLabel: 'Your name',
    namePlaceholder: 'First name Last name',
    finishCta: 'Finish and create passkey →',
  },
  authWrongWorkspaceView: {
    heading: 'You are signed in to a different workspace',
    loadingBody: 'One moment, checking your session.',
    body: 'Your session belongs to {sessionHost}, but the page you tried to open is on {currentHost}.',
    hint: 'You can continue where you are signed in, or sign out and sign in here.',
    continueCta: 'Continue to {sessionHost}',
    logoutCta: 'Sign out and sign in here',
  },
  authAwaitingReviewView: {
    headingRejected: 'Your application was declined',
    headingAwaiting: 'Your application is being reviewed',
    loadingBody: "One moment, we're checking your status.",
    rejectedBody: "After a review, we've decided that we can't offer the subdomain you chose.",
    reasonLabel: 'Reason:',
    contactBefore: 'If you think this is a mistake, get in touch at',
    awaitingBody1:
      "We've received your application. The subdomain you chose resembles a well-known brand name, so one of us will review the registration before we open the account.",
    awaitingBody2:
      "You'll get an email as soon as we have an answer. It usually takes less than one business day.",
    logout: 'Log out',
    refreshStatus: 'Refresh status',
  },
  subscriptionRequiredView: {
    loadingBody: 'One moment, we are checking your subscription.',
    headingTrialExpired: 'Your trial has ended',
    headingPastDue: 'Your payment did not go through',
    headingCanceled: 'Your subscription has ended',
    headingPaused: 'Your subscription is paused',
    headingDefault: 'Your subscription is not active',
    bodyTrialExpired: 'Your free trial has ended. Choose a plan to start using the account again.',
    bodyPastDue:
      'We could not charge your payment method. Update your payment to unlock the account again.',
    bodyCanceled: 'Your subscription has ended. Start a new subscription to use the account again.',
    bodyPaused: 'Your subscription is paused. Resume it to use the account again.',
    bodyDefault: 'Choose or renew a plan to start using the account again.',
    nonOwnerBody:
      'Contact the account owner to sort out the subscription. You will get access as soon as it is in place.',
    goToBilling: 'Go to subscription',
    logout: 'Log out',
  },
  dataProcessingAgreementView: {
    // Shown on non-Norwegian locales only. The agreement exists in Norwegian
    // alone, and that version is the binding one — a DPA is a contract, so an
    // unreviewed translation would be worse than none.
    authoritativeLanguageNote:
      'The data processing agreement is available in Norwegian only. The Norwegian text is the binding version.',
    // Document header. The values are pulled out of the agreement text, so
    // these labels are the only translated part.
    versionLabel: 'Version {version}',
    effectiveLabel: 'Effective from {date}',
    backToHome: 'Back to home',
  },
  agreementsAcceptView: {
    heading: 'Get your page ready for bookings',
    headingTermsOnly: 'Operator agreement',
    bothLede:
      'Before you publish your page, we need two short agreements. You can read each one in full before accepting it.',
    partyDisclosure: 'Show who the agreements apply to',
    termsTitle: 'Operator agreement',
    termsBody:
      'The rules for using Stemne, payments, and your responsibility for your own prices, VAT, and bookkeeping.',
    termsReadCta: 'Read the full operator agreement',
    termsConsentLabel: 'I accept the operator agreement.',
    ownerOnly:
      'Only the owner of the business can accept agreements on its behalf. Ask the owner to sign in and accept.',
  },
  operatorAgreementView: {
    authoritativeLanguageNote:
      'The operator agreement exists only in Norwegian. The Norwegian text is the binding version.',
    versionLabel: 'Version {version}',
    backToHome: 'Back to home',
  },
  dpaAcceptView: {
    heading: 'Data processing agreement',
    loadingBody: 'Fetching the agreement.',
    body: 'How Stemne processes and protects customer data on your behalf.',
    unlockBody:
      'Your booking page stays closed until this is in place. Once the agreement is accepted, customers can see your services, find available times and book.',
    readCta: 'Read the full agreement',
    consentLabel: 'I accept the data processing agreement.',
    acceptCta: 'Accept and continue',
    partyController: 'Data controller',
    partyProcessor: 'Data processor',
    partyProcessorValue: 'Stemne AS, company no. 937 952 732',
    partyNameOrgnr: '{name}, company no. {orgnr}',
    partyContact: 'Contact',
    ownerOnly:
      'Only the owner of the business can accept the data processing agreement. Ask the owner to sign in and accept.',
  },
  // Confirmation in front of the agreement-gate bounce (#3287). The button
  // takes you TO the agreement, it does not accept it.
  agreementGateModal: {
    dpaTitle: 'You need to accept the data processing agreement first',
    dpaBody:
      'This action requires your business to have accepted Stemne’s data processing agreement. You can read it and accept it now, and you’ll come back here afterwards.',
    termsTitle: 'You need to accept the operator agreement first',
    termsBody:
      'Before you can activate payments, your business needs to accept Stemne’s operator agreement. You can read it and accept it now.',
    confirm: 'Read the agreement and continue',
  },
  dpaRequiredView: {
    kicker: 'one last step',
    title: 'Before you can offer services',
    lede: 'You can explore the dashboard and prepare your page. Before a service becomes visible to customers, we need you to accept Stemne’s data processing agreement.',
    noteTitle: 'What does that mean?',
    noteBody:
      'The agreement explains how Stemne processes and protects the information customers share when they book with you.',
    reassurance:
      'You choose when to make the service visible. Nothing changes to your service yet.',
    cta: 'Read the agreement and continue',
    back: 'Back to the dashboard',
  },
  dashboardBookabilityBanner: {
    kicker: 'your page is not bookable yet',
    title: 'You have no active services right now',
    body: 'You can still explore and prepare your page. When you are ready, we will guide you through the final step before customers can book.',
    cta: 'See your services',
    dpaKicker: 'the booking page is closed',
    dpaTitle: 'The data processing agreement must be accepted',
    dpaBody:
      'Customers cannot view services or available times, or make a booking, until your business has accepted the agreement.',
    dpaCta: 'View the agreement',
    dpaAcceptCta: 'Accept the agreement and open the page',
  },
  authLoginView: {
    checkingSession: 'Checking your session',
    sentTitle: 'Check your email',
    sentBody: 'If the account exists, we sent a login link to {email}. Click the link to log in.',
    sentHint: "Can't find it? Check your spam folder.",
    resend: 'Send again',
    resendCountdown: 'Send again in {seconds}s',
    tryAnotherAddress: 'Try a different address',
    welcomeBack: 'Welcome back.',
    welcomeSubtitle: "Enter your email. We'll ask your device for your passkey.",
    emailLabel: 'Email',
    emailPlaceholder: "you{'@'}company.no",
    emailRequired: 'Enter your email first.',
    loginWithPasskey: 'Log in with passkey',
    sendMagicLink: 'Send me a login link',
    tryPasskeyAgain: 'Try passkey again',
    fallbackPrompt: 'No passkey on this device? {link}.',
    fallbackLink: 'Send me a login link',
    securitySummary: 'Sign in securely with Face ID, Touch ID, Windows Hello or a security key.',
    originFallbackHint:
      "Log in from your own stemne address ({'{'}your-name{'}'}.stemne.no). We can send you a login link instead.",
    transferFailedWithReason: "Couldn't forward: {reason}",
    transferFailed: "Couldn't forward.",
    rateLimited: 'Too many attempts. Wait a moment and try again.',
    magicLinkError: "We can't reach login right now. Try again in a moment.",
    recoveryLink: 'Log in with a recovery code',
    recoveryBack: 'Back to passkey',
    recoveryCodeLabel: 'Recovery code',
    recoverySubmit: 'Log in with code',
    recoveryInvalid: 'That code is invalid or has already been used.',
    recoveryError: "We can't reach login right now.",
  },
  authPasskeyEnrollView: {
    recoveryTitle: 'Your passkey is ready.',
    recoveryIntro:
      'Store these recovery codes somewhere safe. Each code can only be used once, and they will not be shown again.',
    recoveryCopy: 'Copy codes',
    recoveryDownload: 'Download as .txt',
    recoveryOptional:
      'Your passkey is synced. You can save the codes now, or continue without saving them.',
    recoverySaved: 'I have saved the codes',
    recoveryContinue: 'Continue',
    recoveryContinueWithoutSaving: 'Continue without saving',
    headline: 'Create a passkey with {verb}.',
    ctaLabel: 'Create with {verb}',
    verb: {
      faceId: 'Face ID',
      touchId: 'Touch ID',
      windowsHello: 'Windows Hello',
      fingerprint: 'your fingerprint',
      yourDevice: 'your device',
    },
    deviceAppleMobile: 'iPhone or iPad (Face ID)',
    deviceAppleDesktop: 'Mac (Touch ID)',
    deviceWindows: 'Windows Hello',
    deviceAndroid: 'Android (fingerprint)',
    deviceDefault: 'This device',
    errorNotAllowed:
      'We could not register the passkey. Complete the confirmation in the prompt. If you already have a passkey for stemne saved in iCloud Keychain or 1Password, delete it first and try again.',
    errorUnauthorized: 'You must be logged in to create a passkey. Log in and try again.',
    errorRegistrationFailed: 'Registration failed.',
    errorGeneric: 'Something went wrong. Try again.',
    errorForwardWithMessage: 'Could not forward: {message}',
    errorForward: 'Could not forward.',
    stepLabel: 'step 2 of 2 · security',
    wrongOriginTitle: 'Open this page on your own address.',
    wrongOriginLead:
      'For the passkey to be bound correctly to your account, you have to create it from',
    wrongOriginOwnAddress: "your own stemne address ({'{your-name}'}.stemne.no)",
    wrongOriginTrailer: '. Change the address in the browser and come back here, then we continue.',
    intro:
      'Forget passwords. You log in within seconds, and there is nothing to forget or lose. No one can trick you into handing it over.',
    deviceCardMeta: 'passkey for your account',
    deviceConfirmHint: 'We will ask for confirmation from this device.',
    crossDeviceCta: 'Use a passkey from another device',
    helpLink: 'What is a passkey?',
    bluetoothNote: 'Using a passkey from a phone (QR code) requires Bluetooth to be on.',
    deviceConstrainedTitle: 'This device cannot create a passkey here.',
    deviceConstrainedBody:
      'It has neither biometric login (Touch ID, Face ID, Windows Hello, fingerprint) nor Bluetooth, so we cannot start a passkey ceremony on this page.',
    deviceConstrainedItemEmail:
      'Open {email} on another device, a phone, tablet or a PC with fingerprint or Bluetooth. The link is valid for 24 hours.',
    deviceConstrainedItemEmailEmphasis: 'the login link from the email',
    deviceConstrainedItemUsb:
      'Or connect a {usb} (for example a Yubikey) to this PC and click "{cta}" below. Windows then starts the ceremony with the security key instead of an on-device passkey.',
    deviceConstrainedItemUsbEmphasis: 'USB security key',
    deviceConstrainedItemHelp: 'Need help? Send us an email at {email}.',
    helpPanelP1:
      'A passkey is a cryptographic key that lives on your device. It can be stored in iCloud Keychain, Google Password Manager, 1Password or a physical security key. It replaces the password. When you log in, you confirm with Face ID, Touch ID or a PIN, and the device proves to stemne that it is you, without sending anything that can be leaked or phished.',
    helpPanelP2:
      'The passkey is bound to {host}. A fake website that looks similar cannot ask for it, because the browser will not hand it out to the wrong domain. You do not need to remember anything, and there is nothing to type wrong.',
    loginFallbackLink: 'Log in with a passkey',
    bulletNoPasswordTitle: 'No password.',
    bulletNoPasswordBody: 'There is no password to leak or remember.',
    bulletBoundTitle: 'Bound to {host}.',
    bulletBoundBody: 'The passkey works only on our domain. Phishing sites cannot use it.',
    bulletSyncedTitle: 'Synced safely.',
    bulletSyncedBody: 'iCloud, Google and 1Password can sync passkeys between your devices.',
  },
  authVerifyTokenView: {
    missingToken: 'Invalid link, the token is missing.',
    invalidOrExpired: 'The link is invalid or expired. Ask for a new one.',
    somethingWentWrong: 'Something went wrong. Try again.',
    loggingIn: 'Logging in...',
    title: 'Could not log in',
    backToLogin: 'Back to login',
  },
  bookingPaymentRequest: {
    heading: 'Payment',
    deposit: 'Deposit',
    remainder: 'Remainder',
    full: 'Full amount',
    planned: 'Sent after the meeting',
    notSent: 'Not sent',
    paidOn: 'Paid {date}',
    chargeStatus: {
      succeeded: 'Paid',
      pending: 'Awaiting payment',
      failed: 'Payment failed',
      expired: 'Expired',
      refunded: 'Refund recorded',
      refundPending: 'Refund in progress',
      refundFailed: 'Refund was not completed',
      unpaid: 'Not paid',
    },
    headingRemainder: 'Remaining amount',
    headingAfter: 'To pay after the meeting',
    status: {
      open: 'Not paid',
      paid: 'Paid',
      cancelled: 'Cancelled',
      expired: 'Expired',
    },
    sentOn: 'Sent {date}',
    limits:
      'The invoice is due 14 days after the meeting. The customer gets one reminder after 7 days and one on the due date. Stemne does not add fees or interest and does not start debt collection automatically. The payment link works for 90 days.',
    collectionPaused: 'Automatic reminders are paused. The invoice and payment link remain valid.',
    collectionDisputed:
      'The claim is marked as disputed. Stemne sends no reminders until you resume collection.',
    resend: 'Send the link again',
    pause: 'Pause reminders',
    resume: 'Resume collection',
    dispute: 'Mark as disputed',
    cancel: 'Stop collection',
    cancelConfirmTitle: 'Stop collection?',
    cancelConfirmBody:
      'The customer stops getting reminders about {amount}, and their payment links stop working. The invoice is not marked paid or credited. Record payment or a credit note in the bookkeeping system. This cannot be undone in Stemne.',
    cancelConfirmCta: 'Stop collection',
  },

  bookingChoosePersonView: {
    kicker: 'who would you like to talk to?',
    titleFallback: 'Choose a person',
    subtitle: 'Choose a person, or let us set you up with whoever is available first.',
    anyoneName: "It doesn't matter",
    anyoneRole: "We'll set you up with whoever is available first.",
  },
  bookingConfirmView: {
    onlineOrLocation: 'online or {location}',
  },
  bookingConfirmedView: {
    portalHintConjunction: 'and',
  },
  bookingServiceLandingView: {
    pickSubtitle: 'Choose who you want to talk to.',
    pooledTeamSubtitle:
      "We'll set you up with one of us. You'll find out who you're meeting as soon as the meeting is confirmed.",
    pickKicker: 'choose a person',
    personCta: 'choose →',
    escapeQuestion: "Doesn't matter who?",
    escapeLink: 'Show all available times across everyone',
    escapeNote: "We'll set you up with whoever is free first.",
  },
  customerEmailConfirmView: {
    loadingSub: 'Confirming your new email address ...',
    successTitle: 'Your email is confirmed.',
    successSubPrefix: 'Reminders and confirmations now go to',
    successSubSuffix: '. You can close this window.',
    expiredTitle: 'The link is invalid or expired.',
    expiredSub:
      'Ask the operator to send you a new link. Links are valid for seven days from when they are sent.',
    errorTitle: 'Something went wrong.',
    genericError: 'Something went wrong. Try again shortly.',
  },
  // /betal/{token} — the pay page the customer is sent after the meeting
  // (#3063). Every refusal comes verbatim from the server, which already
  // worded it for this customer, so there are only four strings here.
  pay: {
    redirectingTitle: 'Taking you to payment.',
    redirectingSub: 'We are opening the payment page. It only takes a moment.',
    paidTitle: 'We have received your payment.',
    paidSub: 'Thank you. You can close this window.',
    failedTitle: 'We could not open the payment.',
    failedBody: 'It might be the network. Try again shortly.',
  },
  marketingFaqView: {
    categoryPersonvern: 'Privacy and data',
    categoryPris: 'Price and commitment',
    categoryBooking: 'Booking and customers',
    categoryKalender: 'Calendar and video meetings',
    categoryTeam: 'Team and setup',
    categoryAnnet: 'Other',
    navForsiden: 'Home',
    navKomIGang: 'Get started',
    kicker: 'Help',
    title: 'Questions and answers',
    leadIntro:
      "Here are the answers to what people most often wonder about before they start using Stemne. Can't find what you're looking for? Write to",
    leadOutro: '. We reply within one business day.',
    tocAriaLabel: 'Table of contents',
    ctaKomIGangGratis: 'Get started for free',
    footerHome: 'Stemne',
    footerFaq: 'Questions',
    footerPrivacy: 'Privacy',
    footerTerms: 'Terms',
  },
  marketingLandingView: {
    periodYearly: 'per year',
    periodMonthly: 'per month',
    yearlyHint: 'save up to {pct}%',
    pricingUnavailableTitle: 'Prices are temporarily unavailable.',
    pricingUnavailableBody:
      'We are connecting to our payment system now. Reload the page in a moment, or contact us at {email}.',
    footerDev: 'built by kodelauget.no',
  },
  // This paid-search destination intentionally stays in Bokmål for both
  // locale branches so browser language cannot change the ad message.
  marketingPractitionersView: {
    homeAriaLabel: 'Stemne, forsiden',
    navAriaLabel: 'Hovedmeny',
    pricing: 'Priser',
    login: 'Logg inn',
    heroEyebrow: 'Mer tid til kunden',
    heroTitle: 'Bookingsystem for massører og behandlere',
    heroLead:
      'La kundene bestille selv og få påminnelser før timen. Samle avtalene på ett sted, med mindre administrasjon og færre dobbeltbookinger.',
    trial: 'Gratis i 3 måneder. Ingen bindingstid.',
    priceLoading: 'Henter månedspris …',
    priceError: 'Kunne ikke hente prisen akkurat nå.',
    price: 'Deretter {price} kr per måned for Solo, uten mva.',
    priceUnavailable: 'Månedsprisen er ikke tilgjengelig akkurat nå.',
    cta: 'Prøv gratis i 3 måneder',
    dashboardAlt: 'Stemne viser kommende avtaler og kunder i oversikten',
    dashboardCaption: 'Oversikten i Stemne, vist med demodata.',
    photoAlt: 'En massør masserer skuldrene til en kunde på en behandlingsbenk i et lyst rom.',
    benefitsAriaLabel: 'En enklere timehverdag',
    benefitBookingTitle: 'Kundene finner en tid selv',
    benefitBookingBody:
      'Del bookinglenken din. Kunden velger behandling og en ledig tid, uten at du må svare mellom timene.',
    benefitCalendarTitle: 'Avtalene henger sammen',
    benefitCalendarBody:
      'Påminnelser hjelper kunden å huske timen. Koble til kalenderen din, så opptatte tider holdes utenfor bookingen.',
    benefitContinuityTitle: 'Ta opp tråden neste gang',
    benefitContinuityBody:
      'Åpne kundens historikk før neste time. Tidligere avtaler og notater gir deg et utgangspunkt for oppfølgingen.',
    continuityEyebrow: 'Kontinuitet mellom timene',
    continuityTitle: 'Husk det dere skal følge opp',
    continuityBody:
      'Private, krypterte notater ligger ved siden av booking og historikk. Du åpner dem når du forbereder neste time. Kunden ser ikke de interne notatene dine.',
    journalBoundary:
      'Har du journalplikt, bruker du Stemne ved siden av journalen din, ikke i stedet for.',
    customersAlt: 'Kundeoversikten i Stemne med tidligere og neste avtale',
    customersCaption: 'Kundeoversikten i Stemne, vist med demodata.',
    closingTitle: 'Gjør det enkelt å bestille neste time',
    closingBody: 'Legg inn behandlingene og tidene dine. Del lenken når du er klar.',
  },
  marketingAdvisorsLandingView: {
    homeAriaLabel: 'Stemne – home',
    login: 'Log in',
    eyebrow: 'For people who make a living from good conversations',
    title: 'Booking system for advisors',
    lead: 'Let the customer book for themselves. Keep video, customer notes and history in one place, so you can spend your time advising.',
    price: 'From NOK {price} per month, excluding VAT.',
    priceLoading: 'Loading the current subscription price.',
    priceUnavailable: 'The price is not available right now.',
    pricingLink: 'See prices and what is included',
    getStarted: 'Get started',
    nextConversation: 'For the next conversation',
    privateLabel: 'Only for you and your team',
    productImageAlt:
      'The customer card in Stemne with meeting notes marked Internal, which the customer cannot see.',
    productCaption:
      'Your notes stay with you. The customer only sees what they need to book and meet.',
    photoAlt: 'An advisor and a customer in conversation at a round table in a bright room.',
    workflowEyebrow: 'From the first booking to the next conversation',
    workflowTitle: 'A continuous thread through your advisory work',
    workflowLead: 'For people who work alone or together with other advisors.',
    step1Title: 'The customer chooses a conversation and time',
    step1Body:
      'You add your services and availability. The customer chooses a service and a time. For teams, available times can be collected on one booking page.',
    step2Title: 'Meet the customer with built-in video',
    step2Body:
      'The video meeting opens from the booking. The customer does not need to create an account. Video calls last up to 60 minutes, with a paid extension to 120 minutes.',
    step3Title: 'Carry the history forward',
    step3Body:
      'Collect meeting notes and previous conversations on the customer card. You and your team have the overview. The customer never sees internal notes or internal history.',
    footer: 'Stemne · Made in Norway',
    aboutAriaLabel: 'More about Stemne',
    privacy: 'Privacy',
    terms: 'Terms',
    contact: 'Contact us',
  },
  marketingLoginView: {
    emailPlaceholder: "you{'@'}email.com",
  },
  marketingPaymentTermsView: {
    kicker: 'Terms',
    title: 'Terms for Payment at booking',
    lastUpdated: 'Last updated {date}',
    lead: 'These terms apply when you have turned on the «Payment at booking» add-on and let customers pay by card when they book. They come in addition to the Terms of Service. Where something here differs from the Terms of Service, these terms govern the payments.',
    s1Heading: '1. You are the seller',
    s1Para1:
      'The customer buys the service from you. Stemne is the technical platform, and has an agreement with you, not with your customer.',
    s1Para2:
      "The payment goes into your own Stripe account. The money never passes through Stemne's account, and Stemne takes no share of it.",
    s1Para3:
      'You create the Stripe account through Stemne. The agreement covering the account itself is between you and Stripe (the Stripe Connected Account Agreement), and Stripe may ask you for identification and other documentation before you can receive money.',
    s2Heading: '2. What Stemne charges',
    s2Para1:
      'The add-on costs 199 kr per month excl. VAT, which is 248.75 kr with VAT. Yearly it is 2 029 kr excl. VAT. It is billed together with your subscription.',
    s2Para2: 'Stemne takes no commission on the payments.',
    s2Para3:
      'If you cancel the add-on, customers can no longer pay at booking. Payments you have already received, and the documents belonging to them, remain.',
    s3Heading: '3. You carry the card fee',
    s3Para1:
      "Stripe deducts its card fee before the money reaches your balance. The size follows Stripe's pricing and varies with the card type.",
    s3Para2:
      'You cannot pass the fee on to the customer. Charging extra because someone pays by card is prohibited under finansavtaleloven § 2-4.',
    s3Para3: 'The price the customer sees when booking is the whole amount the customer pays.',
    s4Heading: '4. Payouts',
    s4Para1:
      'You control the payout schedule and bank account in your Stripe dashboard. Stripe pays out on the schedule you choose.',
    s4Para2:
      'The timing of the first and later payouts follows Stripe’s terms and the status of your account.',
    s4Para3:
      'You see the balance, the next payout and every individual payment in your Stripe dashboard. Open it from Payments in settings.',
    s5Heading: '5. Refunds',
    s5Para1:
      'Stemne refunds automatically in three cases: the customer cancels within the notice period you set for the service, the customer cancels within the 14-day withdrawal period, or you cancel the meeting yourself.',
    s5Para2:
      'The refund is deducted from your Stripe balance. Stripe does not return the card fee from the original payment, so a refund costs you that fee.',
    s5Para3:
      'If your balance does not cover it, Stripe decides how to recover the amount under the agreement between you and Stripe.',
    s5Para4:
      'If you refund from your Stripe dashboard yourself, Stemne records it and sends a credit note to the customer. The meeting stays confirmed in Stemne until you cancel it.',
    s6Heading: '6. Customer disputes',
    s6Para1:
      'If the customer disputes a payment with their bank, Stripe deducts both the amount and a dispute fee from your account while the case runs.',
    s6Para2:
      'You answer the dispute in your Stripe dashboard within the deadline Stripe gives. Stemne notifies you by email, but cannot answer on your behalf.',
    s6Para3:
      'If the case goes your way, you get the amount back. If it does not, the deduction stands.',
    s7Heading: '7. Receipt, credit note and VAT',
    s7Para1:
      'Stemne produces the sales document in your name, with your organisation number and your address, and sends it to the customer. The number series is yours alone.',
    s7Para2: 'A refund produces a credit note, as bokføringsforskriften § 5-2-7 requires.',
    s7Para3:
      'You choose the VAT treatment per service and answer for that choice being right. Stemne looks up whether you are in Merverdiavgiftsregisteret and applies the answer to the documents.',
    s7Para4: 'If the service carries 25 % VAT, the price must be stated including VAT.',
    s8Heading: '8. Right of withdrawal',
    s8Para1:
      'The customer has 14 days to withdraw from the booking. Stemne shows the information the law requires, gives the customer the withdrawal form, and asks for the acknowledgement needed for the service to be delivered before the period ends.',
    s8Para2:
      'The duties under angrerettloven are yours as the seller. Stemne carries them out on your behalf, and you answer the customer yourself if they get in touch about the withdrawal right.',
    s8Para3:
      'If the customer withdraws before the meeting is held, the customer gets the whole amount back. That also applies when the customer is inside the withdrawal period but past the cancellation deadline you set.',
    s9Heading: '9. Liability',
    s9Para1:
      'Stripe, not Stemne, is responsible for any shortfall on the connected account. Your responsibility towards Stripe follows the agreement between you and Stripe.',
    s9Para2:
      'Stemne does not answer for whether the customer pays, nor for when Stripe pays out. The limitation of liability in the Terms of Service section 8 applies to the add-on as well.',
    s10Heading: '10. Changes',
    s10Para1:
      "We may change these terms. We give at least 30 days' notice of material changes, in the same way as in the Terms of Service section 9.",
    contact: 'Questions about the payment terms: {email}',
    footerHome: 'Back to the front page',
  },
  marketingPrivacyView: {
    navFront: 'Home',
    navGetStarted: 'Get started',
    kicker: 'Privacy',
    title: 'Privacy policy',
    lastUpdated: 'Last updated {date}',
    lead: 'Stemne is a booking service built in Norway for advisers, therapists and consultants. We are built on the principle that your data is yours. We collect only what we need to deliver the service, and you can delete everything with a single click.',
    s1Heading: '1. Who is the data controller',
    s1Controller1: 'Stemne is provided by',
    s1Controller2: '(company no.',
    s1Controller3: ', Skjetleinskogen 30, 7082 Kattem, Norway). Contact:',
    s1Processor:
      'When a business (operator) uses Stemne to book meetings with their customers, the operator is the data controller for customer data, and Stemne is the data processor under a data processing agreement.',
    s2Heading: '2. What data we process',
    s2OperatorsHeading: 'Operators (those who use Stemne)',
    s2Op1: 'Name, email address, phone number (for login and contact)',
    s2Op2: 'Organization name, company number, subdomain',
    s2Op3: 'Working hours and availability',
    s2Op4: 'The meeting types you offer, including price and duration',
    s2Op5: 'Payment information (processed by Stripe; we never store card data)',
    s2Op6:
      'Connected calendars (Google, Outlook). See the separate section "Use of Google services" below for details on what we read and write',
    s2CustomersHeading: 'End customers (those who book with an operator)',
    s2Cust1: 'Name and email address given at booking',
    s2Cust2: 'Meeting type, time and any message from the customer',
    s2Cust3: 'IP address and usage statistics (anonymized, only for security)',
    s2Cust4: 'For video meetings: attendance times (start/end), not meeting content',
    s3Heading: '3. Why we process the data',
    s3DeliveryLabel: 'Delivering the service:',
    s3DeliveryBody:
      'we need email and name to send booking confirmations, calendar invitations and video meeting links.',
    s3LegalLabel: 'Legal requirements:',
    s3LegalBody:
      'accounting data is kept for as long as Norwegian bookkeeping law requires (typically 5 years).',
    s3SecurityLabel: 'Security:',
    s3SecurityBody: 'logging of logins and critical events to detect misuse.',
    s3Basis:
      'The legal basis is contract fulfilment (GDPR art. 6(1)(b)) for booking and payment data, legally required processing (art. 6(1)(c)) for accounting, and legitimate interest (art. 6(1)(f)) for security logging.',
    s4Heading: '4. Where the data is stored',
    s4AppLabel: 'Application and database:',
    s4AppBody: 'Hetzner, Germany (EU)',
    s4BackupLabel: 'Backups:',
    s4BackupBody: 'encrypted storage within the EU/EEA',
    s4PaymentsLabel: 'Payments:',
    s4PaymentsBody: 'Stripe Inc. (USA), under the EU-US Data Privacy Framework',
    s4EmailLabel: 'Email:',
    s4EmailBody: 'Brevo (France, EU)',
    s4VideoLabel: 'Video meetings:',
    s4VideoBody: 'Whereby AS, Norway',
    s4ErrorsLabel: 'Error reporting:',
    s4ErrorsBody: 'Sentry (Germany, EU)',
    s4Transfer:
      'Data never leaves the EU/EEA without a valid transfer basis (typically Standard Contractual Clauses or Data Privacy Framework certification).',
    s5Heading: '5. Use of Google services',
    s5Intro:
      "When you as an operator connect your Google calendar to Stemne, we ask for access via Google's official OAuth flow. You explicitly see which permissions Stemne asks for, and you can withdraw access at any time.",
    s5ScopesHeading: 'Which permissions Stemne asks for',
    s5ScopeEvents:
      'read, create, change and delete calendar events on the calendars your Google account has access to.',
    s5ScopeOpenidAnd: 'and',
    s5ScopeOpenidBody: 'Only to show which Google account is connected in the admin view.',
    s5UsageHeading: 'What we do with the data',
    s5FreeBusyLabel: 'Reads busy/free',
    s5FreeBusyBody:
      'from your selected calendars to calculate available times in the booking flow.',
    s5ExistingLabel: 'Reads existing events',
    s5ExistingBody:
      'in your own calendar to show conflict warnings in the operator dashboard (only your own overview. These are never shown to your customers).',
    s5WriteLabel: 'Writes new Stemne bookings',
    s5WriteBody:
      'as calendar events in your primary calendar, with service, customer name, the customer\'s message, and either a video link or office address. You can turn off this synchronization at any time from "Manage calendar".',
    s5UpdateLabel: 'Updates and deletes',
    s5UpdateBody:
      'calendar events when a Stemne booking is moved, changes meeting format or is cancelled.',
    s5LimitedHeading: 'What we do not do (Google\'s "Limited Use" requirements)',
    s5LimitedIntro1: "Stemne's use of information received from Google APIs follows Google's",
    s5LimitedPolicyLink: 'API Services User Data Policy',
    s5LimitedIntro2: ', including the limited use requirements ("Limited Use"):',
    s5Limited1: 'We use Google data only to deliver the user-facing features described above.',
    s5Limited2:
      'We do not share Google data with third parties, except as necessary to deliver these features (e.g. infrastructure providers in the EU/EEA subject to strict confidentiality).',
    s5Limited3:
      'No humans read your Google data. Exceptions apply only if you have given explicit consent, the data is necessary for security (e.g. investigation of misuse), or we are required by law, and only anonymized/aggregated where possible.',
    s5Limited4:
      'We never use Google data to train AI or machine learning models, and never for advertising or resale.',
    s5TokensHeading: 'Storage and encryption of tokens',
    s5TokensBody:
      'The access token and refresh token we receive from Google are stored encrypted (AES-256-GCM) in our database in Germany (EU). The actual content of your calendar events stays with Google. We do not download or store calendar data for later use outside the operations that are necessary for the service to work.',
    s5RevokeHeading: 'How to withdraw access',
    s5RevokeStemneLabel: 'From Stemne:',
    s5RevokeStemne1: 'go to',
    s5RevokeStemneEm: 'Manage calendar',
    s5RevokeStemne2:
      'in the operator dashboard and click "Disconnect". Tokens are deleted immediately from our database.',
    s5RevokeGoogleLabel: 'From Google directly:',
    s5RevokeGoogle1: 'go to',
    s5RevokeGoogle2: 'and remove "Stemne" from the list of connected apps.',
    s5RevokeNote:
      'Existing calendar events that Stemne has created stay in your Google calendar after disconnecting. You can delete them manually in Google if you want.',
    s6Heading: '6. How long we keep the data',
    s6OperatorLabel: 'Operator account:',
    s6OperatorBody:
      'for as long as the subscription is active, plus 30 days after cancellation (allows time for data export).',
    s6CustomerLabel: 'End customer data:',
    s6CustomerBody:
      '12 months after the last meeting, or immediately if the customer requests deletion.',
    s6AccountingLabel: 'Accounting data:',
    s6AccountingBody: '5 years (the bookkeeping act).',
    s6LogsLabel: 'Security logs:',
    s6LogsBody: '90 days.',
    s7Heading: '7. Your rights',
    s7Intro: 'As a registered person you have the right to:',
    s7Right1: 'See which data we hold about you',
    s7Right2: 'Correct errors in your data',
    s7Right3: 'Have the data deleted (except what we must keep by law)',
    s7Right4: 'Receive your data in a machine-readable format',
    s7Right5: 'Withdraw consent when processing is based on it',
    s7Right6: 'Complain to the Data Protection Authority if you think we process data wrongly',
    s7Requests1:
      'End customers can delete all their data with an operator directly from the booking portal. Operators can delete their own account from account settings. For other requests, contact',
    s7Requests2: 'we respond within 30 days.',
    s7PortalLink: 'Open the customer portal',
    s7PortalBody:
      'to log in with your email. You can view, download or delete your data with Stemne.',
    s8Heading: '8. Cookies and tracking',
    s8Body:
      "Stemne uses only necessary cookies to keep you logged in and protect against CSRF. We use Sentry for error reports and feedback; the data is used for debugging, not marketing or product analytics. On the marketing site stemne.no we additionally use Google Ads, Meta and OpenAI Ads pixels to measure how well our ads are performing and share your visit with those providers, but only once you accept this in the cookie banner shown on your visit. If you decline, or leave the banner unanswered, we load none of these advertising trackers. This never applies to an operator's booking page, where their customers are never measured for ads. If you log in from a video meeting client, the service (e.g. Whereby) may set its own cookies. See their privacy policy.",
    s8Withdraw:
      'You can change your mind at any time. If you choose Decline after accepting, the ad trackers stop sending data from your visit straight away, and we delete the cookies they have set, without reloading the page. Change your choice under',
    s9Heading: '9. Security',
    s9Body:
      'We encrypt all data in transit (TLS) and sensitive fields at rest (AES-256-GCM). Login happens via passkey (WebAuthn) or a one-time link by email. We do not store passwords. Access to production data is limited to a minimum of people and logged.',
    s10Heading: '10. Changes',
    s10Body:
      'We notify you of material changes to this policy via email and a banner in the application at least 30 days before they take effect. Historical versions are available on request.',
    s11Heading: '11. Contact',
    s11PrivacyLabel: 'Privacy questions:',
    s11GeneralLabel: 'General inquiries:',
    s11AuthorityLabel: 'Data Protection Authority:',
    footerStemne: 'Stemne',
    footerFaq: 'Questions',
    footerPrivacy: 'Privacy',
    footerTerms: 'Terms',
  },
  marketingSignUpView: {
    segmentAdvisor: 'Advisor or consultant',
    segmentTrainer: 'Trainer or coach',
    yearlyHint: 'save up to {pct}%',
    pricePerYear: '{price}/yr',
    pricePerMonth: '{price}/mo',
    seatsUnlimited: 'Unlimited',
    seats: '1 user | {n} users',
    turnstileWaiting: 'hold on a moment while we confirm you are not a robot',
    back: '← Back',
    heading: 'Get your page set up.',
    lede: 'Start with your name and email. We’ll help you create a booking page after you verify your email. 3 months free. No card now.',
    segmentKicker: 'what do you do?',
    segmentHint: 'Helps us set up the right kind of services. You can change this later.',
    planKicker: 'choose a plan',
    planHint: '3 months free whatever you choose. No card now. You can change the plan anytime.',
    nameLabel: 'Name',
    namePlaceholder: 'First name Last name',
    emailLabel: 'Email',
    emailPlaceholder: "you{'@'}company.no",
    turnstileAriaLabel: 'Bot check from Cloudflare',
    submitCta: 'Set up my page →',
    loginPrompt: 'Already have an account?',
    loginLink: 'Log in with a passkey',
    loginSuffix: '.',
    termsPrefix: 'By continuing you accept our',
    termsLink: 'terms',
    termsMiddle: 'and',
    privacyLink: 'privacy policy',
    termsSuffix: '.',
  },
  marketingSwaggerDocsView: {
    loadError: 'Could not load the documentation. Reload the page.',
    docsAriaLabel: 'API documentation',
  },
  marketingTermsView: {
    navFrontpage: 'Home',
    navGetStarted: 'Get started',
    kicker: 'Terms',
    title: 'Terms of use',
    lastUpdated: 'Last updated {date}',
    lead: 'These terms govern the use of Stemne, a booking service for advisors, therapists and consultants. By creating an account or booking a meeting through the service, you accept the terms below.',
    s1Heading: '1. About the service',
    s1Para1:
      'Stemne is provided by {company} (org. no. 937 952 732, Skjetleinskogen 30, 7082 Kattem, Norway), hereafter "we" or "Stemne". The service comprises an online tool for receiving and managing bookings, sending calendar invitations, holding video meetings (via Whereby) and communicating with customers.',
    s1Para2:
      'The service is provided "as is". We work continuously to improve it and may change, add or remove features without notice, as long as the changes do not substantially reduce the core functionality you subscribe to.',
    s1Para3:
      'Video meetings are limited to 60 minutes per meeting. You can buy extended duration that lets each video meeting last up to 120 minutes. The limit applies to the video room itself, not to the length of the service you offer.',
    s2Heading: '2. User account',
    s2Para1:
      'You must be over 18 and have the right to enter into agreements on behalf of your business to create an operator account. You are responsible for keeping login information safe and for all activity that happens from your account.',
    s2Para2:
      'You can delete your account at any time from account settings. We may suspend or terminate accounts that breach these terms or use the service in a way that harms us or other users.',
    s3Heading: '3. Subscription and payment',
    s3Para1:
      'Subscription prices are shown in the price list on stemne.no. You pay monthly or yearly via Stripe. Prices are stated excluding VAT; VAT is added where applicable.',
    s3Para2:
      'If a payment is not completed, we notify you and give 14 days to update the payment method before we suspend the service.',
    s4Heading: '4. Refund and cancellation',
    s4Para1:
      'Stemne is an ongoing subscription. You pay for access during the period you have chosen, and can stop the charge whenever you want. We do not refund the current period.',
    s4Para2:
      'This applies regardless of whether you used the service a lot or a little during the period. Consumer rights under mandatory legislation apply regardless of these terms.',
    s4Annex:
      'If you accept card payment when customers book, separate terms govern those payments: {link}.',
    s4AnnexLink: 'Terms for Payment at booking',
    s5Heading: "5. Your content and your customers' data",
    s5Para1:
      'You keep ownership of all content you enter into Stemne: meeting notes, customer data, availability and so on. We have a limited right to process the data in order to provide the service to you.',
    s5Para2:
      'When you process personal data about your customers in Stemne, you are the data controller and we are the data processor. The details are set out in the data processing agreement that is entered into automatically when you create an account.',
    s5Cta: 'Are you an end customer of {name}? {link} to view, download or delete your own data.',
    s5CtaLink: 'Open the customer portal',
    s6Heading: '6. Acceptable use',
    s6Intro: 'You may not use Stemne to:',
    s6Item1: 'Send spam or unwanted communication',
    s6Item2: 'Break Norwegian law or other relevant regulations',
    s6Item3: 'Manipulate the booking system or bypass security measures',
    s6Item4: 'Reverse-engineer, copy or resell the service',
    s6Item5: 'Use the service in a way that harms other users or the technical operation of Stemne',
    s7Heading: '7. Availability and support',
    s7Para1:
      'We aim for 99.5 % uptime for the production environment, but give no guarantee. Planned maintenance is announced at least 48 hours in advance and carried out outside business days where possible.',
    s7Para2:
      'Support is given by email to {email} within one business day. Urgent cases that block core operation (login, booking) are prioritised the same day.',
    s8Heading: '8. Limitation of liability',
    s8Para1:
      'We are not liable for indirect losses, including lost income, lost data or damage to reputation, resulting from use of or lack of access to the service. Our total liability toward you is in any case limited to what you have paid for the service in the last 12 months before the incident.',
    s8Para2:
      'The limitation does not apply to losses caused by gross negligence, intent or breach of consumer rights.',
    s9Heading: '9. Changes to the terms',
    s9Para1:
      'We may change these terms. Substantial changes are announced at least 30 days before they take effect, by email and a banner in the application. If you do not accept the changes, you can cancel the subscription before they take effect.',
    s10Heading: '10. Choice of law and venue',
    s10Para1:
      'These terms are governed by Norwegian law. Disputes are sought resolved amicably. If that does not succeed, {court} is the venue.',
    s11Heading: '11. Contact',
    s11Terms: 'Questions about the terms: {email}',
    s11Privacy: 'Privacy: {email}',
    footerHome: 'Stemne',
    footerFaq: 'Questions',
    footerPrivacy: 'Privacy',
    footerTerms: 'Terms',
  },
  verifyCode: {
    eyebrow: 'confirm your email',
    body: 'Enter the six digits from the email.',
    label: 'Code from the email',
    placeholder: '000000',
    submit: 'Confirm code',
  },

  marketingVerifyView: {
    rateLimited: 'Wait a moment and try again.',
    invalidLink: 'The confirmation is invalid or expired. Request a new code.',
    alreadyVerifiedMeta: 'all good',
    alreadyVerifiedBody:
      'Your account exists. Log in with your email address and we will send you a link.',
    goToLogin: 'Go to login',
    sessionMismatchBody: 'Enter your name and email address to get a new code and continue here.',
    sessionMismatchBodyKnownEmail: 'Send a new code and enter it here to continue.',
    recoveryNameLabel: 'Name',
    recoveryEmailLabel: 'Email address',
    recoveryEmailHint: 'Enter the new code in this browser.',
    turnstileWaiting: 'One moment while we confirm you are not a robot.',
    turnstileAriaLabel: 'Bot check',
    resendFailed: 'We could not send a new code. Try again shortly.',
    codeFailed: 'We could not confirm the code. Try again shortly.',
    verifyingHeading: 'Confirming your email...',
    errorMeta: 'something went wrong',
    errorBody: 'You can send a new code, or try again with a different address.',
    resend: 'Send again',
    resendCooldown: 'Send again in {n}s',
    tryAnotherAddress: 'Try a different address',
    backToSignup: 'Back to signup',
    resentHeading: 'Look for a new code',
    toPrefix: 'to',
  },
  meetingInMeetingView: {
    mobilePanesLabel: 'Choose meeting view',
    videoTab: 'Video',
    backToPrep: '← Preparations',
    leaveMeeting: 'Leave the meeting',
    iframeTitle: 'Stemne meeting room',
    preparingRoom: 'Opening the video room',
    fallbackTitle: 'Video is not set up',
    fallbackBody:
      'This meeting has no video link. Get in touch with the operator if you expected to meet on video, or use the email address from the invitation to arrange an in-person meeting.',
    chatTab: 'Chat',
    notesTab: 'Notes',
    notesHeading: 'Notes',
    notesPrivate: 'private',
    notesDefaultTitle: 'During the meeting',
    wrapUpCta: 'Finish and summarize →',
  },
  portalManageView: {
    advisorReturning: 'You are meeting {name} again.',
    documentsLabel: 'Documents to review',
    messagesLabel: 'Messages',
    seriesSlotTime: '{date} at {time}',
    withdrawal: {
      label: 'Right of withdrawal',
      body: 'The right of withdrawal is separate from ordinary cancellation. Send a clear withdrawal notice to {operator}. The message is stored with its date and time, and the operator will follow up on the meeting and any payment.',
      open: 'Send withdrawal notice',
      formLink: 'Open the printable withdrawal form',
      emailLink: 'Send by email to {email} instead',
      sentAt: 'You sent the withdrawal notice to {operator} {date}. It is stored with its time.',
      modalTitle: 'Use the right of withdrawal?',
      modalBody:
        'Review the message before sending. This notifies the operator, but does not cancel the meeting automatically.',
      messageLabel: 'Withdrawal notice',
      submit: 'Send withdrawal notice',
      cancel: 'Go back',
      subjectFallback: 'the agreement',
      operatorFallback: 'the operator',
      emailSubject: 'Withdrawal notice – booking {reference}',
      prefill:
        'I hereby give notice that I am exercising the right of withdrawal for “{subject}”, scheduled for {date}.\n\nBooking reference: {reference}',
    },
    statusReadonly: {
      cancelled: 'This meeting has been cancelled.',
      expired: 'This meeting has expired.',
      completed: 'This meeting has taken place.',
      noShow: 'This meeting did not take place.',
      unresolved: 'The time for this meeting has passed.',
      pending: 'This meeting is awaiting confirmation.',
    },
    rebookPrompt: 'Would you like to book another?',
  },
  portalPollVoteView: {
    eyebrowInvite: 'Meeting invitation',
    eyebrowVote: 'Vote on a time',
    titleInvite: 'You are invited to a meeting with {name}',
    titleVote: 'Find a time that works with {name}',
    submitError: 'Could not save your vote. Try again.',
    suggestError: 'Could not suggest the time. Try again.',
    loading: 'Loading the vote...',
    meetingTimeLead: 'Time for the meeting',
    committedLead: 'The time is set',
    committedNote: 'You will get a separate confirmation by email with the details.',
    voteLead:
      'This is the proposed time. Let us know if it works, or if one of the others works better.',
    othersSubhead: 'These work too',
    deadlineNote:
      'Vote or suggest a new time by {deadline}. If we hear nothing, the proposed time is chosen.',
    savedNote: 'Your vote is saved. Thanks.',
    updateVoteCta: 'Update vote',
    saveVoteCta: 'Save vote',
    closed: 'The vote is closed.',
    resultLabel: 'Result so far',
    suggestCta: 'Suggest a time',
    dateLabel: 'Date',
    timeLabel: 'Time',
    suggestSubmitCta: 'Suggest the time',
    rsvpLabel: 'Can you attend?',
    rsvpConfirmed: 'You have confirmed.',
    rsvpDeclined: 'You have declined.',
    rsvpConfirmCta: 'Confirm',
    rsvpDeclineCta: 'Decline',
  },
  portalPortalView: {
    emailPlaceholder: "you{'@'}email.com",
  },
  portalRescheduledView: {
    icsSummaryFallback: 'Meeting',
    icsLocationOnline: 'Online',
  },
  portalRoomView: {
    defaultOperatorName: 'your advisor',
    unavailableHeading: 'The room is not available right now.',
    unavailableBody:
      'The link has either expired or belongs to an organisation that has not turned on shared rooms yet. Ask for a new link if it has been a while since you got the email.',
    backToPortal: 'Back to the portal',
    errorHeading: 'Something went wrong.',
    retry: 'Try again',
    kicker: 'shared room',
    heading: 'Shared room with {operator}',
    lead: 'Here you talk with {operator} and find everything they have shared with you. Messages and documents in one place, safe behind a login, not over email.',
    emptyBody:
      'No messages or documents shared yet. When {operator} shares something with you, it shows up here.',
    advisorTitle: 'Your advisor',
    privacy:
      'The documents are only visible to you and {operator}. Some may have an expiry date, download them if you want to keep them.',
  },
  setupDashboardAccountView: {
    tenantHostFallback: 'your page',
    profileSaved: 'Your profile is saved',
    profileSaveError: 'Could not save. Try again.',
    emailEmpty: 'The email address cannot be empty.',
    emailSameAsCurrent: 'The new address must be different from the current one.',
    emailInUse: 'The address is already in use by another account.',
    emailInvalid: 'Invalid email address.',
    emailSendError: 'Could not send the confirmation link. Try again.',
    passkeyAdded: 'Passkey added',
    passkeyRenamed: 'The name is updated',
    passkeyLastCannotRemove: 'Cannot remove your last passkey.',
    passkeyRemoved: 'Passkey removed',
    sessionsRevoked: 'Logged out {n} other session | Logged out {n} other sessions',
    sessionsSudoRequired: 'Confirm with your passkey first',
    sessionsRevokeError: 'Could not log out other sessions',
    dateJustNow: 'just now',
    dateMinutesAgo: '{n} min ago',
    dateToday: 'today',
    dateYesterday: 'yesterday',
    breadcrumb: 'my account',
    heading: 'My account',
    tabProfile: 'Profile',
    tabAvailability: 'Availability',
    tabMyServices: 'My services',
    tabPasskeys: 'Passkeys',
    tabNotifications: 'Notifications',
    avatarChangeAria: 'Change profile photo',
    avatarUploadAria: 'Upload profile photo',
    photoSwap: 'Swap photo',
    photoUpload: 'Upload photo',
    nameLabel: 'Name',
    titleLabel: 'Title',
    titleHint: 'Shown on your profile page.',
    bioLabel: 'A bit about you',
    bioHint: 'Shown at the top of {host}. 1–3 sentences works best.',
    locationLabel: 'City / place',
    phoneLabel: 'Phone (optional)',
    loginEmailLabel: 'Login email',
    changeEmailCta: 'Change email',
    saveChanges: 'Save changes',
    passkeysTitle: 'Passkeys',
    addPasskeyCta: 'Add passkey',
    passkeysDesc:
      'Each device you log in from gets its own passkey. We recommend <strong>at least two</strong>, one on your phone, one on a computer or security key, so you have a backup.',
    singlePasskeyWarning:
      '<strong>You only have one passkey.</strong> If the device is lost, you can still log in via a login link sent to your email, but it is much faster if you have an extra passkey ready (phone plus computer, for example).',
    pillPrimary: 'primary',
    pillSynced: 'synced',
    pillSyncedTitle:
      'Saved in your cloud keychain. You can safely remove it from this device, it also lives on other devices you are logged in on.',
    pillLocal: 'only on this device',
    pillLocalTitle:
      'Saved only locally on this device. If you remove it here, you lose access from all other devices, use a magic link to log in again.',
    metaAdded: 'added {date}',
    metaLastUsed: '· last used {date}',
    renameCta: 'Change name',
    sessionsTitle: 'Active sessions',
    sessionsDesc:
      'You are logged in on <strong>{count}</strong> devices or browsers. If you suspect someone else has access, you can log out all other sessions, the one you are using now stays.',
    sessionsLogOutOthers: 'Log out all others',
    emailModalTitle: 'Change login email',
    emailSentText:
      'We have sent a confirmation link to <strong>{email}</strong>. The link is valid for 24 hours. The current email works until you have confirmed the new one.',
    emailModalLede:
      'We send a confirmation link to the new address. The current one works until you have confirmed.',
    newEmailLabel: 'New email',
    sendConfirmLink: 'Send confirmation link',
  },
  setupDashboardBookingDetailView: {
    startAnywayTitle: 'Start the meeting anyway?',
    crumbMeeting: 'Meeting',
    crumbPrivateMeeting: 'Private meeting',
    greetingSaved: 'The greeting has been saved.',
    greetingSaveError: 'Could not save the greeting.',
    guestAdded: 'The guest has been added and invited.',
    guestAddError: 'Could not add the guest.',
    guestRemoved: 'The guest has been removed.',
    coHostRemoved: 'The co-host has been removed.',
    attendeeRemoveError: 'Could not remove the attendee.',
    roleHost: 'Host',
    roleYou: 'you',
    roleColleague: 'Colleague',
    badgeHost: 'host',
    badgeCustomer: 'customer',
    badgeGuest: 'guest',
    badgeCoHost: 'co-host',
    waitingForNames: ' · waiting for {names}',
    declinedNames: ' · {names} declined',
    confirmationSummary: '{confirmed} of {total} have confirmed{waiting}{declined}',
    lockTimeError: 'Could not lock the time.',
    timeLocked: 'The time is locked',
    voteSaved: 'Your vote has been saved.',
    voteSaveError: 'Could not save the vote.',
    movedToCustomerCard: 'Moved to the customer card.',
    movePointError: 'Could not move the point.',
    notFoundOrNoAccess: 'The meeting does not exist, or you do not have access.',
    loadError: 'Could not load the meeting.',
    slotLabel: '{date} at {time}',
    durationMin: '{mins} min',
    durationEditLabel: 'Duration',
    durationEditAction: 'Change duration',
    durationRangeError: 'Duration must be a whole number of minutes between 1 and 480.',
    durationVideoError: 'Video meetings can last up to {max} minutes on your plan.',
    modeVideo: 'Video',
    modeOffice: 'Office',
    modeClinic: 'Clinic',
    anAdvisor: 'an advisor',
    markedNoShow: 'Marked as no-show.',
    markNoShowError: 'Could not mark as no-show.',
    markedCompleted: 'Marked as completed.',
    markCompletedError: 'Could not mark as completed.',
    refunded: 'The payment was refunded. The customer has the credit note by email.',
    refundError: 'Could not refund the payment.',
    paymentRequestResent: 'The payment link was sent again.',
    paymentRequestResendError: 'Could not send the payment link again.',
    paymentRequestCancelled: 'Payment collection stopped.',
    paymentRequestCancelError: 'Could not stop payment collection.',
    paymentCollection: {
      active: 'Payment collection resumed.',
      paused: 'Automatic reminders paused.',
      disputed: 'The claim was marked as disputed.',
      error: 'Could not update payment collection.',
    },
    meetingBooked: 'The meeting is booked.',
    bookMeetingError: 'Could not book the meeting.',
    revealError: 'Could not show the meeting.',
    headingUnresolved: 'The meeting was not registered.',
    headingCancelled: 'The meeting is cancelled.',
    headingExpired: 'The meeting has expired.',
    headingPast: 'Write the notes.',
    headingPrep: 'Prepare the meeting.',
    summaryCalloutTitle: 'Review before sending',
    summaryCalloutBody: 'Review and edit the summary the participants will receive.',
    summaryCalloutAction: 'View and edit summary',
    summarySentTitle: 'The summary has been sent',
    summarySentBody: 'Sent {when} to {to}. You can send it again if something has changed.',
    summarySentBodyNoRecipient: 'Sent {when}. You can send it again if something has changed.',
    summarySentAction: 'View and send again',
    ledeUnresolved: 'We saw no video call in this time window.',
    ledeCancelled: 'Anything already written is preserved as history and cannot be changed here.',
    unresolvedSetNewTime: 'Set a new time',
    unresolvedModalKicker: 'meeting · unresolved',
    unresolvedModalBody:
      'We saw no video call in this time window. Mark it as completed if you met another way, or close to set up a new time.',
    completedPaymentCollectionNotice:
      'When you mark the meeting as completed, Stemne sends the customer a payment link for any outstanding balance. Unpaid requests get automatic reminders after 7 and 14 days.',
    unresolvedModalDismiss: 'Not now',
    ledePastNamed:
      'The meeting with {name} is completed. The notes are only visible to you and colleagues with access.',
    ledePast:
      'The meeting is completed. The notes are only visible to you and colleagues with access.',
    ledePrep: 'Your notes are only visible to you and colleagues with access.',
    resumeMeeting: 'Resume the meeting →',
    startMeeting: 'Start the meeting →',
    kebabKicker: 'meeting',
    kebabKickerCancelled: 'meeting · cancelled',
    kebabJoin: 'Join the meeting',
    rescheduleOrCancel: 'Reschedule or cancel',
    markCompleted: 'Mark as completed',
    markNoShow: 'Mark as no-show',
    sectionLifecycle: 'lifecycle',
    sectionFollowUp: 'follow-up',
    rebook: 'Book a new meeting',
    refund: 'Refund',
    visibilityNoGrants: 'visible to you and colleagues with access',
    visibilityYouAndOne: "visible to you and {'@'}{name}",
    visibilityYouAndTwo: "visible to you, {'@'}{first} and {'@'}{second}",
    visibilityYouAndMany: "visible to you, {'@'}{name} + {count} others",
    summaryEmpty: 'empty',
    wordCount: '{count} words',
    asksSummary: 'empty | 1 question | {n} questions',
    ownerYou: 'assignee · you',
    ownerNamed: 'assignee · {name}',
    changeLocation: 'Change location',
    addLocation: 'Add location',
    inviteResent: 'The invitation has been resent to {email}.',
    verificationResent: 'The confirmation link has been resent to {email}.',
    resendError: 'Could not resend.',
    meetingUpdated: 'The meeting is updated and the participants have been notified.',
    saveChangesError: 'Could not save the changes.',
    unsavedChangesConfirm:
      'You have unsaved changes to the meeting. Do you want to leave the page and discard them?',
    meetingReassigned: 'The meeting has been transferred. Both advisors are notified.',
    reassignError: 'Could not transfer the meeting.',
    meetingCancelled: 'The meeting is cancelled',
    cancelError: 'Could not cancel the meeting.',
    seriesCancelled: 'The rest of the series is cancelled',
    seriesCancelError: 'Could not cancel the series.',
    seriesRescheduled:
      '{n} meeting in the series was moved | {n} meetings in the series were moved',
    seriesRescheduleError: 'Could not move the series.',
    settingSaved: 'The setting for this meeting has been saved.',
    settingSaveError: 'Could not save the setting.',
    loadingMeeting: 'Loading the meeting...',
    redactedTitle: 'Not visible to you.',
    redactedLede:
      "The meeting is booked on a private service type. Only the person responsible for the meeting sees the customer's name, notes, and links. The time is shown so you can plan around it.",
    showAsAdmin: 'Show as administrator',
    accessLogged: 'the access is logged',
    returningCustomer: 'Returning customer · last met {date} with {name}',
    moreActions: 'More actions',
    openMeeting: 'Open the meeting →',
    openingRoom: 'Opening room',
    sendMessage: 'Send message',
    sendMessageTo: 'Send message to {navn}',
    attendees: 'Participants',
    removeCoHost: 'Remove co-host',
    removeGuest: 'Remove guest',
    resending: 'sending ...',
    resendLink: 'Resend link',
    inviteIntroLabel: 'Greeting in the invitation (optional)',
    inviteIntroPlaceholder: "Hi, let's gather to review ...",
    inviteIntroHint: 'Included in the invitation to new guests, above the agenda.',
    saveGreeting: 'Save greeting',
    addGuest: 'Add guest',
    emailLabel: 'Email',
    previousParticipantLabel: 'Find a previous participant',
    previousParticipantPlaceholder: 'Search by name or email',
    useEmailAddress: 'Use email address',
    nameOptionalLabel: 'Name (optional)',
    namePlaceholder: 'First name Last name',
    sendInvitation: 'Send invitation',
    meetingInProgress: 'The meeting has been in progress since {time}',
    meetingOverLabel: 'The meeting is over',
    endedAt: 'Ended {time}',
    endedDuration: ' · lasted {min} min',
    endedSummaryPrompt: ', do you want to send a summary to {name}?',
    // Replaces the prompt once the summary has already been sent — the
    // callout right below carries when and to whom.
    endedSentenceEnd: '.',
    participants: 'the participants',
    pendingLabel: 'Waiting for confirmation',
    pendingBody: '{name} has not opened the link yet. Resend if it has been a while.',
    awaitingPaymentLabel: 'Waiting for payment',
    awaitingPaymentBody:
      '{name} has not paid yet. The meeting is confirmed automatically once the payment goes through.',
    theCustomer: 'The customer',
    sending: 'Sending ...',
    resendVerification: 'Resend confirmation link',
    cancellationReasonLabel: 'Cancellation reason',
    rescheduleMessageLabel: 'Message sent when rescheduling',
    rescheduleApprovalLabel: 'Change of time',
    rescheduleApprovalInherit: 'Follow the default for the service',
    rescheduleApprovalRequire: 'Require approval (the customer proposes)',
    rescheduleApprovalAllow: 'Allow direct change',
    rescheduleApprovalHint:
      'Applies to this meeting only. Group meetings always go through proposals.',
    remindersStatusAria: 'Reminder status',
    remindersLabel: 'Reminders',
    reminderEmailChannel: '24h email',
    reminderSmsChannel: '24h SMS',
    reminderSent: 'sent {time}',
    reminderWaiting: 'waiting',
    asksTitle: 'What do we need answers to?',
    cancelledAsksTitle: 'Questions planned for the meeting',
    notesTitle: 'Notes',
    cancelledNotesTitle: 'Preserved notes',
    notesFromMeetingTitle: 'Notes from the meeting',
    preparationsTitle: 'Preparations',
    liveNotesLabel: 'During the meeting',
    prepNotesLabel: 'Preparations',
    cancelledPrepNotesLabel: 'Preparations before cancellation',
    cancelledPlanTitle: 'Planned for the meeting',
    heldPointsTitle: 'Action points',
    heldPointsSummary: 'empty | 1 point | {n} points',
    heldPointsOpenSummary: '1 open | {n} open',
    accessTitle: 'Access',
    documentsTitle: 'Documents',
    transfersLabel: 'transfers',
    meetingActivityLabel: 'meeting history',
    customerMessageLabel: 'Message from customer',
    meetingActions: 'Meeting actions',
    revealConfirmTitle: 'Show private meeting?',
    revealConfirmBody:
      'You are an administrator. When you continue, the access is recorded in the audit log together with the time and your account. Only do this when it is necessary.',
    revealConfirmCta: 'Show the meeting',
    noShowConfirmTitle: 'Mark as no-show?',
    noShowConfirmBody:
      "The meeting is moved to the 'no-show' status. You cannot undo this. Use it when the customer did not show up and the meeting was not held.",
    refundConfirmTitle: 'Refund {amount}?',
    refundConfirmBody:
      'The customer gets the full amount back on their card within 5 to 10 business days, and you send a credit note right away. The meeting is unchanged — cancel it afterwards if it is not going ahead. This cannot be undone.',
    lockTimeTitle: 'Lock the time',
    lockTimeBody:
      'The participants get an email that the time is set, with an optional message from you.',
    messageOptionalLabel: 'Message (optional)',
    messageToParticipantsPlaceholder: 'Short message to the participants',
    lockTimeCta: 'Lock time',
    saveConfirmTitle: 'Save and notify the participants',
    saveConfirmBody:
      'The changes are saved and the participants get one email that the meeting is updated.',
    saveConfirmBodyPending:
      'The customer has not confirmed the meeting yet. The changes are saved, and the customer gets a new invitation with the updated time for them to confirm.',
    diffKicker: 'This has changed',
    messageToParticipantsLabel: 'Message to the participants',
    saveAndSend: 'Save and send',
    unsavedChangesAria: 'Unsaved changes',
    unsavedCount: '{n} change not saved | {n} changes not saved',
    discard: 'Discard',
  },
  setupDashboardCalendarView: {
    weekdayMon: 'Mon',
    weekdayTue: 'Tue',
    weekdayWed: 'Wed',
    weekdayThu: 'Thu',
    weekdayFri: 'Fri',
    weekdaySat: 'Sat',
    weekdaySun: 'Sun',
    dowSun: 'S',
    dowMon: 'M',
    dowTue: 'T',
    dowWed: 'W',
    dowThu: 'T',
    dowFri: 'F',
    dowSat: 'S',
    meetingCreatedWithInvite: 'Meeting created. The customer has received the invitation.',
    slotAlreadyBooked: 'That time is already booked. Pick another time.',
    meetingCreateFailed: 'Could not create the meeting.',
    meetingCreated: 'Meeting created.',
    endBeforeStart: 'End time must be after start time',
    periodAdded: 'Period added',
    periodAddFailed: 'Could not add the period.',
    allDayPrefix: 'all day · {label}',
    blockedPrefix: 'Blocked · {label}',
    meetingsThisDay: '{n} meetings this day',
    showAll: 'Show all',
    weekOverflowMore: '+{count} more',
    rescheduleTitle: 'Move the meeting?',
    rescheduleBody:
      'Move {title} from {from} to {to}? The customer gets an updated invitation by email.',
    moveMeeting: 'Move the meeting',
    conflictIntro:
      'These meetings clash with appointments already in the calendar you just connected. You have to move or cancel them manually.',
    conflictConfirm: 'Got it',
    settingsTitle: 'Calendar settings',
    viewLabel: 'View',
    connectedCalendars: 'Connected calendars',
    hourRangeLabel: 'Hours in day and week view',
    hourRangeHint: 'Working hours only shows 08–17, extended by your working hours.',
    zoomHeading: 'Zoom the time axis',
    zoomHint: 'Pinch the grid with two fingers to stretch or compress the hours.',
    zoomResetCta: 'Fit to screen',
    showHolidaysLabel: 'Show holidays',
    showHolidaysDesc: 'Easter, Pentecost and other red-letter days show in the calendar.',
    blockedTimeHeading: 'Blocked time',
    blockedTimeHint: 'Mark time ranges as unavailable for booking.',
    blockTimeCta: 'Block time',
  },
  setupDashboardCustomerConversationView: {
    meetingCount: '{n} meeting | {n} meetings',
    customerSince: 'customer since {date}',
    bookingCreated: 'The meeting is booked.',
    bookingFailed: 'Could not book the meeting.',
    backAria: 'Back to the customer card',
    seeProfile: 'See profile',
    customerNotFound: 'Customer not found.',
    fencedThread: 'This conversation is assigned to another advisor.',
    loadingConversation: 'Loading the conversation ...',
    suggestNewTime: 'Suggest a new time',
    deleteMessageTitle: 'Delete the message?',
    deleteMessageBody: 'It will disappear for the customer too. This cannot be undone.',
    deleteMessageConfirm: 'Delete',
  },
  setupDashboardCustomerDetailV2View: {
    customerDeleted: 'Customer deleted',
    customerDeleteError: 'Could not delete the customer.',
    meetingBooked: 'The meeting is booked.',
    meetingBookError: 'Could not book the meeting.',
    noteNeedsMeeting:
      'Notes belong to a meeting. Book one first, then you can write notes from the meeting page.',
    customerInfoLoadError: 'Could not load customer info.',
    customerInfoUpdated: 'Customer info updated.',
    // Email changes are verify-before-commit: the customer row keeps the old
    // address until the customer clicks the link, so the save toast must not
    // claim the address is changed (#3488).
    customerInfoUpdatedEmailPending:
      'Customer info updated. We sent a confirmation link to {email} — the address changes once the customer confirms.',
    customerInfoSaveError: 'Could not save customer info.',
    meetNow: 'Meet now',
    bookMeeting: 'Book a meeting',
    writeNote: 'Write a note',
    shareDocument: 'Share document',
    emptyDocuments: 'No documents shared yet.',
    shareDocumentCta: 'Share a document',
    emptyMeetings: 'No meetings yet.',
    emptyConversations: 'No conversations yet.',
    emptyFilter: 'No results for this filter yet.',
    loadingCustomer: 'Loading customer...',
    notFound: 'Customer not found.',
    backToCustomerList: 'Back to the customer list',
    breadcrumbCustomers: 'Customers',
    bellAria: '{count} new since last time, go to {pill}',
    kickerCustomer: 'customer',
    editContactInfo: 'Edit contact info',
    since: 'since {date}',
    personalNotesChip: 'personal notes',
    // "Everything we have ON someone" is the register of a dossier, which is a
    // poor fit for a product that sells its privacy posture. The Norwegian
    // ("Alt vi har om …") is neutral; this matches it.
    allWeHave: 'Everything about {name}.',
    newestFirst: 'newest first',
    treatmentLocked:
      'You can see the profile and book or prepare meetings. Treatment data such as meeting notes, documents and conversations opens when you have a meeting with this customer.',
    lockedPanelTitle: 'Treatment data is locked',
    lockedPanelBody:
      'Treatment data opens when you have a meeting with this customer. Book a meeting to get access.',
    retry: 'Try again',
    searchPlaceholder: "search {name}'s history ...",
    ariaForberedelser: 'Preparations',
    ariaFiler: 'Files',
    ariaNotater: 'Notes',
    ariaHistorikk: 'History',
    ariaTimeline: 'Timeline, filter {pill}',
    ariaSidebar: 'Sidebar',
    fabLabel: 'Actions for the customer',
    deleteCustomerTitle: 'Delete the customer?',
    deleteCustomerBody:
      'Delete {name}? Notes, preparations, and history tied to the customer are also deleted. This cannot be undone. If the customer has meetings, the deletion is stopped.',
    deleteCustomerConfirm: 'Delete customer',
    personalNotesModalTitle: 'Personal notes',
  },
  setupDashboardCustomersView: {
    sortRecent: 'last activity',
    sortNotifications: 'number of notifications',
    sortName: 'name',
    relToday: 'today · {time}',
    relTomorrow: 'tomorrow · {time}',
    relYesterday: 'yesterday',
    relLastWeek: 'last week',
    exportCta: 'Export',
    // Not "Import more" — the button is there before a first import too.
    importMoreCta: 'Import',
    sortedBy: 'sorted by',
  },
  setupDashboardDocumentsView: {
    offLead:
      'A shared space for documents is on the way. You will hear from us when it is ready for you to use.',
    manageAria: 'Manage {name}',
    sharedWith: ' with {name}',
    sharedCount: '{count} documents shared',
    sharedOne: 'Shared',
  },
  setupDashboardHelpArticleView: {
    backToHelp: 'Back to help',
    footPrompt: "Didn't get the answer you needed?",
    footLink: "Write to support{'@'}stemne.no",
    footTail: ', we reply within one business day.',
    notFound: "We couldn't find that article.",
    backToOverview: 'Back to the overview',
    imageDialogLabel: 'Enlarged image',
    closeZoom: 'Close image',
  },
  setupDashboardHelpIndexView: {
    catKonto: 'Account and sign-in',
    catTeam: 'Team and roles',
    catKalender: 'Calendar and video meetings',
    catBooking: 'Booking and customers',
    catBetaling: 'Payment at booking',
    catProfil: 'Profile and branding',
    catFakturering: 'Billing and subscription',
    catAnnet: 'Other',
    kicker: 'Help',
    title: 'How can we help?',
    lead: "Short, practical articles about how you do the most common things in Stemne, inviting a colleague, connecting your calendar, or changing your page. Can't find what you're looking for? Write to {support}.",
    searchPlaceholder:
      'Search the help articles, for example "connect calendar" or "invite colleague"',
    searchAria: 'Search the help articles',
    noResults: 'Found no matches for "{query}". Try another search word, or write to {support}.',
    resultsCount: '{count} matches for "{query}"',
    emptyBody: 'No help articles have been published yet.',
  },
  setupDashboardHistoryView: {
    backOverview: 'Overview',
    kicker: 'full history · all customers',
    title: 'Everything happening at your company.',
    newestFirst: 'newest first',
    searchPlaceholder: 'search the whole history ...',
    searchAriaLabel: 'Search the whole history',
    upcomingKicker: 'upcoming',
    meetingFallback: 'Meeting',
    hosAdvisor: 'with {name}',
    emptyTitle: 'Quiet so far.',
    emptyBody: 'When customers book, send messages or receive documents, everything gathers here.',
    noHits: 'No matches for "{query}" in this period.',
    thisWeek: 'this week',
    bookingLabel: 'meeting | meetings',
    newCustomerLabel: 'new customer | new customers',
    documentLabel: 'document | documents',
    team: 'team',
    teamHint: "Tap to see one advisor's events",
    ghostBook: 'book a meeting for a customer',
    ghostShowOlder: 'show older events',
    roleOwner: 'owner',
    roleAdvisor: 'advisor',
    roleAdmin: 'administrator',
  },
  setupDashboardInnsiktView: {
    noConsultant: 'No consultant',
    daySun: 'su',
    dayMon: 'mo',
    dayTue: 'tu',
    dayWed: 'we',
    dayThu: 'th',
    dayFri: 'fr',
    daySat: 'sa',
    barTitle: '{dow} {day}.: {count} meeting | {dow} {day}.: {count} meetings',
    kicker: 'insights · {period}',
    title: 'How the practice is doing.',
    selectPeriod: 'Choose period',
    unavailableTitle: 'Insight is on its way.',
    unavailableBody:
      "This page is rolling out gradually. It shows up here as soon as it's ready for you.",
    loading: 'Fetching the numbers ...',
    emptyTitle: 'No numbers yet.',
    emptyBody:
      'When customers book meetings, the overview of time, load and services collects here.',
    kpiAriaLabel: 'Key figures',
    kpiBookings: 'meetings',
    kpiRevenue: 'booked value',
    kpiNoShow: 'no-show',
    kpiNoShowSub: '{noShow} of {total} meetings',
    noShowNudge:
      '{count} meetings did not happen in this period. Asking for a deposit when the customer books usually means fewer.',
    noShowNudgeLink: 'See add-ons',
    noShowNudgeOwnerOnly: 'The account owner can add it.',
    kpiCancelled: 'cancelled',
    kpiCancelledSub: '{count} cancellations',
    kpiRescheduled: 'rescheduled',
    kpiRescheduledSub: '{count} rescheduled',
    kpiHours: 'hours booked',
    bookingsOverTime: 'meetings over time',
    bookingsOverTimeAria: 'Meetings over time',
    loadPerConsultant: 'load per consultant',
    thConsultant: 'Consultant',
    thBookings: 'Meetings',
    thHours: 'Hours',
    serviceMix: 'service mix',
    mixCount: '{count} meeting | {count} meetings',
    foot: 'calculated from your meetings, updated on every visit',
  },
  setupDashboardIntegrationsView: {
    tabKeys: 'API keys',
    tabWebhooks: 'Webhooks',
    tabStart: 'Get started',
    tabsAriaLabel: 'Integration tabs',
    eventCreatedLabel: 'New booking',
    eventCreatedTag: 'new booking',
    eventRescheduledLabel: 'Booking moved',
    eventRescheduledTag: 'moved',
    eventCancelledLabel: 'Booking cancelled',
    eventCancelledTag: 'cancelled',
    scopeRead: 'read',
    scopeWrite: 'write',
    errorKeyName: 'Give the key a name so you can recognize it later.',
    errorKeyScope: 'Choose at least one access level.',
    errorGeneric: 'Something went wrong. Try again.',
    errorHookUrl: 'Enter the URL webhooks should be sent to.',
    errorHookUrlScheme: 'The URL must start with https:// (or http:// in development).',
    errorHookEvents: 'Choose at least one event type to subscribe to.',
    healthDelivering: 'delivering',
    healthFailing: 'failing',
    healthIdle: 'no deliveries',
    healthDisabled: 'disabled',
    lastDeliveryFailed: 'failed {time}',
    lastDeliveryOk: 'ok {time}',
    title: 'Integrations',
    lede: 'Connect Stemne to your CRM, accounting or own systems. Read and write bookings via the API, and get real-time alerts with webhooks when something changes.',
    notEnabledTitle: 'Integrations are not enabled',
    notEnabledBody:
      'The public integration API is available on the Growth plan. Get in touch and we will enable access for your organization.',
    contactSupport: 'Contact support',
    apiAccessTitle: 'API access',
    apiAccessEyebrow: 'public api · {version} · {oas}',
    authPrefix: 'Authenticate with a bearer key',
    authSuffix: 'The key identifies the organization and points straight at the API host.',
    keysTitle: 'API keys',
    keyCount: '{n} key | {n} keys',
    activeCount: '{n} active',
    createKeyCta: 'Create new key',
    keysLede:
      'Each key grants access on behalf of the whole organization. Treat it like a password.',
    loading: 'Loading...',
    keysErrorTitle: 'Could not fetch the keys',
    fetchErrorBody: 'Something went wrong. Reload the page, or try again in a moment.',
    retry: 'Try again',
    keysEmptyTitle: 'You have no keys yet',
    keysEmptyBody:
      'Create a key to get started. The key is shown only once when it is created, so remember to save it somewhere safe (for example 1Password).',
    statusActive: 'active',
    statusNeverUsed: 'never used',
    createdMeta: 'created {date}',
    lastUsedMeta: 'last used {time}',
    revokeKeyCta: 'Revoke',
    webhooksTitle: 'Webhooks',
    endpointCount: '{n} endpoint | {n} endpoints',
    createHookCta: 'Create new webhook',
    webhooksLede: 'Server-to-server alerts, signed with HMAC-SHA256. Standard Webhooks protocol.',
    webhooksErrorTitle: 'Could not fetch webhooks',
    webhooksEmptyTitle: 'You have no webhooks yet',
    webhooksEmptyBody:
      'Create an endpoint to receive events in real time in your own system. The signing secret is shown only once when the endpoint is created.',
    lastDeliveryMeta: 'last delivery · {status}',
    deleteHookCta: 'Delete',
    subscribeTitle: 'You can subscribe to',
    subscribeEyebrow: 'events',
    firstRequestTitle: 'Your first request',
    firstRequestEyebrow: 'get started',
    copyLower: 'copy',
    copiedLower: 'Copied',
    quickstartComment: 'Fetch upcoming bookings',
    docsTitle: 'Documentation',
    docsLede:
      'Endpoints, field descriptions, error codes and the whole contract for building against Stemne.',
    docsApiLink: 'API documentation',
    footNote: 'The public integration API follows the Growth tier.',
    footLink: 'Need help? Contact support',
    createKeyModalTitle: 'Create new API key',
    createKeyModalKicker: 'integrations',
    keyNameLabel: 'Name of the key',
    keyNamePlaceholder: 'My CRM integration',
    keyNameHint: 'Use a name that explains what the key is used for.',
    scopeLegend: 'Access level',
    scopeReadLabel: 'Read',
    scopeReadHint: 'Fetch bookings and other data via the API.',
    scopeWriteLabel: 'Write',
    scopeWriteHint: 'Create and change data via the API. Enabled when write endpoints are ready.',
    createKeySubmit: 'Create key',
    revealKeyModalTitle: 'Save the key somewhere safe',
    revealModalKicker: 'shown only this one time',
    revealKeyLede:
      'Treat the key like a password. Stemne does not store it, and it will not be shown again. If you lose it, you have to create a new one.',
    revealKeyAria: 'New API key',
    revealSavedAck: 'I have saved the key',
    revealDone: 'Done',
    revokeConfirmTitle: 'Revoke the key?',
    revokeConfirmBody:
      'After you revoke "{name}" it can no longer be used. Existing integrations that use the key will stop working.',
    createHookModalTitle: 'Create new webhook',
    hookUrlLabel: 'Receiver URL',
    hookUrlPlaceholder: 'https://your-app.com/webhooks/stemne',
    hookUrlHint: 'The URL Stemne will send POST requests to. Must start with https://.',
    hookEventsLegend: 'Events to subscribe to',
    createHookSubmit: 'Create webhook',
    revealSecretModalTitle: 'Save the signing secret somewhere safe',
    revealSecretLede:
      'Treat this key like a password. Use it to verify the HMAC signature on every request. Stemne does not store it in plain text, and it will not be shown again. If you lose it, you have to create a new endpoint.',
    revealSecretAria: 'New signing secret',
    deleteConfirmTitle: 'Delete webhook?',
    deleteConfirmBody:
      'After you delete "{url}" Stemne will stop sending events there. You have to create a new endpoint to start again.',
  },
  dashboardAgendaPointsBuilder: {
    label: 'What do we need answers to?',
    removeAria: 'Remove point',
    inputPlaceholder: 'Write a question',
    addAria: 'Add point',
  },
  dashboardCandidateTimesEditor: {
    addLabel: 'add time',
    prevDayAria: 'Previous day',
    nextDayAria: 'Next day',
    loading: 'fetching available times…',
    empty: 'No more available times this day.',
  },
  treatmentDemoRoomPanel: {
    kicker: 'video room · demo',
    title: 'The video room appears here',
    body: 'In a real meeting the video call opens in this area. The notes and summary alongside work just as usual.',
  },
  dashboardCustomerMessageCard: {
    label: 'Message from the customer',
  },
  setupDashboardMeetingLiveView: {
    novideoKicker: 'video meeting',
    novideoTitle: 'The video room is not available',
    novideoBody:
      'We could not create a video room for this meeting. You can still take notes during the meeting and finish as usual. Reload if the room shows up.',
    novideoReload: 'Reload',
    customerFallback: 'the customer',
    teamMeeting: 'Team meeting',
    teamMeetingSub: 'Team meeting · {mins} min',
    meetingFallback: 'Meeting',
    headerSub: '{label} · {mins} min',
    loadError: 'Could not load the meeting',
    finishBodyJournal:
      "You'll be taken to the summary and can save the journal. You can go back if you change your mind.",
    finishBody: "You'll be taken to the summary. You can go back if you change your mind.",
    finishBodyVideo:
      "The video stream stops and you'll be taken to the summary. You can go back if you change your mind.",
    finishConfirmLabel: 'Yes, end it',
    finishTitle: 'End the meeting?',
    backAria: 'Back to preparations',
    back: 'Preparations',
    inProgress: 'in progress · {copy}',
    no: 'No',
    qaKicker: 'ask about this, from the preparation',
    qaTitle: 'Questions and answers',
    encBadge: 'stored encrypted',
    logTitle: 'Log quickly',
    painBefore: 'pain before',
    painAfter: 'pain after',
    saved: 'Saved ✓',
    finishAndSave: 'End and save →',
    finish: 'End →',
    physicalMeeting: 'physical meeting',
    atOffice: 'At the office',
    seriesOf: ' of {total}',
    durationMins: '{mins} min',
  },
  setupDashboardMeetingsView: {
    stripLabelAll: 'all',
    stripAriaLabel: 'Filter meetings by owner',
    stripLabelSelf: 'you',
    stripLabelNone: 'unassigned',
    ownerSelf: 'you',
    privateMeeting: 'Private meeting',
    meetingFallback: 'Meeting',
    statusSoon: 'soon',
    statusPending: 'unconfirmed',
    statusCancelled: 'cancelled',
    statusCompleted: 'completed',
    statusPast: 'past',
    statusUpcoming: 'upcoming',
    statusRescheduled: 'rescheduled',
    notesMissing: 'notes missing',
    footerScopeAll: 'the whole company',
    footerScopeNone: 'unassigned',
    footerScopeMine: 'yours',
    loadError: 'Could not load meetings.',
    createSuccess: 'Meeting created. The customer has received the invitation.',
    createConflict: 'That time is already booked. Choose another time.',
    createError: 'Could not create the meeting.',
    assignModalTitle: 'Assign',
    assignModalKicker: 'unassigned meeting',
    unreadAria: '{n} unread',
    sigilAll: 'all',
    dayHeaderCount: '{n} meeting | {n} meetings',
    today: 'TODAY',
    tomorrow: 'TOMORROW',
    dayHeaderToday: 'TODAY · {date}',
    dayHeaderTomorrow: 'TOMORROW · {date}',
  },
  setupDashboardNoteDetailView: {
    errorNotAvailable: 'The note is not available.',
    errorLoadFailed: 'Could not load the note.',
    grantFailedWithReason: 'Could not grant access: {reason}',
    grantFailed: 'Could not grant access. Try again.',
    anOperator: 'An operator',
    anOperatorLower: 'an operator',
    theOwner: 'the owner',
    aColleague: 'A colleague',
    backToCustomers: 'Back to customers',
    composerCaptionAuthor: 'your comment is visible to everyone with access',
    composerCaptionRecipient: 'comment visible to {name} and you',
    headerKicker: 'note',
    headerKickerAuthor: 'note · yours',
    bodyKickerPreview: 'the note · preview',
    bodyKicker: 'the note',
    deleteConfirm: 'Delete the note? This action cannot be undone.',
    deleteFailed: 'Could not delete the note.',
    deleteSuccess: 'The note has been deleted.',
    archiveFailed: 'Could not archive the note.',
    archiveSuccess: 'The note has been archived.',
    linkHintNotLinked: 'not linked',
    menuEditTitle: 'Edit title',
    menuEditLink: 'Edit link',
    menuShare: 'Share with others',
    menuNotify: 'Notify me on replies',
    toggleOn: 'on',
    toggleOff: 'off',
    menuGroupExport: 'export',
    menuExportPdfBusy: 'Preparing PDF ...',
    menuExportPdf: 'Export as PDF',
    menuDuplicate: 'Duplicate note',
    menuGroupLifecycle: 'lifecycle',
    menuArchive: 'Archive',
    menuDelete: 'Delete note',
    exportPdfFailed: 'Could not create PDF',
    kebabKickerAuthor: 'your note',
    kebabKickerShared: 'shared with you',
    untitled: 'Untitled',
    loadingNote: 'Loading note ...',
    breadcrumbAriaLabel: 'Breadcrumbs',
    breadcrumbCustomers: 'Customers',
    noteFallback: 'Note',
    // #544 — see the nb bundle for the rationale.
    bridgeAriaLabel: 'This note belongs to a meeting',
    bridgeTitle: 'This note belongs to a meeting',
    bridgeTail: ', the meeting page also has the access list and delete',
    bridgeCtaLong: 'Open on the meeting page',
    bridgeCtaShort: 'Open',
    bannerSharedSuffix: 'shared this note with you',
    scopeFull: 'with meeting, you can join',
    scopeEntry: 'note only, the meeting is hidden from you',
    replyToAuthor: 'Reply to {name}',
    titleKickerAuthor: 'your note',
    writtenBy: 'written by {name}',
    updatedAt: 'updated {time}',
    syncsAutomatically: 'syncs automatically',
    moreActions: 'More actions',
    authorNoteAriaLabel: 'Message from operator',
    authorNoteLabel: 'message',
    draftAriaLabel: 'The note is empty for now',
    draftKicker: 'draft',
    draftTitle: '{name} is working on this note.',
    draftBody:
      'It is empty for now. You have access because {name} wants to share it with you when it is ready.',
    draftToggleLabel: 'Notify me when it is ready',
    draftToggleHint: 'Push when {name} updates the note.',
    editorPlaceholder: 'Write here ...',
    commentsAriaLabel: 'Comments',
    threadTitle: 'Conversation',
    replyCount: '{n} replies',
    deleteCommentFrom: 'Delete the comment from {name}',
    deleteComment: 'Delete',
    replyPlaceholder: 'Write a reply ...',
    sending: 'Sending ...',
    send: 'Send',
    customerAriaLabel: 'Customer',
    customerKicker: 'customer',
    customerFoot: 'Note across the meetings with this customer.',
    customerNotLinked: 'Not linked',
    customerNotLinkedSub:
      "This note is not linked to a customer. You can link it later from the note's menu.",
    meetingAriaLabel: 'The meeting',
    meetingKicker: 'the meeting',
    meetingFallback: 'Meeting',
    meetingWithCustomer: 'with {name}',
    metaWhen: 'when',
    metaDuration: 'duration',
    durationMinutes: '{minutes} min',
    metaOwner: 'owner',
    joinMeeting: 'Join the meeting',
    addToCalendar: 'Add to calendar',
    hiddenByOwner: 'Hidden by owner',
    hiddenByOwnerSub:
      'You have access to the note, but not the meeting itself. {name} has chosen to keep the customer and the time private.',
    metaCustomer: 'customer',
    metaSharedBy: 'shared by',
    readOnlyCallout: 'You can read, but not forward or export.',
    accessAriaLabel: 'Access',
    accessKicker: 'access',
    accessAdd: '+ add',
    accessTagOwner: 'owner',
    accessYou: 'You',
    accessTagCanComment: 'can comment',
    accessTagReadOnly: 'read only',
    mobileReplyAriaLabel: 'Write a reply to {name}',
    mobileReplyText: 'Write a reply to {name}...',
    renameLabel: 'Title',
    renameHint: 'Up to 200 characters.',
    linkSearchLabel: 'Search meetings',
    linkSearchPlaceholder: 'Title, customer, service',
    linkUnlink: 'Unlink',
    linkUnlinkSub: 'The note becomes standalone',
    linkLoading: 'Loading meetings...',
    linkNoResults: 'No meetings match the search.',
    unknownCustomer: 'Unknown customer',
    duplicateCta: 'Duplicate',
    duplicateBody:
      'Creates a copy of the note with the title "Copy of {title}". The content is copied as is. Comments, sharing and history are not included.',
    duplicateUntitled: 'untitled',
    duplicateKeepLinkLabel: 'Keep the link to the meeting',
    duplicateKeepLinkDescription: 'Turn off to save the copy as a standalone note.',
  },
  setupDashboardOverviewView: {
    statMeetingsToday: 'meetings today',
    statThisWeek: 'this week',
    statNewClients: 'new customers',
    copyError: 'Could not copy the link. Copy it manually, or try again.',
    subjectPrivate: 'private',
    subjectFallback: 'meeting',
    loadError: 'Could not load data.',
    modeOffice: 'office',
    modeTreatment: 'treatment',
    modeTrening: 'training',
    modeVideo: 'video',
    nameFallback: 'you',
    needsAttentionLabel: 'needs a hand · {count}',
    seeAll: 'see all →',
    todaysMeetingsLabel: "today's meetings · {count}",
    week: 'the week →',
    prepareCta: 'Prepare →',
    confirmAttendanceCta: 'Confirm attendance →',
    noMeetingsToday: 'No meetings today.',
    seeWeek: 'See the week →',
    completedTodayLabel: 'completed today · {count}',
    writeNoteCta: 'Write note →',
    activityLabel: 'activity, last 7 days',
    fullHistory: 'full history →',
    weekRangeLabel: 'the week · {range}',
    meetingCount: '1 meeting | {n} meetings',
    carouselLabel: 'The week, meetings per day',
    moreCount: '+{count} more',
    calendar: 'calendar →',
    newClientsLabel: 'new customers · {count} this week',
    shareYourPage: 'share your page',
    copiedLink: 'Copied ✓',
    copyLink: 'Copy link',
    preview: 'Preview →',
    comeFurther: 'go even further',
    makeItYours: 'Make your page yours.',
    openYourPage: 'Open your page for bookings.',
    nextPill: 'NEXT',
    unresolvedPill: 'UNRESOLVED',
  },
  setupDashboardRapporterView: {
    summaryPaid: 'Paid through Stemne for meetings in the selected month: {amount}.',
    summaryOutstanding: 'Outstanding amount for meetings in the selected month: {amount}.',
    receiptsTitle: 'Receipts, invoices and credit notes',
    receiptsReconciliationLabel: 'Payment reconciliation',
    receiptsGrossSales: 'Received for documents issued in the selected period',
    receiptsCredited: 'Credited',
    receiptsStripeFee: 'Stripe fee',
    receiptsNetAfterStripe: 'Net after card processing fees and credit notes',
    receiptsReconciliationNote:
      'The reconciliation covers receipts and credit notes issued in the selected month, invoices issued in the month, and recorded card fees for the receipts and invoices. Disputes, dispute fees, and other Stripe balance movements are excluded, regardless of when they occur.',
    receiptsUnavailable: 'Not available',
    receiptsOpenStripe: 'Open Stripe',
    receiptDownload: 'Download PDF',
    receiptsDownloadFailed: 'Could not download the document',
    receiptCreditNote: 'credit note for {number}',
    receiptInvoice: 'invoice',
    receiptUnpaid: 'unpaid',
    kicker: 'invoice basis',
    title: 'Invoice basis',
    sub: 'Completed sessions per customer for the selected month. Basis for invoicing.',
    periodLabel: 'Period',
    exportCta: 'Download CSV',
    unavailableTitle: 'The invoice basis is on the way.',
    unavailableBody:
      'This page is rolling out gradually. It will appear here as soon as it is ready for you.',
    tableLabel: 'Invoice basis',
    emptyTitle: 'No completed sessions in {month}.',
    emptyBody:
      'Once sessions are completed in the period, they collect here per customer and service, ready to invoice.',
    colCustomer: 'Customer',
    colService: 'Service',
    colCategory: 'Category',
    colCount: 'Count',
    colDuration: 'Duration',
    noShowMarker: 'No-show',
    kindSamtale: 'Conversation',
    kindBehandling: 'Treatment',
    kindTrening: 'Training',
    kindOther: 'Other',
    summaryHeld: 'Completed: {count} session, {duration} | Completed: {count} sessions, {duration}',
    summaryNoShow: 'Additionally: {count} not attended, {duration}',
  },
  setupDashboardServicesView: {
    reorderError: 'Could not save the order. Try again.',
    segmentServices: 'Services',
    segmentMatrix: 'Who offers what',
    sortingDone: 'Done',
    sortListLabel: 'Sort the services',
    durationMins: '{n} min',
  },
  setupDashboardSettingsView: {
    kicker: 'settings',
    title: 'Settings',
    tabTeam: 'Team',
    tabBranding: 'Profile & booking',
    tabCompany: 'Company',
    tabSecurity: 'Security',
    tabBilling: 'Subscription',
    tabPayments: 'Payments',
    tabTreatment: 'Treatment',
    tabAudit: 'Activity log',
    paymentsLockedTitle: 'Payment at booking is not turned on',
    paymentsLockedBody:
      'Connecting Stripe requires the "Payment at booking" add-on. Buy it under Subscription → Add-ons, and this tab comes into use.',
    paymentsLockedBodyOwnerOnly:
      'Payment at booking is not turned on. The account owner can add it.',
    paymentsLockedAction: 'See add-ons',
    paymentsOwnerOnlyTitle: 'Only the owner can manage payments',
    paymentsOwnerOnlyBody: 'Ask the account owner to open the payment settings.',
    paymentsGateErrorTitle: "We couldn't load your payment settings.",
  },
  setupDashboardView: {
    unreadBadge: '{n} unread',
    staffConsole: 'Stemne staff',
    navCollapse: 'Narrow menu',
    navExpand: 'Show menu',
    loggedInAs: 'logged in as {email}',
    logout: 'log out',
  },
  setupSetupAvailabilityView: {
    saveError: 'Could not save availability. Try again.',
  },
  setupSetupCompanyView: {
    skipError: 'Could not continue. Try again.',
    saveError: 'Could not save. Try again.',
    logoLabel: 'Logo',
    logoHint: 'PNG, JPEG, WebP or SVG. Transparent background recommended. Max 10 MB.',
  },
  setupSetupDoneView: {
    waitingMeta: 'preparing domain',
    waitingHeading: 'One moment,',
    waitingHeadingItalic: ' we are setting up',
    waitingHeadingEnd: ' your domain',
    waitingMessage1:
      'We are fetching your certificate from Cloudflare. It usually takes under a minute, just enough time to put on the coffee.',
    waitingMessage2: 'Your domain needs to be sent around the internet. A coffee break is enough.',
    waitingMessage3: 'We are polishing the last details. You are almost on the air.',
    waitingMessage4:
      'Certificate issuance is under way. Use the minute to stretch your legs, we will be done before the coffee gets cold.',
    forwardErrorWithMessage: 'Could not forward: {message}',
    forwardError: 'Could not forward. Try again.',
    takingLonger:
      'This is taking longer than usual. We are still waiting for the certificate, and you can safely leave this page open.',
    stuckBody:
      'The certificate is not in place yet. Your account has been created, so nothing is lost. Try again, or get in touch and we will sort it out.',
    stuckRetry: 'Try again',
    stuckContact: 'Contact us',
    stuckMailSubject: 'Domain {slug} is not ready',
  },
  setupSetupServicesView: {
    offersTreatmentLabel: 'Do you offer treatment?',
    offersTreatmentHint:
      'Turn this on if you are a physiotherapist, naprapath, podiatrist or similar, then you get a dedicated meeting view with a plan checklist during the treatment itself. You can change this later in the settings.',
    offersTrainingLabel: 'Do you offer training or coaching?',
    offersTrainingHint:
      'Turn this on if you are a personal trainer, running coach or similar, then you can mark services as training and get a meeting view made for sessions, with exercises and results. You can change this later in the settings.',
    modeLegend: 'How do you meet your customers?',
    modeVideoTitle: 'By video',
    modeVideoSub: 'I hold meetings online',
    modeOfficeTitle: 'In person',
    modeOfficeSub: 'at the clinic, office or studio',
    modeBothTitle: 'Both',
    modeBothSub: 'video and in person, I switch between them',
    kindLegend: 'Type',
    kindSamtale: 'Conversation',
    kindTrening: 'Training',
    namePlaceholderTrening: 'e.g. Strength session',
    videoDurationHint: 'Video meetings can last up to {max} minutes with your subscription.',
    videoDurationError: 'Choose a duration of {max} minutes or less for video meetings.',
    saveError: 'Could not save the services. Try again.',
  },
  viewsRequestDemoView: {
    turnstilePending: 'hold on a moment while we confirm you are not a robot',
    genericError: 'Something went wrong. Try again.',
    formEyebrow: 'demo',
    formTitle: 'The demo is closed right now',
    formBody: 'Leave your name and email, and we will open the demo for you and get in touch.',
    nameLabel: 'Name',
    namePlaceholder: 'First name Last name',
    emailLabel: 'Email',
    emailPlaceholder: "you{'@'}company.no",
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Feel free to tell us a bit about what you would like to see',
    turnstileAriaLabel: 'Bot check from Cloudflare',
    submitCta: 'Request access',
    sentEyebrow: 'thanks',
    sentTitle: 'Your request has been sent',
    sentBody: 'Thanks. We will get in touch and open the demo for you.',
  },
  accountMarketingEmailsCard: {
    title: 'News and offers',
    hint: 'Applies to your email address across all workspaces.',
    toggleLabel: 'Receive news and offers by email',
    toggleDescription: 'This choice does not affect essential account or booking emails.',
    loading: 'Loading your email preference …',
    saving: 'Saving …',
    saved: 'Email preference saved.',
    loadError: 'We could not load your email preference. Please try again.',
    saveError: 'We could not save the change. Your previous choice is shown. Please try again.',
    retry: 'Try again',
  },
  accountDripEmailsCard: {
    title: 'The welcome series',
    hint: 'A short series of tips to help you get started with Stemne, sent over the first few weeks. It applies to the whole workspace, not just you. Booking-related emails carry on as usual.',
    toggleLabel: 'Get the welcome series by email',
    toggleDescription:
      "Turn it off any time. You won't miss any booking confirmations or reminders.",
  },
  accountEmailNotificationsCard: {
    title: 'Notifications',
    hint: 'Choose which emails you want from Stemne. The setting applies only to you, and is saved immediately.',
    bookingLabel: 'Booking confirmation',
    bookingDescription: 'When a customer books a meeting with you or your team.',
    cancelLabel: 'Cancellation',
    cancelDescription: 'When a customer cancels a scheduled meeting.',
    rescheduleLabel: 'Time change',
    rescheduleDescription: 'When a customer moves a meeting to another time.',
    noshowLabel: 'No-show',
    noshowDescription: 'When a customer does not show up for their meeting.',
    reminderLabel: 'Reminder',
    reminderDescription: 'Reminder about your upcoming meetings. Sent on its own schedule.',
    paymentLabel: 'Payment activity',
    paymentDescription: 'When a payment link or reminder is sent, or a payment is refunded.',
    paymentSettledLabel: 'Payment received',
    paymentSettledDescription: 'When a deposit or the remaining amount after the meeting is paid.',
    messageLabel: 'Messages',
    messageDescription: 'Chat messages from customers.',
    systemLabel: 'System notifications',
    systemDescription: 'Important operational messages (calendar sync, security, updates).',
    mentionLabel: 'Mentions in notes',
    mentionDescription: "When a colleague mentions you ({'@'}name) in a meeting note.",
    actionPointDueLabel: 'Follow-up point due',
    actionPointDueDescription: 'When a follow-up point you own reaches its due date.',
  },
  accountMyServicesTab: {
    saveError: 'Could not save. Try again.',
    title: 'My services',
    intro:
      'Choose which services you offer. You offer all of them by default, until you turn some off here.',
    loading: 'Loading ...',
    emptyBody: 'There are no services yet.',
    guard:
      'You are not offering any services right now. That means you will not be assigned new meetings.',
    hiddenPill: 'Hidden',
    hiddenDpa: 'Accept the data processing agreement before you can start using this service.',
    hiddenDpaCta: 'Accept the agreement',
    hiddenPublish: 'This service is hidden. Make it visible under Services to start using it.',
  },
  accountTilgjengelighetTab: {
    endBeforeStart: 'End time must be after start time',
    periodAdded: 'Period added',
    periodSaveError: 'Could not save period',
    availabilityConflictIntro:
      'These meetings fall outside the new opening hours. If you save, you have to move or cancel them manually.',
    saveAnyway: 'Save anyway',
    blockerConflictIntro:
      'These meetings fall within the period you are blocking. You have to move or cancel them manually after the period is saved.',
  },
  accountVarslingerTab: {
    title: 'Notifications',
    quietLedeDigest:
      'Set quiet hours so emails wait until the next window. In-app notifications still arrive right away; we only hold back outgoing emails.',
    quietLede:
      "Set quiet hours so we don't send emails during that period. In-app notifications still arrive right away.",
    quietToggleLabelDigest: 'Hold emails during quiet hours',
    quietToggleLabel: "Don't send emails during quiet hours",
    quietToggleDescriptionDigest:
      'When on, we gather all emails from the period into a single morning summary.',
    quietToggleDescription:
      "When on, we don't send emails during quiet hours. You see everything in the app as usual.",
    saved: 'Saved.',
    saveError: "Couldn't save. Try again.",
    quietSaveError: "Couldn't save quiet hours.",
    formatBoth: 'Use the format 22:00 and 07:00.',
    formatFrom: 'Use the format 22:00.',
    formatTo: 'Use the format 07:00.',
    sameTime: "From and to can't be the same.",
    teamExpiryLabel: "Notify me when my team's customers don't confirm",
    teamExpiryDescription:
      "You get one email per meeting a colleague was assigned and the customer didn't confirm in time. The customer always gets an offer to rebook, this notification is just an overview for you.",
    fromLabel: 'From',
    toLabel: 'To',
    timezoneKicker: 'Time zone',
    timezoneHint: 'Changed under Settings → Company.',
    loading: 'Loading notification settings...',
  },
  bulkimportBulkImportWizard: {
    titleUpload: 'Import multiple customers',
    titleMap: 'Map your columns',
    titleReview: 'Review before importing',
    kicker: 'customers',
    importError: 'Something went wrong during the import.',
  },
  bulkimportDoneStep: {
    headline: '1 customer added | {n} customers added',
    detailIntro: 'They are now in your customer list, marked as imported.',
    updated: '1 customer was updated. | {n} customers were updated.',
    skipped: '1 row was skipped. | {n} rows were skipped.',
    errors: '1 row had an error and was not saved. | {n} rows had an error and were not saved.',
    seeCustomers: 'See the customers',
  },
  bulkimportMapStep: {
    intro:
      'Tell us what each column in the file is. We have guessed from the headings, change it where needed. Leave columns you do not need on "Do not import".',
    headerToggle: 'First row is headings',
    fieldIgnore: 'Do not import',
    fieldFirstName: 'First name',
    fieldLastName: 'Last name',
    fieldEmail: 'Email',
    fieldPhone: 'Phone',
    fieldTags: 'Label',
    columnFallback: 'Column {n}',
    inUseSuffix: ' (in use)',
    previewEmpty: 'empty',
    noticeOk: '✓ Ready. We use email to avoid duplicates against the customers you already have.',
    noticeBadPre: 'Connect at least',
    noticeBadOr: 'or',
    noticeBadPost: 'to continue.',
    reviewCta: 'Review {n} rows',
  },
  bulkimportReviewStep: {
    intro:
      'Here is how we read the file. Fix emails that are missing or wrong, and choose what happens with customers you already have.',
    tallyRows: 'rows in the file',
    tallyReady: 'ready',
    tallyDuplicate: 'already exist',
    tallyProblems: 'need a fix',
    dedupLabel: 'Already exist ({n})',
    dedupSkip: 'Skip',
    dedupUpdate: 'Update',
    bulkTagLabel: 'Tag on all',
    tagNone: 'none',
    colName: 'NAME',
    colEmail: 'EMAIL',
    colTags: 'TAGS',
    colStatus: 'STATUS',
    includeRowAria: 'Include row {row}',
    noName: '(no name)',
    emailPlaceholder: 'enter email',
    statusReady: 'ready',
    statusDuplicate: 'already exists',
    statusInvalid: 'invalid email',
    statusMissing: 'missing email',
    footnote:
      'Imported customers get no automatic email. You can invite them later from the customer card.',
    importing: 'Importing...',
    importCta: 'Import 1 customer | Import {n} customers',
  },
  bulkimportStepperRail: {
    ariaLabel: 'Import steps',
    stepUpload: 'upload',
    stepMap: 'map columns',
    stepReview: 'review',
  },
  bulkimportUploadStep: {
    intro:
      'Upload a spreadsheet from your CRM, an export, or a list you have gathered. You map the columns in the next step. We support CSV files in this first version, Excel is coming.',
    dropTitle: 'Drop the file here, or browse',
    dropMeta: '.csv · max 5 MB · up to 2,000 rows',
    fileMeta: '{rows} rows · {size}',
    swapFile: 'Change file',
    hint: 'Not sure how? Make a spreadsheet with the columns first name, last name, email, phone, and label. We will tell you if something is missing.',
    mapColumnsCta: 'Map columns →',
  },
  dashboardActiveSessionBanner: {
    eyebrowLate: 'over time',
    eyebrowActive: 'in progress now',
    modeTreatment: 'Treatment',
    modeVideo: 'Video meeting',
    modeMeeting: 'Meeting',
    titleWithName: '{label} with {name}',
    titleActive: '{label} in progress',
    finishCta: 'Finish',
    goToSessionCta: 'Go to the meeting',
  },
  dashboardActivityEventRow: {
    colleague: 'A colleague',
    unknown: 'Unknown',
    docReceived: 'received',
    docActive: 'active',
    docExpired: 'expired',
    docRevoked: 'revoked',
    docMetaReceived: '{size} · received',
    docMetaViewOnly: '{size} · view only',
    docMetaDownload: '{size} · download',
    hos: 'with {name}',
  },
  dashboardActivityFeedSection: {
    chipAlle: 'all',
    chipMoter: 'meetings',
    chipDokumenter: 'docs',
    chipSamtaler: 'conversations',
    chipKunder: 'customers',
    filterAria: 'Filter activity',
    emptyTitle: 'Quiet these last seven days.',
    emptyBody: 'No new meetings, no activity to report. When something comes in, it shows up here.',
    olderLink: 'older events in full history →',
  },
  dashboardActionPointsSection: {
    label: 'action points',
    overdueBadge: '{count} overdue',
    scopeAria: 'Show my points or the team’s',
    scopeMine: 'Mine',
    scopeTeam: 'Team',
    allCustomers: 'All customers',
    noDue: 'no due date',
    overduePrefix: 'overdue {date}',
    emptyTitle: 'No open action points.',
    emptyTeamTitle: 'The team has no open action points.',
    emptyBody:
      'Points you note after a meeting collect here, soonest due first. You are all caught up.',
    searchPlaceholder: 'search points, customer, advisor ...',
    noResults: 'No points match your search.',
  },
  dashboardActivityPillFilter: {
    lensAlle: 'all',
    lensMoter: 'meetings',
    lensDokumenter: 'documents',
    lensSamtaler: 'conversations',
    lensKunder: 'new customers',
    listLabel: 'Filter history',
  },
  dashboardAddClientModal: {
    kicker: 'customers',
    editTitle: 'Edit customer',
    createTitle: 'Add customer',
    intro:
      'Use this for customers who booked outside the system. They do not get an automatic email.',
    firstNameLabel: 'First name',
    lastNameLabel: 'Last name',
    emailLabel: 'Email',
    emailPlaceholder: "name{'@'}company.com",
    emailWarning:
      'The customer must confirm the new address before it takes effect. We send a link to {email}. Until the customer confirms, reminders, confirmations and login still go to the old address.',
    emailWarningFallback: 'the new email',
    emailPending:
      'Waiting for the customer to confirm {email}. You can change the address again here.',
    phoneLabel: 'Phone (optional)',
    phonePlaceholder: '+47 ...',
    addressLabel: 'Address (optional)',
    addressPlaceholder: 'Search for an address',
    addressHint: 'Used when you travel to the customer',
    tagsLabel: 'Tags',
    otherTagHint: 'Automatic or legacy tag — cannot be removed here yet',
    saveEditCta: 'Save changes',
    saveCreateCta: 'Save customer',
  },
  dashboardAddPasskeyModal: {
    title: 'New passkey',
    labelPrompt: 'What should we call this one?',
    labelHint: 'This is how you recognize it in the list, and when revoking it.',
    deviceKicker: 'this device',
    deviceFallback: 'This device',
    deviceMeta: 'The private key never leaves this device.',
    reauthNote:
      'This is an extra passkey, so we need to confirm it is you. Use a passkey you already have to continue.',
    confirmHint:
      'After you click confirm, the device asks for Face ID, a fingerprint, or a security key.',
    confirmWithPasskeyCta: 'Confirm with passkey',
    confirmCta: 'Confirm',
  },
  dashboardBlockTimeModal: {
    kicker: 'calendar',
    title: 'Block time',
    intro: 'No one can book during this period. Existing meetings are not affected.',
    reasonLabel: 'Reason (optional, not shown publicly)',
    reasonPlaceholder: 'e.g. Supervision · Day off',
    allDay: 'All day',
    dateLabel: 'Date',
    fromLabel: 'From',
    toLabel: 'To',
    fromDateLabel: 'From date',
    toDateLabel: 'To date',
    repeatLabel: 'Repeat',
    repeatNever: 'Never',
    repeatWeekly: 'Weekly, same time',
    repeatMonthly: 'Monthly',
    blockCta: 'Block',
  },
  dashboardBookingAccessPanel: {
    you: 'you',
    unknownColleague: 'unknown colleague',
    roleCoHostReadWrite: 'Co-host (read + write)',
    roleViewer: 'Read access',
    grantedToast: '{name} was given access.',
    revokedToast: '{name} no longer has access.',
    revokedNoteToast: '{name} no longer has access to the note.',
    entryUntitled: 'Untitled',
    sectionAriaLabel: 'Access to the meeting',
    title: 'Access',
    privateMeeting: 'private meeting',
    ownerLabel: 'Owner',
    participantCoHostLabel: 'Co-host',
    participantMeta: 'participant',
    sharedNoteLabel: 'Shared note',
    sharedNoteTipAriaLabel: 'What does shared note mean',
    sharedNoteTip:
      "Colleagues with access to a single note see only that note, not the customer's name, other notes or the whole meeting.",
    revokeTip: 'Revoke access',
    revokeAriaLabel: 'Remove access for {name}',
    coHostLabel: 'Co-host',
    coHostMeta: 'co-host',
    viewerLabel: 'Read access',
    viewerMeta: 'reader',
    grantColleagueCta: 'Give a colleague access',
    colleagueLabel: 'Colleague',
    colleaguePlaceholder: 'Choose a colleague',
    roleLabel: 'Role',
    grantAccessCta: 'Give access',
    loadingAccess: 'Loading access ...',
    canCommentLabel: 'Can comment',
    canCommentHint: "You'll be notified about replies.",
    noCommentsMeta: 'cannot comment',
    sharedToday: 'shared today',
    sharedYesterday: 'shared yesterday',
    sharedDaysAgo: 'shared 1 day ago | shared {n} days ago',
    sharedWeeksAgo: 'shared 1 week ago | shared {n} weeks ago',
    sharedMonthsAgo: 'shared 1 month ago | shared {n} months ago',
    expiresOn: 'expires {date}',
  },
  dashboardBookingAuditTimeline: {
    removedUser: 'Removed user',
    you: 'You',
    unknownUser: 'Unknown user',
    openedMeeting: 'opened the meeting',
    title: 'Access log',
    lede: 'Every time an administrator opens a private meeting, it gets logged here.',
    loading: 'Loading the log...',
    empty: 'No openings logged for this meeting.',
  },
  dashboardBulkOwnerAssign: {
    noTeamToDistribute: 'No one on the team to distribute to.',
    assignedCount: '1 meeting assigned | {n} meetings assigned',
    assignFailed: 'Could not assign. Try again.',
    assignedPartial: '{ok} assigned, {failed} failed, reload and try the rest again',
    withoutOwner: '{count} unassigned',
    headingOne: 'Assign before it starts.',
    headingMany: 'Assign before they start.',
    bodySoloOne: 'You can take it yourself.',
    bodySoloMany: 'You can take all {count} yourself.',
    bodyTeamOne: 'You can take it yourself, or pick a colleague in the row.',
    bodyTeamMany: 'Take them all yourself in one click, or distribute evenly across the team.',
    takeSelfOne: 'Take it yourself',
    takeSelfMany: 'Take all {count} yourself',
    distributeEvenly: 'Distribute evenly across the team',
    perPersonHint: '~{n} each',
    previewCaption: 'Preview of even distribution',
    confirmTakeTitleOne: 'Take this meeting yourself?',
    confirmTakeTitleMany: 'Take all {count} yourself?',
    confirmTakeBodyOne:
      'Assigns the meeting to you. You can move it afterwards if the time does not suit you.',
    confirmTakeBodyMany:
      'Assigns {count} meetings to you. You can move individual meetings afterwards if a time does not suit you.',
    confirmTakeCtaOne: 'Yes, give it to me',
    confirmTakeCtaMany: 'Yes, give me all {count}',
    confirmDistributeTitle: 'Distribute {count} evenly?',
    confirmDistributeBody:
      'Distributes {count} meetings evenly across the team. You can move individual meetings afterwards.',
    confirmDistributeCta: 'Yes, distribute',
  },
  dashboardCalendarConnectionsPanel: {
    syncedToday: 'today {time}',
    syncedYesterday: 'yesterday {time}',
  },
  dashboardCalendarEventPopover: {
    sourceCalendar: '{provider} · {calendar}',
    stemneFallback: 'Stemne',
    ctaGoToSession: 'Go to session',
    ctaStartTreatment: 'Start treatment',
    ctaGoToMeeting: 'Go to meeting',
    seeDetails: 'See details',
    attendeeAria: '{title} and {count} more attendees: {names}',
    attendeesCount: '{n} attendee | {n} attendees',
    statusPending: 'Awaiting confirmation',
    statusRescheduled: 'Rescheduled',
    statusCompleted: 'Completed',
    statusUnresolved: 'Unresolved',
    statusNoShow: 'No show',
    statusCancelled: 'Cancelled',
    statusExpired: 'Expired',
    statusConfirmed: 'Confirmed',
  },
  dashboardDashboardCustomersMobile: {
    heroKicker: 'customers · {count} total',
    heroTitle: 'Your customers',
    heroSub:
      'Everyone who has booked a meeting. Only you and advisors you have given access see this list.',
    filterAll: 'all',
    filterNew: 'new',
    filterNewSub: 'this week',
    filterActive: 'active',
    filterPaused: 'paused',
    tagAriaLabel: 'Lifecycle filter',
    searchPlaceholder: 'Search name or email...',
    searchAriaLabel: 'Search customers',
    sectionNew: 'new · this week',
    sectionActive: 'active',
    sectionPaused: 'paused',
    sectionPast: 'past',
    customerCount: '{n} customer | {n} customers',
    meetingCount: '{n} booking | {n} bookings',
    nextRel: 'next {rel}',
    requestedRel: 'requested {rel}',
    lastRel: 'last {rel}',
    relToday: 'today · {time}',
    relTomorrow: 'tomorrow · {time}',
    relYesterday: 'yesterday',
    relLastWeek: 'last week',
    stagePaused: 'paused',
    stageNew: 'new',
    stageCustomer: 'customer',
    stageRequest: 'request',
    emptyTitle: 'No customers yet.',
    emptyBody:
      'Your customers show up here after the first meeting, or when you add someone manually.',
    filteredHeadNew: 'new customers',
    filteredHeadActive: 'active customers',
    filteredHeadPaused: 'paused',
    filteredHeadAll: 'all customers',
    noMatchTitle: 'No customers match "{query}".',
    noneInViewTitle: 'No customers in this view.',
    noMatchBody: 'Try a different search word or remove the filter.',
    noneInViewBody: 'Switch filter to see other customers, or remove it entirely.',
    footerHint: 'Sorted · last seen. Tap a row for history and notes.',
    addCta: 'Add customer',
  },
  dashboardDashboardOverviewMobile: {
    loadError: 'Could not load the overview.',
    youFallback: 'you',
    weekdayDate: '{weekday} · {day} {month}',
    goodMorning: 'Good morning',
    goodLateMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    goodNight: 'Working late',
    heroTitle: '{greeting}, {name}.',
    quietDay: 'Quiet day, no meetings scheduled.',
    todayMeetings: '1 meeting today. | {n} meetings today.',
    notesWaiting: '1 note is waiting for you. | {n} notes are waiting for you.',
    scopeTodayLabel: 'today',
    scopeMeetingsSub: 'meetings',
    scopeWeekLabel: 'this week',
    scopeNewLabel: 'new customers',
    scopeThisWeekSub: 'this week',
    minutes: '{m} min',
    inProgress: 'in progress',
    inMinutes: 'in {min} min',
    inHours: 'in {h} h',
    today: 'today',
    tomorrow: 'tomorrow',
    filterAriaLabel: 'Overview filter',
    todaysMeetingsKicker: "today's meetings",
    meetingFallback: 'Meeting',
    withPerson: 'with {name}',
    joinMeeting: 'Join the meeting',
    prepare: 'Prepare',
    open: 'Open',
    unownedTitle: 'Unassigned meetings',
    unownedSub: 'Assign before they start',
    laterTodayKicker: 'later today · tomorrow',
    meetingCount: '1 meeting | {n} meetings',
    emptyTitle: 'Quiet for the rest of the day.',
    emptyBody:
      "No more meetings, no unread notes. Enjoy the break, we'll let you know when something comes up.",
    seeWholeWeek: 'See the whole week in Meetings',
    weekMeetings: '1 meeting this week. | {n} meetings this week.',
    weekQuiet: 'No meetings scheduled this week.',
    weekEmpty: 'No meetings this week.',
    newCustomers: '1 new customer this week. | {n} new customers this week.',
    newQuiet: 'No new customers this week yet.',
    newEmpty: 'No new customers this week.',
    newCustomerFirstMeeting: 'First meeting {when}',
  },
  dashboardDateOverrideModal: {
    errorNoDate: 'Pick a date.',
    errorNoWindow: 'Add at least one time window.',
    errorStartBeforeEnd: 'Start must be before end in every window.',
    titleEdit: 'Edit override',
    titleCreate: 'Override a day',
    dateLabel: 'Date',
    dateHint: 'To change the date, delete this one and create a new one.',
    kindLegend: 'How',
    kindClosed: 'Closed, no meetings this day',
    kindReplace: 'Other hours, use these times instead',
    removeWindowAria: 'Remove window',
    addWindow: '+ add window',
    noteLabel: 'Note (optional)',
    notePlaceholder: 'e.g. conference, vacation',
  },
  dashboardDemoCtaBanner: {
    kicker: 'demo',
    body: 'Everything here belongs to a made-up practice. If you want to see the same thing with your own services and hours, setting up your own page takes a few minutes.',
    bodyMobile: 'Example data',
    setupCta: 'Set up your own page',
    setupCtaMobile: 'Set up your page',
    dismissCta: 'Not now',
  },
  dashboardDemoWelcomeModal: {
    title: 'Welcome to the demo',
    kicker: 'demo',
    intro:
      'This is a live demo. While you look around, activity pops up as if real customers are on the move.',
    bulletBooking: 'New customers book and confirm meetings',
    bulletDocuments: 'Customers upload documents before the meeting',
    bulletMessages: 'Messages and questions come in along the way',
    muted:
      'Everything you do here resets each night, so the next tour starts fresh. The calendar is not connected in the demo.',
    exploreCta: 'Explore the demo',
  },
  dashboardEmptyAvailabilityBanner: {
    title: 'You have not set up your own working hours',
    hint: "We use the organization's default for now. Set your own hours →",
  },
  dashboardCalendarConnectionBanner: {
    eyebrow: 'calendar',
    title: 'Connect Google Calendar',
    body: 'Avoid double bookings and add Stemne meetings to your calendar.',
    cta: 'Connect calendar →',
    dismiss: 'Not now',
  },
  dashboardImminentMeetingBanner: {
    eyebrow: 'starting soon',
    title: 'Meeting',
    titleWithName: 'Meeting with {name}',
    joinCta: 'Join the meeting',
    openCta: 'Open the meeting',
  },
  dashboardInstantMeetingModal: {
    kicker: 'meet now',
    titleReady: 'Meeting room ready',
    titleStart: 'Start a meeting now',
    formIntro:
      'For conversations you want to start right away. We create the meeting room and send an invitation to the customer, and you get the link here right away.',
    customerComboLabel: 'Pick a customer or add a new one',
    customerComboPlaceholder: 'Search or enter a new customer',
    customerComboCreateLabel: 'Add new customer',
    newCustomerSectionLabel: 'new customer',
    nameLabel: 'Customer name',
    namePlaceholder: 'Enter name',
    emailLabel: 'Email',
    emailPlaceholder: "name{'@'}company.com",
    messageLabel: 'Message to the customer (optional)',
    messageHint: 'A few words about what the meeting is about. Shown in the email.',
    loadCustomersError: 'Could not load customers. Try again shortly.',
    successIntro:
      'The meeting room is ready, and {email} has received the invitation by email. While the coffee is brewing, you can share the link below if you want the customer in sooner.',
    linkLabel: 'Link for the customer',
    startCta: 'Start meeting',
    joinAsHostCta: 'Join as host',
    startError: 'Could not start the meeting. Try again in a moment.',
    copySuccess: 'Link copied. Send it to the customer.',
    copyError: 'Could not copy, select the URL and copy it manually.',
  },
  dashboardLobbyBanner: {
    eyebrow: 'waiting in the lobby',
    unnamedCustomer: 'customer without a name',
    joinCta: 'Join the meeting',
    waiting: 'waiting · {time}',
    overdue: 'overdue · {time}',
  },
  dashboardMeetingAttachments: {
    label: 'Documents to review',
    uploading: 'Uploading...',
    removeAria: 'Remove {name}',
    dropPrimary: 'Attach documents',
    dropMeta: 'click or drag and drop · PDF, Word, spreadsheet, image · max 25 MB',
    errUnsupportedType: 'This file type is not supported',
    errUploadFailed: 'Upload failed',
  },
  dashboardMeetingDocumentsPanel: {
    hint: 'Documents attached to this meeting.',
    readOnlyHint: 'Documents preserved from this cancelled meeting.',
    encryptedBadge: 'Encrypted',
    addCta: 'Add document',
    loading: 'Loading...',
    emptyBody: 'No documents added yet.',
    removedOnSave: '· removed on save',
    undo: 'Undo',
    open: 'Open',
    history: 'History',
    revoke: 'Revoke',
    noActivity: 'No activity recorded yet.',
    addedOnSave: '· added on save',
    modalKicker: 'meeting',
    pickFile: 'Choose file',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'A short note to the participants',
    note: 'The document is added when you save the meeting. Participants are notified in the combined update.',
    submitCta: 'Add',
    openError: 'Could not open the document. Try again.',
    historyError: 'Could not load the history',
    actorAdvisor: 'advisor',
    actorParticipant: 'participant',
    actorSystem: 'system',
    activityShared: 'Shared by {who}',
    activityNotified: 'Email sent',
    activityOpened: 'Opened by {who}',
    activityDownloaded: 'Downloaded by {who}',
    activityRevoked: 'Revoked by {who}',
    activityExtended: 'Expiry changed by {who}',
    activityExpired: 'Expired automatically',
    activityReceived: 'Received from {who}',
    activityPolicyChanged: 'Access level changed',
    pickFileError: 'Choose a file',
    uploadError: 'Could not upload the document',
  },
  dashboardMeetingNotesHistoryModal: {
    loadError: 'Could not load history',
    timestamp: '{date} at {time}',
    title: 'History for "{title}"',
    untitled: 'Note',
    loading: 'Fetching history...',
    empty: 'No earlier versions.',
    version: 'Version {number}',
  },
  dashboardMeetingNotesPanel: {
    sectionLabel: 'Notes',
    loading: 'Loading notes...',
    empty: 'No notes on this meeting yet.',
    conflict: 'A newer version was saved by another session. Reload to see the latest version.',
    editorPlaceholder:
      'Write what was discussed, what you are preparing, or what you are following up on.',
    saveVersion: 'Save version',
    edit: 'Edit',
    history: 'History',
    delete: 'Delete',
    deleteTitle: 'Delete "{title}"?',
    deleteBody:
      'The note is hidden from the timeline, but history and revisions are kept for auditing.',
    deleteConfirm: 'Delete note',
    autosaveSaving: 'Saving...',
    autosaveSavedAt: 'Saved at {time}',
    autosaveSaved: 'Saved',
    autosaveError: 'Could not save',
    autosaveDirty: 'Unsaved changes',
    duringMeeting: 'during the meeting',
    sameDayBefore: 'same day, before the meeting',
    daysBefore: '1 day before the meeting | {n} days before the meeting',
    sameDayAfter: 'same day, after the meeting',
    daysAfter: '1 day after the meeting | {n} days after the meeting',
    timestamp: '{date} at {time}',
    defaultTitle: 'Note',
  },
  dashboardMiniMonthPicker: {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
  },
  dashboardMobileDetailHeader: {
    moreActions: 'More actions',
  },
  dashboardMobileNav: {
    brandAria: 'stemne, dashboard',
    unreadAria: '{count} unread',
    more: 'More',
    moreListAria: 'More pages',
    logout: 'Log out',
  },
  dashboardModeIcon: {
    video: 'Video meeting',
    office: 'At the office',
    both: 'Video or the office',
    treatment: 'The clinic',
  },
  dashboardNewMeetingModal: {
    stepKicker: 'step {current} of {total}',
    stepType: 'Type',
    stepParticipants: 'Participants',
    stepPickCustomer: 'Pick customer',
    stepPickTime: 'Pick time',
    stepConfirmAndSend: 'Confirm and send',
    tabType: 'Type',
    tabParticipants: 'Participants',
    tabTime: 'Time',
    tabConfirm: 'Confirm',
    goToStepAria: 'Go to step {number}: {label}',
    loadCustomersError: 'Could not load customers. Try again shortly.',
    loadServicesError: 'Could not load services. Try again shortly.',
    loadSlotsError: 'Could not fetch available times.',
    newCustomerPlaceholder: 'new customer',
    nameAndEmailError: 'Enter a name and a valid email.',
    guestNotRegisteredOption: '{email} · not registered',
    hostFallback: 'Host',
    teamMemberFallback: 'Team member',
    vertLabel: '{n} host | {n} hosts',
    hostPillLabel: 'Host · {name}',
    meetingTypeTeam: 'Team meeting',
    meetingTypeGroup: 'Group session',
    meetingTypeCustomer: 'Customer meeting',
    whoLeadOneCustomer: '1 customer',
    whoLeadManyCustomers: '{count} customers',
    whoSameRoom: ' Same room for everyone.',
    whoWhatInPerson: 'a confirmation with the location and a calendar file',
    whoWhatVideo: 'a confirmation with the video link and a calendar file',
    whoWhatSeries:
      'one invitation with all {count} times, and confirms the whole series in one click',
    whoWhatSeriesUntil:
      'one invitation with all the times, and confirms the whole series in one click',
    whoRest: ' gets {what}.{tail}',
    whoColleagueSingle: 'The colleague gets an internal calendar invite.',
    whoColleaguePlural: 'The colleagues get an internal calendar invite.',
    sendInvitation: 'Send invitation',
    sendInvitations: 'Send invitations',
    busyConflictError: 'Someone is already booked: {names}',
    createMeetingError: 'Could not create the meeting. Try again shortly.',
    summaryWhen: '{date} · {time}',
    priceNotSet: 'price not set',
    priceFree: 'free',
    summaryService: '{name} · {duration} min · {price}',
    recurrenceNone: 'Just this one',
    recurrenceDaily: 'Daily',
    recurrenceWeekly: 'Weekly',
    recurrenceBiweekly: 'Every other week',
    recurrenceMonthly: 'Monthly',
    recurrenceTimes: '{n} times',
    recurrenceDailyLower: 'daily',
    recurrenceWeeklyLower: 'weekly',
    recurrenceBiweeklyLower: 'every other week',
    recurrenceMonthlyLower: 'monthly',
    recurrenceEndLabel: 'Ends',
    recurrenceEndCount: 'After a number of times',
    recurrenceEndUntil: 'On a date',
    recurrenceUntilLabel: 'Through',
    seriesSummary: '{count} meetings, {freq} from {date}',
    seriesSummaryUntil: '{freq} from {date}, through {until}',
    seriesSummaryNoDate: 'Pick an end date to see what will be booked',
    typeHintTeam: 'Colleagues only. No customer or service.',
    typeHintGroup: 'One service, several customers together.',
    typeHintCustomer: 'One customer and one service.',
    blockTime: 'Block time',
    blockTimeHint: 'Mark the time as unavailable. No customer.',
    titleLabel: 'Title',
    titlePlaceholder: 'Give the meeting a name',
    vertSectionLabel: 'host',
    youFallback: 'You',
    vertBadge: 'host',
    vertSubtitle: 'you · assigned automatically',
    customerComboLabel: 'Pick a customer or add a new one',
    customerComboPlaceholder: 'Search or enter a new customer',
    customerComboCreateLabel: 'Add new customer',
    newCustomerSectionLabel: 'new customer',
    nameLabel: 'Name',
    namePlaceholder: 'Enter a name',
    emailLabel: 'Email',
    emailPlaceholder: "margrethe{'@'}firma.no",
    newCustomerHint: 'The customer gets the invitation and reminders at the email above.',
    billingAddressLabel: 'Billing address',
    billingAddressHint: 'Used as the buyer address on the invoice after the meeting is delivered.',
    angrerettAttestationLabel:
      'I confirm that the customer expressly asked for the service to be delivered before the withdrawal period expires and was told that the withdrawal right ends once the service is fully delivered.',
    angrerettAttestationHint:
      'This is stored as the operator’s attestation, not as a customer click.',
    customersSectionLabel: 'customers',
    customerCount: '{count} of {max}',
    removeCustomerLabel: 'Remove customer',
    addCustomerComboLabel: 'Search or add a new customer',
    addButton: 'Add',
    addCustomerRow: 'Add customer',
    serviceLabel: 'Service',
    serviceOption: '{name} · {duration} min',
    colleaguesSectionLabel: 'colleagues',
    coHostCount: 'co-host',
    coHostBusySubtitle: 'busy at the chosen time',
    coHostSubtitle: 'co-host · gets a calendar invite',
    removeColleagueLabel: 'Remove colleague',
    guestNotRegisteredBadge: 'not registered',
    guestSubtitle: 'co-host · gets an email invite',
    removeGuestLabel: 'Remove guest',
    pickColleagueLabel: 'Pick colleague',
    addColleagueRow: 'Add colleague',
    meetingTypeLabel: 'Service',
    dateLabel: 'Date',
    fetchingSlots: 'fetching available times...',
    availabilityFor: 'Availability for',
    meetingDuration: 'Meeting duration',
    meetingDurationAria: 'Meeting duration: {minutes} minutes',
    durationLabel: 'Duration',
    durationHint:
      'Video meetings can last up to {limit} min. In-person meetings can last up to 480 min.',
    durationWholeMinutesError: 'Enter the duration in whole minutes.',
    durationRangeError: 'The duration must be between 1 and 480 minutes.',
    durationVideoLimitError:
      'Video meetings can last up to {limit} minutes. Choose a meeting place for a longer meeting.',
    pollDeadlineError: 'The deadline must be before the first proposed time.',
    busyTitle: 'Busy: {names}',
    busyAria: '{time}, busy: {names}',
    dimmedBusyHint: 'Dimmed times are busy for one or more participants.',
    noCommonSlots: 'No common free times this day. Pick another date.',
    allowOutsideHours: 'Allow outside working hours',
    timeSectionLabel: 'time',
    outsideHoursNote:
      'These times fall outside your usual working hours. Buffer and other meetings are still filtered out.',
    noSlots: 'No available times this day. Pick another date.',
    noFollowUpSlotsAhead:
      'No available times in the next 60 days. Choose a later date to keep looking.',
    recurrenceLabel: 'Repeat',
    recurrenceCountLabel: 'Number of times',
    summaryTypeLabel: 'Type',
    summaryTitleLabel: 'Title',
    summaryServiceLabel: 'Service',
    summaryTimeLabel: 'Time',
    summaryRecurrenceLabel: 'Repeat',
    summaryParticipantsLabel: 'Participants',
    noteLabel: 'Note to the customer',
    noteHint: 'Sent along with the invitation.',
    agendaLabel: 'What do we need answered?',
    agendaPlaceholder: 'One point per line',
    agendaHintTeam:
      'Optional. Enter adds the next point. The points stay ready as a checklist in the meeting.',
    agendaHintCustomer:
      'Optional. Enter adds the next point. The points stay ready as a checklist in the meeting, and go along with the invitation.',
    inviteMessageLabel: 'Message to the participants',
    inviteMessagePlaceholder: 'Optional greeting that goes with the invitation',
    inviteMessageHint: 'Optional. Shown in the invitation to the participants.',
    locationLabel: 'Location',
    locationHelp: 'Search for an address or pick a saved place.',
    locationDefaultHint:
      'Default for "{name}": {address}. The meeting uses this unless you pick a different location.',
    locationTeamHint:
      'Leave it empty for a video meeting with its own link. If you pick a place, the meeting becomes an in-person meeting without a video link.',
    backCta: '← Back',
    nextCta: 'Next →',
  },
  dashboardNewServiceTypeModal: {
    titleStep1: 'What kind of meeting is this?',
    titleDetails: 'Details',
    titleNew: 'New service',
    kickerStep: 'new service · step {step} of 2',
    kicker: 'new service',
    kindSamtale: 'Conversation',
    kindBehandling: 'Treatment',
    kindTrening: 'Training',
    lede: 'The choice decides how the meeting looks to you during the session itself, and which details we ask for in the next step. You cannot change it later without creating a new service.',
    samtaleSub:
      'You talk, listen, take notes. The meeting happens on video, by phone or in person at the office. Prepare with context and questions; during the meeting you have a notepad.',
    samtaleChip1: 'Intro conversation',
    samtaleChip2: 'Follow-up',
    samtaleChip3: 'Strategy',
    samtaleChip4: 'Advisory',
    behandlingSub:
      'The customer is with you, your hands are working. The meeting surface is hands-free, with big checkboxes and a short plan for what you are going to do.',
    behandlingChip1: 'Neck treatment',
    behandlingChip2: 'Reflexology',
    behandlingChip3: 'Manual mobilisation',
    behandlingChip4: 'Acupuncture',
    treningSub:
      'You guide an athlete through a session. The meeting surface is built for training, you plan exercises before the session and note results along the way. No pain score or journal.',
    treningChip1: 'PT session',
    treningChip2: 'Strength session',
    treningChip3: 'Running coaching',
    treningChip4: 'Follow-up',
    nameLabel: 'Name',
    namePlaceholderBehandling: 'e.g. Neck treatment',
    namePlaceholderTrening: 'e.g. Strength session',
    namePlaceholderSamtale: 'e.g. Intro conversation',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What kind of meeting is this?',
    requiresAckLabel: 'Require the customer to confirm the description',
    requiresAckDescription:
      'The customer must read the description and tick "I have read and understood" before the booking can be completed.',
    durationLabel: 'Duration (min)',
    videoDurationHint: 'Video meetings can last up to {max} minutes with your subscription.',
    videoDurationError: 'Choose a duration of {max} minutes or less for video meetings.',
    turnaroundLabel: 'Turnaround time (min)',
    turnaroundHint: 'Between meetings.',
    bufferLabel: 'Buffer (min)',
    priceLabel: 'Price (kr)',
    freeLabel: 'Free service',
    freeHint: '(hide the price field on the page)',
    modeLabel: 'Where',
    modeVideo: 'Video',
    modeOffice: 'At the office',
    modeBoth: 'Both',
    addressLabel: 'Address',
    addressHint:
      'The address the meeting takes place at. In-person meetings use this as the default.',
    trainingTypeLabel: 'Training type',
    typeSearchPlaceholder: 'Search or add',
    typeCreateLabel: 'Add',
    practitionerTypeLabel: 'Practitioner type',
    seriesLabel: 'Part of a treatment series',
    seriesDescription:
      'We show "treatment 3 of 6" on the meeting card and give you a recap from the previous session on the Prepare page. For planning only, not a punch card or a payment counter.',
    seriesLengthLabel: 'Number of treatments in the series',
    seriesLengthPlaceholder: 'e.g. 6',
    continueCta: 'Continue →',
    createCtaBehandling: 'Create treatment',
    createCtaTrening: 'Create training type',
    createCtaSamtale: 'Create service',
    addTypeError: 'Could not add the type.',
    createError: 'Could not create the service.',
  },
  dashboardNoteCreateDialog: {
    title: 'New note',
    kicker: 'new note',
    prefillLabel: 'Note about',
    intro:
      'A note is most useful when it is about a customer, ideally across the meetings you have together. Choose who it concerns.',
    customerLabel: 'Customer',
    customerPlaceholder: 'Search by name or email',
    createCustomerLabel: 'Create customer from',
    loadingCustomersHint: 'Loading customers ...',
    creatingCustomerHint: 'Creating customer ...',
    escapeLabel: 'Not tied to a customer',
    escapeHint: 'For sparring, a template, or something you want to link later.',
    titleFieldPrefillLabel: 'Note',
    titleFieldLabel: 'Title (optional)',
    titlePlaceholderPrefill: 'Write down what you want to remember',
    titlePlaceholder: 'Leave empty to use the default title',
    saveCta: 'Create note',
    loadCustomersError: 'Could not load customers. Try again shortly.',
    createCustomerError: 'Could not create customer. Try again.',
  },
  dashboardNoteShareDialog: {
    modalTitle: 'Give a colleague access.',
    modalKicker: 'share note',
    lockedIntro:
      'Sharing notes requires the Team plan or higher. On Team you can give individual colleagues access to a note or the whole meeting, with or without commenting rights.',
    seePlansCta: 'See plans',
    intro: 'What can your colleague see? You can change this at any time.',
    recipientLabel: 'colleague',
    removeRecipientAria: 'Remove {name}',
    recipientPlaceholder: 'Type a name...',
    scopeLegend: 'access level',
    scopeBookingLabel: 'With meeting',
    scopeBookingDesc:
      "Your colleague sees every note on this meeting, plus the name, time and meeting link. Access covers this meeting only, not the customer's other meetings or history. The meeting shows up under their Meetings.",
    scopeBookingFoot: 'for collaboration before the meeting, or when someone takes over for you',
    scopeEntryLabel: 'Note only',
    scopeEntryDesc:
      "Your colleague sees the note, but not the customer's name, time or meeting link. The note lives only under Notes → Shared with me.",
    scopeEntryFoot: 'for sparring or a second pair of eyes without exposing the customer',
    authorNoteLabel: 'Message (optional)',
    authorNotePlaceholder:
      "Tell them what you want help with, so the recipient doesn't open the note without context.",
    charsLeftHint: '{n} characters left',
    canCommentLabel: 'Can comment',
    canCommentHint: "You'll be notified about replies.",
    shareCta: 'Share note',
    expiryLabel: 'duration',
    expiry7d: '7 days',
    expiry30d: '30 days',
    expiry90d: '90 days',
    expiryNever: 'Until you remove it',
    footerNoteEntry: "the customer can't see that the note is shared",
    footerNoteBooking: 'meeting access lasts until you remove it',
    footerNoteBookingUntil: 'meeting access expires {date}',
  },
  dashboardNotesEntryForm: {
    suggestionPrep: 'Preparations',
    suggestionNotes: 'Notes',
    suggestionFollowUp: 'Follow-up',
    newNote: 'New note',
    chooseTemplate: 'Or choose a template:',
    titleLabel: 'Title (optional)',
    titlePlaceholder: 'E.g. Preparations',
    bodyLabel: 'Note',
    bodyPlaceholder:
      'Write what was discussed, what you are preparing, or what you are following up on.',
    saveNote: 'Save note',
  },
  dashboardPollSetup: {
    allowVoting: 'Allow voting on times',
    hint: 'Pick at least two times. The first one is your suggestion.',
    starHint: 'Tap the star to choose which time is the suggestion.',
    deadlineHint:
      'If you hear nothing before the deadline, the suggestion is chosen automatically.',
    allowAfterHours: 'Allow outside working hours',
    loadingSlots: 'fetching available times...',
    pickTimes: 'pick times',
    busyTitle: 'Busy: {label}',
    busyAriaLabel: '{time}, busy: {label}',
    busyHint: 'Dimmed times are busy for one or more participants.',
    emptyBody: 'No available times this day. Pick another date.',
    deadlineLabel: 'Deadline to vote (optional)',
  },
  dashboardPollStatusPanel: {
    kicker: 'Poll · {status}',
    turnoutNone: 'No participants yet',
    turnoutAllVoted: 'Everyone has voted ({total})',
    turnoutCount: '1 participant | {total} participants',
    voteLead: 'Tell us which times work for you.',
    voteLeadHostLocks: ' The host locks the final time.',
    voteSaved: 'Your vote is saved.',
    voteUpdateCta: 'Update vote',
    voteSaveCta: 'Save vote',
    emptyBody: 'No votes yet.',
    markWinner: 'chosen',
    markLeading: 'leading',
    countWorks: '{count} works',
    countDoesnt: '{count} does not work',
    lockTimeCta: 'Lock time',
    hideWho: 'Hide who',
    showWho: 'Show who',
    whoLabelWorks: 'Works',
    whoLabelDoesnt: 'Does not work',
    participantFallback: 'Participant',
    pendingSection: 'suggestions for approval',
    approveCta: 'Approve',
    rejectCta: 'Reject',
    pillCommitted: 'time chosen',
    committedMoved: 'The meeting was moved to {time} after the poll.',
    committedLocked: 'The time is locked. Participants have received a confirmation.',
    statusCommitted: 'time chosen',
    statusCancelled: 'cancelled',
    statusExpired: 'expired',
    statusOpen: 'open for voting',
  },
  portalActionPoints: {
    title: 'My tasks',
    open: 'To do',
    completed: 'Completed',
    loading: 'Loading your tasks …',
    empty: 'You have no tasks here yet.',
    loadError: 'Could not load your tasks. Try again.',
    saveError: 'Could not save the change. Try again.',
    retry: 'Try again',
    meeting: 'Meeting',
    due: 'Due {date}',
    noDeadline: 'No deadline',
    completedOn: 'Completed {date}',
    complete: 'Mark as completed',
    reopen: 'Reopen',
    completeLabel: 'Mark “{text}” as completed',
    reopenLabel: 'Reopen “{text}”',
  },
  dashboardPrepNotePad: {
    defaultTitle: 'Preparation',
    liveEditorLabel: 'During the meeting',
    saveError: 'The note could not be saved. Your changes are still here. Try again.',
    retrySave: 'Try again',
    unsavedChangesConfirm: 'The note has unsaved changes. Leave this page and discard them?',
    grantErrorWithReason: 'Could not grant access: {reason}',
    grantError: 'Could not grant access. Try again.',
    titlePlaceholder: 'Title (optional)',
    editorPlaceholder:
      'Write questions, context and what you want to get out of the meeting. Use the buttons above for headings, lists and quotes.',
    count: '{words} words · {chars} characters',
    conflictStatus: '⚠ a new version was saved elsewhere, reload',
    readonlyStatus: 'read only',
    savingStatus: 'saving ...',
    savedStatus: 'saved automatically',
    emptyStatus: 'empty',
    loading: 'Loading notes...',
  },
  dashboardPrepProTip: {
    ariaLabel: 'Tip',
    label: 'Tip',
  },
  dashboardPriorMeetingsCard: {
    title: 'Earlier with {name}',
    defaultMeetingTitle: 'Meeting',
    withConsultant: 'with {name}',
    seeAllHistory: "See all of {name}'s history",
    meetingCount: '1 earlier meeting | {n} earlier meetings',
    today: 'today',
    yesterday: 'yesterday',
    daysAgo: '1 day ago | {n} days ago',
    weeksAgo: '1 week ago | {n} weeks ago',
    monthsAgo: '1 month ago | {n} months ago',
    yearsAgo: '1 year ago | {n} years ago',
  },
  dashboardRescheduleOrCancelModal: {
    title: 'Change or cancel',
    chooseAction: 'Choose action',
    tabReschedule: 'Move meeting',
    tabMode: 'Change format',
    tabLocation: 'Change location',
    tabReassign: 'Change advisor',
    tabCancel: 'Cancel',
    newDate: 'New date',
    chooseTime: 'Choose time',
    allowOffHours: 'Allow outside working hours',
    loadingSlots: 'Loading available times ...',
    noSlotsAnyHours: 'No available times this day. Choose another date.',
    noSlotsWorkHours:
      'No available times within working hours. Turn on "outside working hours" or choose another date.',
    offHoursNote: 'Some of these times may fall outside your usual working hours.',
    emailHint: 'Included in the email about the change.',
    messageToCustomer: 'Message to the customer',
    messageToParticipants: 'Message to the participants',
    modeToday: 'Today',
    modeSwitchesTo: 'Switches to',
    modeOffice: 'at the office',
    modeVideo: 'video',
    customer: 'The customer',
    modeNotice: '{name} gets an email that the meeting format has changed.',
    modeNoticeVideo: 'A video link is created automatically and attached.',
    modeNoticeOffice: 'The address is shown on their booking page.',
    locationNotice:
      '{name} gets an email with the new location and a fresh link. Connected calendars update automatically.',
    locationLabel: 'Location',
    messageToCustomerOptional: 'Message to the customer (optional)',
    reassignNotice:
      'The meeting is handed to another advisor. Both advisors are notified, and the customer keeps the same time and link.',
    reassignEmpty: 'No other advisors to hand it to.',
    chooseAdvisor: 'Choose advisor',
    cancelNotice: '{name} gets an email that the meeting is cancelled. No charge is made.',
    cancelReasonLabel: 'Reason (optional, shown in the email)',
    cancelReasonPlaceholder: 'Write something short and human...',
    rescheduleCta: 'Move and notify',
    modeCta: 'Switch to {mode}',
    locationCta: 'Save location',
    reassignCta: 'Hand over meeting',
    cancelCta: 'Cancel and notify',
    kicker: 'meeting · {date} {time}',
    scopeLabel: 'Choose scope',
    scopeSingle: 'Only this meeting',
    scopeSeries: 'The whole series',
    seriesRescheduleNote:
      'Every remaining meeting in the series moves by the same amount as this one. Meetings that already happened are untouched.',
    seriesConflictIntro: 'These times are not available, so nothing was moved:',
    seriesConflictPast: 'in the past',
    seriesConflictUnavailable: 'taken',
    seriesCancelNotice:
      '{name} gets an email that the rest of the series is cancelled. Meetings that already happened are untouched.',
    seriesRescheduleCta: 'Move series and notify',
    seriesCancelCta: 'Cancel series and notify',
  },
  dashboardRescheduleProposalBanner: {
    resolveError: 'Could not respond to the proposal. Try again.',
  },
  dashboardSMSCreditsBanner: {
    eyebrowEmpty: 'out of sms',
    eyebrowLow: 'low on sms',
    titleEmpty: 'You are out of SMS',
    titleLow: 'Running low on SMS, {balance} left',
    meta: 'Email reminders are sent as usual. Buy more to keep sending SMS.',
    cta: 'Buy SMS',
  },
  dashboardSeriesBadge: {
    counted: '{n} of {total}',
    plain: 'series',
    countedAria: 'Meeting {n} of {total} in a recurring series',
    plainAria: 'Part of a recurring series',
  },
  dashboardServiceColorPicker: {
    label: 'Color',
  },
  // Per-service payment terms (#1191). Config only in this slice — nothing is
  // charged to the customer yet.
  stripeConnect: {
    heading: 'Payments',
    lede: 'Connect Stemne to Stripe so you can charge your customers. Nothing is charged yet.',
    loading: 'Loading status',
    loadError: 'Could not load the payment status.',
    connectError: 'Could not start the Stripe connection.',
    refreshError: 'Could not refresh the status from Stripe.',
    dashboardError: 'Could not open your Stripe dashboard.',
    disconnectError: 'Could not disconnect Stripe from Stemne.',
    refreshedToast: 'Status refreshed',
    returnIncomplete: 'The connection was not completed. You can pick up where you left off.',
    disconnectedHeading: 'Stripe is disconnected',
    disconnectedNote:
      'Stemne has stopped new payments. Your previous account, balance and payment history remain available in Stripe. Connect again for future payments.',
    disconnectedToast: 'The Stripe account is disconnected. New payments have stopped.',
    disconnectedByOperatorToast: 'Stripe is disconnected from Stemne. New payments have stopped.',
    chargesLabel: 'Can accept payments',
    payoutsLabel: 'Can receive payouts',
    detailsLabel: 'Connection complete',
    on: 'Yes',
    off: 'No',
    submitted: 'Complete',
    notSubmitted: 'Not complete',
    vippsLabel: 'Vipps',
    vipps: {
      not_requested: 'Not enabled',
      pending: 'In review',
      active: 'Active',
    },
    vippsNote: 'Requires prior approval from Stripe.',
    neverSynced: 'The status has not been fetched from Stripe yet.',
    payoutNote:
      'You manage payouts, refunds and disputes in your Stripe dashboard. Stripe manages the balance and any amounts it must recover under your agreement with Stripe.',
    termsLink: 'Terms for Payment at booking',
    readyNote:
      'Payment at booking is on. You choose the payment method for each service under {services}.',
    servicesLink: 'Services',
    pendingNote: 'Stripe still needs something from you before you can charge.',
    disabledReason: {
      requirements_due:
        'Stripe needs information from you before you can charge. You provide it in the Stripe dashboard.',
      pending_verification:
        'Stripe is reviewing your details. There is nothing for you to do while they are.',
      restricted:
        'Stripe has stopped card payments for your account. Only Stripe can lift that, so get in touch with them.',
      unsupported:
        'Stripe does not support this business or entity type for card payments. Contact Stripe if you think that is wrong.',
    },
    requirementsDeadline: 'The deadline is {date}.',
    connectCta: 'Connect to Stripe',
    resumeCta: 'Continue at Stripe',
    reconnectCta: 'Connect to Stripe again',
    refreshCta: 'Refresh status',
    dashboardCta: 'Open your Stripe dashboard',
    disconnectCta: 'Disconnect Stripe',
    disconnectTitle: 'Disconnect Stripe from Stemne?',
    disconnectBody: 'Stemne will stop using this Stripe connection for new payments.',
    disconnectWarningPayments: 'New payments through Stemne stop immediately.',
    disconnectWarningAccount: 'Your Stripe account, balance and payment history are not deleted.',
    disconnectWarningReconnect: 'If you connect again later, Stemne creates a new connection.',
    disconnectConfirm: 'Disconnect',
  },
  // Reference translation only — the operator UI is Norwegian. The Norwegian
  // wording is the reviewed one; keep this in step with it, and never let
  // either language imply that turning payments on discharges the operator's
  // duty. It does not: Stemne captures the disclosure and the consent, the
  // trader is still the operator.
  serviceAngrerettNotice: {
    title: 'You take payment outside Stemne',
    body1:
      'When you set a price and invoice the customer yourself, it is you who must tell them about the right of withdrawal, and you who must obtain their express request if the meeting starts before the withdrawal period ends. Stemne does not collect that request for you today.',
    body2:
      'If you take payment through Stemne, we do both: the information travels with the confirmation, and the request is collected during booking. Responsibility for the service is still yours.',
    enableCta: 'Turn on payments',
    readMoreCta: 'Read about the right of withdrawal',
  },
  servicePaymentConfig: {
    heading: 'Payment',
    teaser: 'You can take payment or ask for a deposit when a customer books.',
    teaserLink: 'See add-ons',
    teaserOwnerOnly: 'The account owner can add it.',
    hint: 'What the customer pays for this service, and when. Nothing is charged yet.',
    loading: 'Loading payment terms',
    loadError: 'Could not load the payment terms.',
    saveError: 'Could not save the payment terms.',
    savedToast: 'Payment terms saved',
    saveCta: 'Save payment',
    priceLabel: 'Service price',
    priceValue: 'kr {price}',
    priceFree: 'free',
    modeLabel: 'Payment method',
    modeNone: 'No payment',
    modeDeposit: 'Deposit',
    modeFull: 'Full payment',
    modeAfter: 'Pay afterwards',
    modeAfterHint:
      'Nothing is charged at booking. Once the meeting has been held, we ask the customer for the full price by email.',
    depositLabel: 'Deposit',
    depositHint: 'Whole kroner. The remainder is settled outside Stemne.',
    depositHintPct: 'Percent of the price. The remainder is settled outside Stemne.',
    depositHintCollected:
      'Whole kroner. The remainder is collected after the meeting has been held.',
    depositHintPctCollected:
      'Percent of the price. The remainder is collected after the meeting has been held.',
    depositUnitLabel: 'Unit',
    depositUnitKr: 'Kroner',
    depositUnitPct: '% of the price',
    depositResolved: 'Comes to kr {amount} of kr {price}.',
    vatLabel: 'VAT',
    vatUnntatt: 'Exempt health service',
    vatStandard: '25 % VAT',
    vatHint: 'Health services are VAT exempt. Advisory and training are 25 %.',
    priceIncludesVatLabel: 'Price includes VAT',
    priceIncludesVatHint: 'Turn off if the price you set is exclusive of VAT.',
    priceIncludesVatLockedHint: 'When Stemne takes payment, the price must include VAT.',
    errorNeedsPrice:
      'The service needs a price above 0 kroner before you can charge for it. Set a price first.',
    errorDepositRequired: 'Set a deposit amount.',
    errorDepositInteger: 'Enter the amount in whole kroner, without spaces or decimals.',
    errorDepositAbovePrice: 'The deposit cannot be higher than the price.',
    errorDepositPctInteger: 'Enter the percentage as a whole number, without decimals.',
    errorDepositPctRange: 'The deposit must be between 1 and 100 percent.',
  },
  dashboardServiceEditModal: {
    offeringSaveError: 'Could not save. Try again.',
    addTypeError: 'Could not add the type.',
    kindSamtale: 'Conversation',
    kindBehandling: 'Treatment',
    kindTrening: 'Training',
    offeringsPartialSaveError:
      'The service was created, but some of the choices for who offers it were not saved.',
    saveError: 'Could not save the service.',
    deleteError: 'Could not delete the service.',
    titleEdit: 'Edit service',
    titleNew: 'New service',
    kindGroupAria: 'Service kind',
    kindEdit: 'Change category',
    tabsAria: 'Service settings',
    tabDetails: 'Details',
    tabAvailability: 'Availability',
    tabPayment: 'Payment',
    tabNotifications: 'Notifications',
    nameLabel: 'Name',
    namePlaceholder: 'Intro conversation',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'What kind of meeting is this?',
    durationLabel: 'Duration (min)',
    videoDurationHint: 'Video meetings can last up to {max} minutes with your subscription.',
    videoDurationError: 'Choose a duration of {max} minutes or less for video meetings.',
    bufferLabel: 'Buffer (min)',
    priceLabel: 'Price (kr)',
    freeToggleLabel: 'Free service (hide the price field on the page)',
    actionTemplatesLabel: 'action points',
    actionTemplatesHint:
      'Each point becomes a task for the assigned operator whenever someone books this service.',
    actionTemplateTextPlaceholder: 'What needs doing afterwards',
    actionTemplateTagPlaceholder: 'tag',
    actionTemplateDuePlaceholder: 'days',
    actionTemplateRemove: 'Remove point',
    actionTemplateAdd: 'Add point',
    modeLabel: 'Where',
    modeVideo: 'Video',
    modeOffice: 'At the office',
    modeBoth: 'Both',
    addressLabel: 'Address',
    addressHint: 'The address the meeting takes place at. In-person meetings use this by default.',
    locationDetailsLabel: 'Message to the customer about the location',
    locationDetailsPlaceholder: 'Storgata 14, 2nd floor. Ring the doorbell.',
    locationDetailsHint: 'Details the customer needs to find their way, floor, door code, parking.',
    colleagueInviteLabel: 'Let the customer invite colleagues',
    colleagueInviteDesc:
      'Shown as "Invite colleagues" on the meeting overview the customer receives.',
    requiresAckLabel: 'Require the customer to confirm the description',
    requiresAckDesc:
      'The customer must read the description and tick "I have read and understood" before the booking can be completed.',
    groupCapacityLabel: 'Group size',
    groupCapacityHint:
      'Number of seats if this is a group conversation. Leave empty for a regular 1:1 meeting.',
    activeLabel: 'Visible to customers',
    activeDesc:
      'When visible, customers can book this service. The data processing agreement must be accepted before you can receive bookings.',
    privacyLabel: 'Private meeting',
    privacyDesc:
      'When on, only the host (and admins after an explicit open) sees details. Default: off, all colleagues can see.',
    turnaroundLabel: 'Turnaround time (min)',
    turnaroundHint: 'Between meetings, the room is tidied, hands are washed.',
    practitionerKindLabel: 'Practitioner type',
    treatmentTypeSearchPlaceholder: 'Search or add',
    treatmentTypeCreateLabel: 'Add',
    seriesLengthLabel: 'Number of treatments in a series',
    seriesLengthPlaceholder: 'e.g. 6',
    seriesLengthHint:
      'Planned length of a course, shown as "Treatment N of M" on the meeting card. Not a punch card, and does not count paid hours. Leave empty to hide the total.',
    trainingTypeLabel: 'Training type',
    offeringsLabel: 'Who offers this',
    offeringsHint: 'Everyone offers this service. Remove those who should not.',
    availabilityDesc:
      'Decide how far ahead customers can book, and what happens if they reschedule or cancel close to the meeting time.',
    maxLeadLabel: 'How far ahead can customers book?',
    maxLead1Week: '1 week',
    maxLead2Weeks: '2 weeks',
    maxLead1Month: '1 month',
    maxLead3Months: '3 months',
    maxLead6Months: '6 months',
    maxLead1Year: '1 year',
    maxLeadNone: 'No limit',
    maxLeadHint:
      'The default is 3 months. "No limit" lets customers book as far ahead as they want.',
    leadHoursLabel: 'Alert on late change (hours before)',
    leadHoursPlaceholder: 'Leave empty for the default (24 hours)',
    leadHoursHint:
      'You get alerted if the customer reschedules or cancels closer than this. Leave empty for the 24-hour default. Set 0 to turn it off.',
    lateChangeWeekendsLabel: 'Count weekends',
    lateChangeWeekendsDesc:
      'Off: weekends don’t count, so "24 hours" means one working day. On: measure plain wall-clock, weekends included.',
    lateWarningLabel: 'Text the customer sees on a late change',
    lateWarningPlaceholder: 'Cancellations under 24 hours are billed as a regular session.',
    lateWarningHint: 'Shown when the customer reschedules or cancels within the alert time above.',
    rescheduleApprovalLabel: 'Change of time',
    rescheduleInherit: 'Follow the default setting',
    rescheduleRequire: 'Require approval (the customer proposes)',
    rescheduleAllow: 'Allow direct change',
    rescheduleApprovalHint:
      'When approval is required, the customer does not move the meeting themselves, they send a proposal you approve. Group meetings always go through a proposal.',
    notifyConfirmationLabel: 'Send confirmation to the customer',
    notifyConfirmationDesc:
      'Sends a confirmation email with the meeting link when the customer books. Recommended on, so the customer has the meeting link.',
    notifyReminderEmailLabel: 'Send a reminder 24 hours before',
    notifyReminderEmailDesc:
      'Automatically sends an email with the meeting link 24 hours before the meeting.',
    notifyReminderSmsLabel: 'Send an SMS reminder 24 hours before',
    notifyReminderSmsDesc:
      'Sends an SMS 24 hours before the meeting when the customer has entered a phone number. Requires the SMS add-on and remaining SMS credits.',
    deleteCta: 'Delete',
    createCta: 'Create',
    deleteConfirmTitle: 'Delete the service?',
    deleteConfirmBody:
      'Existing meetings are kept. The service disappears from the booking page immediately.',
    deleteConfirmCta: 'Delete the service',
    conflictIntro:
      'These meetings are so close together that they no longer fit with the new settings. You have to move or cancel them manually after you save.',
    conflictConfirmCta: 'Save anyway',
  },
  dashboardServiceOfferingsMatrix: {
    title: 'Who offers what',
    intro: 'Check off who can be assigned to each service. Everyone offers everything by default.',
    loading: 'Loading ...',
    emptyServices: 'There are no services yet.',
    soloOperator:
      'You are the only advisor, so you offer every service. When the team grows, you manage the distribution here.',
    advisorHeader: 'Advisor',
    cellAriaLabel: '{operator} offers {service}',
    zeroCoverageWarning: 'no one offers this service. Meetings will be left without an advisor.',
    saveError: 'Could not save. Try again.',
  },
  dashboardStatusPill: {
    confirmed: 'confirmed',
    pending: 'pending',
    cancelled: 'cancelled',
    rescheduled: 'rescheduled',
    expired: 'expired',
    completed: 'completed',
    noShow: 'no show',
    unresolved: 'unresolved',
  },
  dashboardSubscriptionStatusCard: {
    trialPeriod: 'Trial period',
    lastDay: 'Last day',
    oneDayLeft: '1 day left',
    daysLeft: '{n} days left',
    trialOver: 'Your trial has ended',
    paymentDue: 'Payment overdue',
    paused: 'Paused',
    ended: 'Ended',
    inTrial: 'in the trial period',
    trialUntil: 'until {date}',
    noPaymentMethod: 'no payment method',
    updatePaymentMethod: 'update payment method',
    choosePlan: 'choose plan',
  },
  dashboardTrialBanner: {
    trialingNoDays: 'Choose a plan before the trial ends.',
    trialingLastDay: 'Last day of the trial.',
    trialingDaysLeft: '1 day left of the trial. | {n} days left of the trial.',
    trialExpired: 'The trial is over. Choose a plan to resume booking.',
    pastDue: 'The payment was declined. Update your payment method to avoid interruption.',
    paused: 'The subscription is paused. The booking page is hidden from customers.',
    canceled: 'The subscription has ended.',
    ctaChoosePlan: 'Choose plan',
    ctaUpdatePayment: 'Update payment method',
    ctaResume: 'Resume',
  },
  dashboardWelcomeModal: {
    modalTitle: 'Welcome to Stemne',
    kicker: 'Welcome to Stemne',
    greeting: 'Good to have you here, {name}.',
    greetingNoName: 'Good to have you here.',
    lead: 'Your page is ready. From here you keep track of meetings, customers and notes, all in one place. It is quiet right now; it fills up as people book.',
    dpaLead:
      'Before customers can book, the business must accept the data processing agreement. You can still explore the dashboard.',
    inviteTitle: 'Invite team members',
    inviteDesc: 'Get your advisors on board, so you can share meetings and notes.',
    helpPrefix: 'Stuck? Drop by',
    helpLink: 'the help pages',
    helpMiddle: ', or write to us at',
    helpSuffix: ', we answer like real people.',
    footerNote: 'You will receive short onboarding emails. Turn them off under Notifications.',
    acceptDpaCta: 'Accept the agreement and open the page',
    viewDpaCta: 'View the agreement',
    shareCta: 'Share your page',
  },
  billingAddonCard: {
    pillActive: 'Active',
    pillIncluded: 'Included',
    pillTrial: 'Trial',
    pillRequiresPayment: 'Payment required',
    pillAvailable: 'Available',
    pillRequiresVekst: 'Requires Growth',
    pillUnavailable: 'Not available',
    etaSoon: 'soon',
    pillComing: 'Coming {eta}',
    // Suffix-less form is the "unspecified" case — no VAT claim at all.
    priceLabel: '{sign}{price} kr / month',
    priceLabelInclVat: '{sign}{price} kr / month incl. VAT',
    priceLabelExclVat: '{sign}{price} kr / month excl. VAT',
    tierVekst: 'Growth',
    tierTeam: 'Team',
    tierPlan: 'the plan',
    activateAria: 'Activate {name}',
    deactivateAria: 'Deactivate {name}',
    termsLink: 'Read the terms',
    updating: 'Updating ...',
    tryInTrial: 'Try during trial',
    paymentRequired: 'Purchase requires payment first',
    includedIn: 'Included in {tier}',
    switchToVekst: 'Switch to Growth to activate',
    pilotOnly: 'Opening for pilot customers first',
    notifyMe: 'Notify me',
    willNotify: "We'll let you know",
    cancelTitle: 'Deactivate {name}?',
    cancelBody:
      'The add-on is deactivated immediately. Unused time is credited on your next invoice.',
    cancelConfirm: 'Deactivate add-on',
    cancelKeep: 'Keep add-on',
  },
  billingBillingAddonsPanel: {
    kicker: 'add-ons',
    title: 'Expand your subscription without switching plans',
    sub: 'Add features on top of your plan. Changes are billed pro-rata from today.',
    noActiveAddons: 'No active add-ons.',
    planSub: 'Your plan: {plan}.',
    planLineWithPrice: '{name}, {price} kr / mo',
    activeAddonsCount: '{count} active add-ons.',
    totalPerMonth: '+{total} kr / mo',
  },
  billingBillingCancelModal: {
    title: 'Cancel the subscription?',
    ledeBefore: 'You keep full access for the rest of the period, until',
    ledeAfter: '. After that the account is frozen for 30 days before data is deleted.',
    reasonLegend: 'Why? (optional, but it helps us)',
    reasonTooExpensiveName: 'Too expensive',
    reasonTooExpensiveDesc: "The price doesn't fit right now.",
    reasonNotUsingEnoughName: 'Not using it enough',
    reasonNotUsingEnoughDesc: "I'm not getting the value I hoped for.",
    reasonMissingFeaturesName: 'Missing features',
    reasonMissingFeaturesDesc: "I need something Stemne doesn't have.",
    reasonSwitchingToolsName: 'Switching to another tool',
    reasonSwitchingToolsDesc: 'I found something that fits better.',
    reasonOtherName: 'Other',
    reasonOtherDesc: 'Happy to tell you what, in the text box.',
    noteLabel: 'Details',
    notePlaceholder: 'What should we know? We read every piece of feedback.',
    deflectHeading: 'Just away for a while?',
    deflectBody:
      'A pause might be a better fit, you keep your setup, the calendar is paused, and you can start again whenever you want.',
    pauseInsteadCta: 'Pause instead →',
    confirmCta: 'Cancel {date}',
    cancelSuccess: 'The subscription is cancelled {date}',
    cancelError: "Couldn't cancel the subscription.",
  },
  billingBillingCycleNote: {
    invoiceSentTo: 'invoice sent to',
    cycleKicker: 'billing cycle',
    monthly: 'Monthly',
    yearly: 'Yearly',
    prorationNote: 'Stripe calculates the proration and shows you the amount before you confirm.',
    vatKicker: 'vat',
    vatNote: 'Deducted from the invoice with a valid org number.',
    switchToYearly: 'Switch to yearly',
    switchToMonthly: 'Switch to monthly',
  },
  billingBillingHeroCard: {
    hintPortal:
      "Opens Stripe's customer portal in a new tab. Change plan, update your card, or fetch invoices.",
    hintTrial: 'Pick a plan in the table below. You can switch whenever you like.',
    metaFirstCharge: 'first charge',
    metaAccessExpires: 'access expires',
    metaNextAttempt: 'next attempt',
    metaPaused: 'paused',
    metaEnded: 'ended',
    metaNextCharge: 'next charge',
    subUpdatePaymentMethod: 'update payment method',
    subNoChargesOngoing: 'no charges ongoing',
    subDataDeleted30Days: 'data is deleted after 30 days',
    subDataDeletedSoon: 'data is deleted shortly',
    priceAuto: '{price} for the plan alone · auto',
    currentPlan: 'current plan',
    users: 'users',
    paymentMethod: 'payment method',
    change: 'change',
    noPaymentMethod: 'No payment method added',
  },
  billingContext: {
    planPriceOnly: '{price} for the plan alone',
    priceMonthly: '{price} kr / mo excl. VAT',
    priceYearly: '{price} kr / yr excl. VAT',
    pillTrial: 'Free trial',
    pillLockedInTrial: 'Activated',
    pillActive: 'Active',
    pillPaymentPending: 'Processing',
    pillPaymentFailed: 'Payment failed',
    pillPastDueFinal: 'Final notice',
    pillPaused: 'Paused',
    pillCanceledGrace: 'Ends',
    pillCanceledGraceDate: 'Ends {date}',
    pillCanceledEnded: 'Ended',
    pillTrialEnded: 'Trial expired',
    trialDaysLeftNone: 'less than a day left',
    trialDaysLeft: '{n} day left | {n} days left',
    subtitleTrialWithDate: 'Choose a plan before {date}, or continue — we will remind you.',
    subtitleTrialNoDate: 'Choose a plan when you are ready.',
    subtitleLockedInTrial: '{price} · free until {date}',
    subtitleActive: '{price} · next charge {date}',
    subtitlePaymentPending: '{price} · charge sent',
    subtitleAccessUntil: '{price} · access until {date}',
    subtitlePaymentFailedNoDate: '{price} · update payment method to avoid interruption',
    subtitleAccessEnds: '{price} · access ends {date}',
    subtitleAccessEndsSoon: '{price} · access ends soon',
    subtitlePaused: '{price} · paused',
    subtitleCanceledEnded: 'Welcome back. Choose a plan to reactivate your account.',
    subtitleTrialEndedNoPayment: 'Add a payment method to pick up where you left off.',
    ctaChoosePlan: 'Choose plan',
    ctaManagePayment: 'Manage payment',
    ctaCheckStatus: 'Check status',
    ctaUpdatePayment: 'Update payment method',
    ctaResume: 'Resume subscription',
    ctaReopen: 'Reopen subscription',
    ctaExportData: 'Export data',
    bannerPaymentPendingTitle: 'We are waiting for confirmation from the bank.',
    bannerPaymentPendingBody:
      'This usually takes under a minute. Bank transfers can take up to 3 business days. You do not need to do anything.',
    bannerPaymentFailedTitle: 'The payment was declined.',
    bannerPaymentFailedBody:
      'We will try again automatically over the next few days. Update your payment method to avoid interruption to the service.',
    bannerPastDueTitle: 'We have tried to charge the amount several times.',
    bannerPastDueBodyGate:
      'Access ends if the payment does not go through. After that your customers will see «booking unavailable» when they visit your page.',
    bannerPastDueBodyNoGate: 'Access ends if the payment does not go through.',
    bannerPausedTitle: 'The subscription is paused.',
    bannerPausedBodyGate:
      'Your booking page is hidden from customers. Existing meetings stand; no new ones can be booked until you resume.',
    bannerPausedBodyNoGate: 'Existing meetings stand. You will not be charged until you resume.',
    bannerCanceledGraceTitle: 'Your access lasts through the period.',
    bannerCanceledGraceAutoDeleteDate:
      'After {date} the account is frozen for 30 days before it is deleted. You can reopen the subscription any time in the meantime.',
    bannerCanceledGraceAutoDeleteNoDate:
      'After the period ends the account is frozen for 30 days before it is deleted. You can reopen the subscription any time in the meantime.',
    bannerCanceledGraceKeepDate:
      'After {date} the account is frozen and your data is kept safe. You can reopen the subscription any time.',
    bannerCanceledGraceKeepNoDate:
      'After the period ends the account is frozen and your data is kept safe. You can reopen the subscription any time.',
    bannerCanceledEndedAutoDeleteTitle: 'Data will be deleted soon.',
    bannerCanceledEndedAutoDeleteBody:
      'We keep customer, meeting and invoice data for 30 days after cancellation. Export everything as CSV before then.',
    bannerCanceledEndedKeepTitle: 'Your data is safely stored.',
    bannerCanceledEndedKeepBody:
      'We keep customer, meeting and invoice data until you reopen the account or ask us to delete it. You can export everything as CSV at any time.',
    bannerTrialEndedTitleDate: 'Your trial ended {date}.',
    bannerTrialEndedTitle: 'Your trial is over.',
    bannerTrialEndedBodyGate:
      'Your booking page is hidden from customers, but all your data stands as it was. Activate a plan to resume normal operation.',
    bannerTrialEndedBodyNoGate:
      'All your data stands as it was. Activate a plan to resume normal operation.',
  },
  billingBillingInvoiceHistory: {
    kicker: 'invoices',
    title: 'Billing history',
    searchPlaceholder: 'Search invoice no, amount, month',
    searchAriaLabel: 'Search invoices',
    colDate: 'date',
    colNumber: 'number',
    colAmount: 'amount',
    colStatus: 'status',
    amount: '{amount} kr',
    retry: 'Try again ↗',
    downloadPdf: 'Download PDF ↓',
    statusPaid: 'Paid',
    statusOpen: 'Open',
    statusDraft: 'Draft',
    statusVoid: 'Void',
    statusUncollectible: 'Failed',
  },
  billingBillingInvoicesPanel: {
    summaryAria: 'Billing details',
    kickerPaymentMethod: 'payment method',
    noneAdded: 'None added',
    change: 'Change →',
    addCard: 'Add card →',
    kickerBillingEmail: 'invoice sent to',
    kickerBillingCycle: 'billing cycle',
    kickerVat: 'vat',
    vatValue: '25 %, Norway',
    vatSub: 'Deducted from the invoice with a valid company number.',
    pauseSubscription: 'Pause subscription',
    cancelSubscription: 'Cancel subscription',
    cycleYearly: 'Yearly',
    cycleMonthly: 'Monthly',
    switchToMonthly: 'Switch to monthly →',
    switchToYearly: 'Switch to yearly →',
  },
  billingBillingPauseModal: {
    ledeGated:
      'Your booking page is hidden from customers, existing meetings remain. You are not billed while paused, and your setup is ready when you resume.',
    lede: 'Existing meetings remain. You are not billed while paused, and your setup is ready when you resume.',
    reasonLessToDoName: 'Less to do right now',
    reasonLessToDoDesc: 'Quiet period, coming back.',
    reasonVacationName: 'Vacation / sabbatical',
    reasonVacationDesc: 'Away for a few weeks.',
    reasonRevisitingName: 'Considering changes',
    reasonRevisitingDesc: 'Thinking about plan or setup.',
    reasonOtherName: 'Other',
    reasonOtherDesc: 'Feel free to write a bit more.',
    duration1Month: '1 month',
    duration3Months: '3 months',
    durationIndefinite: 'Until I resume',
    pausedToast: 'Your subscription is paused.',
    pauseError: 'Could not pause the subscription.',
    title: 'Pause the subscription?',
    reasonLegend: 'Why? (optional, but it helps us)',
    noteLabel: 'Details',
    notePlaceholder: 'What should we know?',
    durationLabel: 'How long?',
    pauseCta: 'Pause the subscription',
  },
  billingBillingPaymentChip: {
    card: 'Card',
    balance: 'Balance',
    otherMethod: 'Other payment method',
  },
  billingBillingPlanPanel: {
    soloFeature1: '1 user',
    soloFeature2: 'Encrypted meeting notes',
    soloFeature3: 'Calendar connections (Google, Apple, Outlook)',
    soloFeature4: 'Norwegian invoicing with VAT',
    teamFeature1: 'Up to 5 users',
    teamFeature2: 'Share encrypted notes with colleagues',
    teamFeature3: 'Advisor role for invited users',
    teamFeature4: 'Your own sender address (email)',
    vekstFeature1: 'Up to 15 users',
    vekstFeature2: 'Audit log',
    vekstFeature3: 'API access (buy as an add-on)',
    vekstFeature4: 'Priority support',
    cycleMonthly: 'Monthly',
    cycleYearly: 'Yearly − 17%',
    availablePlansKicker: 'available plans',
    availablePlansTitle: 'Choose the size that fits',
    billingCycleAria: 'Billing period',
    allPlansLabel: 'On all plans:',
    allPlansBody: 'Encrypted meeting notes and calendar connections (AES-256).',
  },
  billingBillingPlanTable: {
    availablePlansKicker: 'available plans',
    title: 'Choose the size that fits',
    yearlyHint: 'save up to {pct}%',
    colPlan: 'plan',
    colPrice: 'price',
    colUsers: 'users',
    contactUs: 'Contact us',
    priceYearly: '{amount} kr / yr',
    priceMonthly: '{amount} kr / mo',
    trialFree: '3 months free',
    included: 'included',
    yearlyInvoiceSoon: 'Yearly billing coming soon',
    subYearlySaving: 'equals {monthly} kr / mo · save {pct}% versus monthly',
    subYearlyNoVat: 'equals {monthly} kr / mo · excl. VAT',
    subMonthly: 'excl. VAT · billed monthly',
    perYearShort: 'yr',
    perMonthShort: 'mo',
    deltaUp: '+{amount} kr / {suffix}',
    deltaDown: '−{amount} kr / {suffix}',
    upgradeTo: 'Upgrade to {plan}',
    switchTo: 'Switch to {plan}',
    seatsUnlimited: 'Unlimited',
    seatsOne: '1 user',
    seatsMany: 'Up to {n} users',
    currentBadge: 'current',
    unavailableNow: 'Not available now',
    onAllPlansKicker: 'on all plans',
    featureEncryptedNotes: 'Encrypted meeting notes and calendar links (AES-256)',
    plansSoloTeamVekst: 'Solo · Team · Growth',
    featuresPerPlanKicker: 'features per plan',
    featureShareNotes: 'Share encrypted notes with colleagues',
    featureInviteColleagues: 'Invite colleagues to the company',
    featureCustomerPicks: 'The customer picks a person or just a time',
    featureAdvisorRole: 'Advisor role for invited users',
    featureCustomSender: 'Custom sender address (email)',
    featureAuditLog: 'Audit log',
    featureApiAccess: 'API access',
    plansTeamVekst: 'Team · Growth',
    plansVekst: 'Growth',
    foot: 'The change takes effect immediately. On an upgrade we charge a pro-rata share of the difference today; on a downgrade the surplus is deducted from your next bill.',
    pauseSubscription: 'Pause subscription',
    cancelSubscription: 'End subscription',
  },
  billingDowngradeReassignModal: {
    title: 'Who takes over the customers?',
    kicker: 'downgrade · step 2 of 2',
    lede: 'Each of these loses access at the end of the period. Choose who takes over their upcoming meetings. Notes keep their author, nothing is rewritten.',
    rowLabel: "Who takes over {name}'s customers?",
    confirmCta: 'Schedule downgrade',
    autoDistribute: 'Distribute automatically',
  },
  billingDowngradeSelectionModal: {
    title: 'Who loses access?',
    kicker: 'downgrade · step 1 of 2',
    lede: '{plan} has room for {cap} users. You are {count}. Choose {surplus} who lose access at the end of the period. You decide who takes over their customers in the next step.',
    toggleLabel: 'Select {name}',
    countRemaining: 'Choose {n} more',
    countOver: 'Remove {n}',
    countReady: 'Ready',
  },
  billingPlanCard: {
    contactUs: 'Contact us',
    priceYearly: '{amount} kr / year',
    priceMonthly: '{amount} kr / mo',
    trialFree: '3 months free',
    included: 'included',
    yearlyInvoiceSoon: 'Yearly invoicing coming soon',
    exVatYearly: 'ex. VAT · invoiced yearly',
    exVatMonthly: 'ex. VAT · invoiced monthly',
    seatsUnlimited: 'Unlimited users',
    seats: '1 user | Up to {n} users',
    upgradeTo: 'Upgrade to {name}',
    switchTo: 'Switch to {name}',
    unitYear: 'year',
    unitMonth: 'mo',
    deltaUp: '+{amount} kr / {unit}',
    deltaDown: '−{amount} kr / {unit}',
    recommended: 'Recommended',
    currentPlan: 'Current plan',
  },
  brandingBrandingProfilePanel: {
    previewFallbackName: 'your page',
    logoNotRegistered: 'The logo was uploaded, but it was not registered. Try again.',
    logoUpdated: 'The logo has been updated',
    settingsSaved: 'The settings have been saved',
    saveFailed: 'Could not save. Try again.',
    logoLabel: 'logo',
    logoHint: 'PNG, JPEG, WebP or SVG. Raster at least 128×128 pixels, max 10 MB.',
    displayNameLabel: 'Display name',
    displayNameHint: 'Shown alongside the logo at the top of the booking page.',
    modeLegend: 'How does the customer choose who they book with?',
    modePooledName: 'We assign them',
    modePooledDescription:
      'The customer chooses a service and time. We assign the person automatically.',
    modeChooseName: 'Let the customer choose a person or just a time',
    modeChooseDescription:
      "After the customer has chosen a service, we show a screen where they can choose a specific person, or 'no preference' for the next available.",
    modePickFirstName: 'Let the customer choose a person first',
    modePickFirstDescription:
      'The customer first chooses who they want to talk to, then a service and time.',
    previewKicker: 'preview',
    poweredBy: 'powered by stemne',
    previewHeading: 'Book a meeting with {name}.',
    previewSubPickFirst: 'Choose who you want to talk to.',
    previewSubChoose: 'Choose a service. Then you choose a person or just a time.',
    previewSubPooled: 'We set you up with one of us.',
    previewSubSolo: 'Choose a service and find an available time.',
    saveCta: 'Save changes',
  },
  brandingMeetingPanel: {
    loading: 'Loading meeting backgrounds…',
    lede: 'Choose backgrounds for new video meetings. They appear only in the meeting room, not on your booking page. Existing video rooms are not updated.',
    roomLabel: 'meeting room background',
    roomHint: 'PNG, at least 1400 pixels wide, max 600 kB. Sits behind the video.',
    knockLabel: 'waiting screen background',
    knockHint:
      'PNG, at least 1400 pixels wide, max 600 kB. Shown while the customer waits to be let in.',
    clearCta: 'Remove image',
    roomClearCta: 'Remove meeting room background',
    knockClearCta: 'Remove waiting screen background',
    roomSaved: 'The background will be used in new meeting rooms',
    knockSaved: 'The background will be used on the waiting screen in new meeting rooms',
    cleared: 'The image is removed for new meeting rooms',
    clearFailed: 'Could not remove the image. Try again.',
    notRegistered: 'The image was uploaded but not registered. Try again.',
    loadFailed: 'Could not load the meeting backgrounds.',
    fallbackNote: 'Without your own image the meeting uses the video provider default background.',
  },
  brandingBrandingQrPanel: {
    title: 'QR code for your page',
    desc: 'Download an A4 sheet with the QR code to your booking page. Put it up in reception, add it to business cards, or hand it out at events.',
    previewAlt: 'QR code',
    sheetKicker: 'how the sheet looks',
    layoutLegend: 'Count per A4 sheet',
    layoutSingleName: '1 large',
    layoutSingleDesc: 'One large QR code in the middle of the sheet, for a poster or notice.',
    layoutGrid4Name: '4 pieces',
    layoutGrid4Desc: '2 × 2, good business-card sizes.',
    layoutGrid8Name: '8 pieces',
    layoutGrid8Desc: '2 × 4, medium sized, fill out and cut up.',
    layoutGrid12Name: '12 pieces',
    layoutGrid12Desc: '3 × 4, for handouts and short distribution.',
    downloadCta: 'Download PDF',
  },
  companyAvailabilityPanel: {
    hoursLabel: '{h} hours',
    daysLabel: '{d} days',
    savedToast: 'Availability saved',
    saveErrorToast: 'Could not save. Try again.',
    timezoneLabel: 'time zone',
    timezoneHint: 'Customers in other time zones see the times converted automatically.',
    noticeCardLabel: 'notice and horizon',
    noticeLabel: 'Minimum notice before a meeting',
    noticeHint: 'customers cannot book closer than this',
    horizonLabel: 'How far ahead can they book?',
    rescheduleCardLabel: 'changing the time',
    rescheduleToggleLabel: 'Require approval to change the time',
    rescheduleHint:
      'When this is on, the customer does not move the meeting themselves. Instead they send a suggestion you approve or decline. When it is off, the customer moves a 1:1 meeting directly. Group meetings always go via a suggestion.',
    continuityCardLabel: 'sharing treatment history',
    continuityToggleLabel: 'Share treatment history between practitioners',
    continuityHint:
      "When this is off, each practitioner sees only their own earlier sessions with the customer: the previous note, open follow-up points and pain scores. Turn it on if you keep a shared journal and every practitioner should see each other's history. Applies to treatment only. Training and conversations are always shared across the team.",
  },
  companyBookingNotifyPanel: {
    saveSuccess: 'Notifications saved',
    saveError: 'Could not save. Try again.',
    cardLabel: 'notification on new meeting',
    toggleLabel: 'Notify the whole team about new meetings',
    toggleHint:
      'When this is on, the whole team gets an email and a notification about every new meeting. When it is off, only the advisor who gets the meeting is notified. A meeting without an assigned advisor always notifies the whole team, so someone can take it.',
  },
  companyCompanyProfilePanel: {
    orgnrLabel: 'Organization number',
    orgnrHint: '9 digits. We send receipts and invoices here.',
    nameLabel: 'Company name',
    nameHintFilled: 'Retrieved from the Brønnøysund Register.',
    nameHintEmpty: 'Enter the organization number above and we fill in the name automatically.',
    addressLabel: 'Business address',
    addressHintFilled: 'Retrieved from the Brønnøysund Register.',
    addressHintEmpty:
      'Filled in from the Brønnøysund Register once the organization number is valid.',
    naceLabel: 'Industry (NACE)',
    subdomainLabel: 'Your address on stemne',
    subdomainHint: 'Changing the address is not supported yet, contact support.',
    invoiceEmailLabel: 'Invoice email',
    invoiceEmailPlaceholder: "invoice{'@'}company.com",
    invoiceEmailHint: 'Does not change your login email.',
    contactEmailLabel: 'Contact email for customers',
    contactEmailPlaceholder: "hello{'@'}company.com",
    contactEmailHint:
      'Customers see this address, including on the withdrawal form. Use one you read yourself, not the invoice address.',
    publicToggleLabel: 'Public profile',
    publicHintBefore: 'When this is on, anyone can book you at',
    publicHintAfter:
      '. Turn it off if you only want to receive customers via private booking links (for example key account managers, recruiters). Token-based links keep working.',
    treatmentToggleLabel: 'Do you offer treatment?',
    treatmentHint:
      'Turn it on if you are a physiotherapist, naprapath, podiatrist or similar. Then you can mark services as treatment and get a tailored meeting surface with a plan checklist during the session itself. If you leave it off, Stemne is a conversation booking site.',
    trainingToggleLabel: 'Do you offer training or coaching?',
    trainingHint:
      'Turn it on if you are a personal trainer, running coach or similar. Then you can mark services as training and get a meeting surface built for sessions, with exercises and results. Existing services are not affected when you turn it off.',
    saveCta: 'Save changes',
    dataCardLabel: 'data processing',
    dataCardBody:
      'stemne stores customer and meeting data in the EU (Frankfurt). You can download everything under Export data or request deletion at any time.',
    saveSuccess: 'The settings are saved',
    saveError: 'Could not save. Try again.',
  },
  settingsAboutSectionEditor: {
    cropHint: 'Drag to move, pinch or use the wheel to zoom.',
    cropTitleImage: 'Crop the image',
    cropEyebrowImage: 'image',
    cropTitlePortrait: 'Crop the portrait',
    cropEyebrowPortrait: 'portrait',
    cropEyebrowGallery: 'gallery image',
    uploadNotRegistered: 'The upload was not registered. Try again.',
    uploadFailed: 'Could not upload the image. Try again.',
    readImageFailed: 'Could not read the image. Try another file.',
    galleryFull: 'The gallery holds up to three images.',
    galleryFullAddedFirst: 'The gallery holds up to three images. Added the first ones.',
    cropFailed: 'Could not crop this image. Upload it again.',
    saved: 'The about section is saved',
    saveFailed: 'Could not save. Try again.',
    title: 'About us on the booking page',
    lede: "Tell customers who they'll meet. Fill in what fits you: short text, a portrait, or photos from where you meet. We choose the layout based on what you've entered.",
    preview: 'Preview',
    kickerText: 'text',
    textTitle: 'What customers read',
    textDesc: 'A heading and some body text is enough to publish. The rest is optional.',
    headingLabel: 'Heading',
    headingHint: 'For example, "A bit about how we work".',
    ledeLabel: 'Intro (optional)',
    ledeHint: "Shown large at the top when you don't have images.",
    bodyLabel: 'Body text',
    bodyHint: 'Separate paragraphs with a blank line.',
    kickerQuote: 'quote',
    quoteTitle: 'A sentence that sticks',
    quoteDesc: 'An optional frame around something that says who you are.',
    quoteLabel: 'Quote (optional)',
    quoteByLabel: 'Quote attribution',
    quoteByHint: 'For example, "Hans-Kristian, asd as".',
    kickerDetails: 'details',
    detailsTitle: 'A small meta line',
    credentialsLabel: 'Footer text (optional)',
    credentialsHint: 'Short facts, separated by ·.',
    kickerImages: 'images',
    imagesTitle: 'Portrait or gallery',
    imagesDesc:
      'Upload one portrait if you work solo. If you add gallery images, we show those instead, and the page switches to a wider layout.',
    portraitLabel: 'portrait',
    portraitOpt: 'for solo',
    portraitHint:
      'PNG, JPEG or WebP. At least 512×512 pixels, max 10 MB. We show it large beside the text.',
    portraitAlt: 'Portrait',
    crop: 'Crop',
    replace: 'Replace',
    dropPortrait: 'Drop a portrait',
    galleryLabel: 'gallery',
    galleryOpt: 'up to three',
    galleryHint:
      'Photos from the office or a conversation. The gallery replaces the portrait on the public page.',
    captionPlaceholder: 'Caption',
    dropImage: 'Drop an image',
    allSaved: 'Everything is saved',
    saveCta: 'Save about us',
    closePreview: 'Close preview',
    previewEmpty: 'Fill in content to see how "About us" looks to customers.',
  },
  settingsAuditLogTab: {
    kicker: 'activity log',
    title: 'Who did what, and when',
    body: 'A chronological overview of logins and changes in the workspace. Activity is retained for at least 12 months.',
    columnTime: 'Time',
    columnCategory: 'Category',
    columnActor: 'Performed by',
    columnEvent: 'Event',
    tableAriaLabel: 'Workspace activity',
    searchLabel: 'Search loaded events',
    searchPlaceholder: 'name, event, or category …',
    shownCount: 'Showing {shown} of {loaded} loaded events',
    clearFilters: 'Clear filters',
    filterScope: 'Sorting and filters apply only to events loaded in the table.',
    categoryLogin: 'Login',
    categorySecurity: 'Security',
    categoryTeam: 'Team',
    categoryMeeting: 'Meetings',
    categoryWorkspace: 'Workspace',
    categoryOther: 'Other',
    agFilterPlaceholder: 'Filter …',
    agEquals: 'Equals',
    agNotEqual: 'Does not equal',
    agContains: 'Contains',
    agNotContains: 'Does not contain',
    agStartsWith: 'Starts with',
    agEndsWith: 'Ends with',
    agBlank: 'Is blank',
    agNotBlank: 'Is not blank',
    agBefore: 'Before',
    agAfter: 'After',
    agAnd: 'AND',
    agOr: 'OR',
    agApply: 'Apply',
    agReset: 'Reset',
    agClear: 'Clear',
    agCancel: 'Cancel',
    agNoRows: 'No loaded events match the filters.',
    loadError: 'Could not load the activity log.',
    actionPasskeyRegister: 'registered a passkey',
    actionPasskeyRegisterMagicLinkGrace:
      'confirmed the passkey registration through a sign-in link',
    actionPasskeyLogin: 'logged in with a passkey',
    actionPasskeyDelete: 'removed a passkey',
    actionPasskeyResetTarget: 'reset the passkeys for {target}',
    actionPasskeyReset: 'reset a passkey',
    actionSessionsRevokedAll: 'logged out all sessions',
    actionAdminRoleChangedTarget: 'changed the role of {target} to {role}',
    actionAdminRoleChanged: 'changed a role to {role}',
    newRoleFallback: 'new role',
    actionAdminSuspendedTarget: 'suspended {target}',
    actionAdminSuspended: 'suspended a user',
    actionAdminReactivatedTarget: 'reactivated {target}',
    actionAdminReactivated: 'reactivated a user',
    actionAdminRemovedTarget: 'removed {target}',
    actionAdminRemoved: 'removed a user',
    actionAdminInvitedWithRole: 'invited {email} as {role}',
    actionAdminInvited: 'invited {email}',
    actionAdminInvitedGeneric: 'sent an invitation',
    actionAdminInvitationRoleChangedWithRole: 'changed the role on an invitation to {role}',
    actionAdminInvitationRoleChanged: 'changed the role on an invitation',
    actionAdminInvitationDeleted: 'withdrew an invitation',
    actionBookingCreatedForCustomer: 'created a meeting for {email}',
    actionBookingCreatedForCustomerGeneric: 'created a meeting for a customer',
    actionBookingModeOffice: 'changed the meeting format to office',
    actionBookingModeVideo: 'changed the meeting format to video',
    actionBookingModeChanged: 'changed the meeting format',
    actionBookingsClaimed: 'took {count} meeting | took {count} meetings',
    actionBookingsClaimedGeneric: 'took meetings',
    actionBookingsDistributed:
      'distributed {count} meeting across the team | distributed {count} meetings across the team',
    actionBookingsDistributedGeneric: 'distributed meetings across the team',
    actionBrandUpdatedFields: 'changed the company ({fields})',
    actionBrandUpdated: 'changed the company',
    fieldCity: 'location',
    unknownOperator: 'Unknown operator',
    emptyBody: 'No activity yet. When someone logs in or changes something, it shows up here.',
    loadMore: 'Load more',
    loadingList: 'Fetching the activity log ...',
  },
  settingsBillingTab: {
    refreshStateError: 'Could not fetch the status. Try again.',
    subscriptionResumed: 'Your subscription has resumed',
    resumeError: 'Could not resume the subscription. Try again.',
    subscriptionReopened: 'Your subscription is open again',
    reopenError: 'Could not reopen the subscription. Try again.',
    subtabPlan: 'Plan',
    subtabTillegg: 'Add-ons',
    subtabFakturering: 'Billing',
    loadError: 'Could not load subscription.',
    subscriptionActivated: 'Your subscription is active',
    confirmingPurchase: 'We are confirming your purchase. Reload the page if this message stays.',
    purchaseCancelled: 'You cancelled the purchase. Nothing was changed.',
    smsPurchaseSuccess: 'Thanks. The texts will be added to your account shortly.',
    checkoutError: 'Could not start the purchase.',
    cycleYear: 'yr',
    cycleMonth: 'mo',
    pricePerCycle: '{price} kr / {cycle}',
    seatsUnlimited: '{used} · Unlimited',
    seatsUsed: '{used} of {purchased}',
    seatsTrial: '{used} user | {used} users',
    invoicesEmptyTitle: 'No invoices yet',
    invoicesEmptyTrialTitle: 'No invoices yet.',
    invoicesEmptyTrialSub:
      'The first one arrives {date} when the trial period switches to a paid plan.',
    invoicesEmptyLockedTitle: 'The first invoice is ready.',
    invoicesEmptyLockedSub:
      'Sent {date} when the trial period ends. Stripe calculates the pro-rata amount and shows it to you before you confirm.',
    invoicesEmptySub:
      'Once the first payment goes through, the invoice appears here with a PDF link.',
    title: 'Subscription',
    lede: 'Which plan you are on, the add-ons you have added, and billing.',
    nextBillKicker: 'next bill',
    nextBillExplanation: 'Includes the plan, active add-ons, any adjustments and VAT.',
    subtabsAriaLabel: 'Subscription subtabs',
    downgradePendingKicker: 'scheduled change',
    downgradePendingTitle: 'Downgrade to {plan} is scheduled',
    downgradePendingBody:
      'The change takes effect {date}. Until then everyone keeps the access they have now.',
    downgradeCancelCta: 'Cancel downgrade',
    downgradeCancelTitle: 'Cancel the downgrade',
    downgradeCancelBody:
      'You stay on your current plan and nobody loses access. You can schedule a downgrade to {plan} again later.',
    downgradeCancelConfirm: 'Cancel the downgrade',
    downgradeCancelDismiss: 'Close',
    downgradeStaged: 'The downgrade is scheduled and takes effect {date}.',
    downgradeCancelled: 'The downgrade has been cancelled.',
    downgradeAlreadyPending:
      'You already have a scheduled downgrade. Cancel it before scheduling a new one.',
    downgradeMembersError: 'Could not load the user list. Try again.',
  },
  settingsBrandingTab: {
    subtabProfil: 'Profile',
    subtabQr: 'QR code',
    subtabOmOss: 'About us',
    subtabMoterom: 'Meeting room',
    title: 'Profile & booking',
    lede: 'This is how customers see your page: logo, workplace name, how meetings are booked, and what you tell them about you.',
    subtabsAriaLabel: 'Subtabs for profile and booking',
  },
  settingsCompanyTab: {
    tabSelskap: 'Company',
    tabTilgjengelighet: 'Availability',
    tabVarsling: 'Booking notifications',
    tabMotesteder: 'Meeting locations',
    introTitle: 'Company',
    introLede: 'Company details, availability, notifications and meeting locations.',
    subtabsAriaLabel: 'Company subtabs',
  },
  settingsEditMemberModal: {
    saveError: 'Could not save. Try again.',
    fallbackTitle: 'Member',
    readonlySelf: 'You edit your profile photo, title and presentation in My account.',
    readonlyOther:
      '{name} manages their own profile from My account. Changes here require them to sign in themselves.',
    titleLabel: 'Title',
    titleHint: 'Shown on the public booking page, e.g. "Couples therapist".',
    titlePlaceholder: 'No title set',
    bioLabel: 'Short presentation',
    bioHint: 'Two to four sentences about what you offer.',
    bioPlaceholder: 'No presentation set',
    resetPasskeysTitle: 'Reset passkeys',
    resetPasskeysBody:
      'Removes all passkeys for {name}. They must set up new ones at their next sign-in.',
    resetPasskeysCta: 'Reset',
    roleLabel: 'Role',
    changeRoleCta: 'Change role',
    servicesLabel: 'Services',
    servicesHint: 'Choose which services {name} can be assigned.',
    servicesLoading: 'Loading ...',
    servicesEmpty: 'There are no services yet.',
    reopenTitle: 'Reopen access',
    suspendTitle: 'Suspend access',
    reopenBody: '{name} regains access to the workspace, with the same role as before.',
    suspendBody:
      '{name} loses access immediately, but the account, meetings and notes are kept. You can reopen at any time.',
    reopenCta: 'Reopen',
    suspendCta: 'Suspend',
    removeTitle: 'Remove from the workspace',
    removeBody:
      '{name} loses access immediately. You will be asked for a reason that goes into the audit log.',
    removeCta: 'Remove user',
    editProfileCta: 'Edit profile in My account',
  },
  settingsInviteUserModal: {
    title: 'Invite users',
    emailLabel: 'Email',
    emailHint: 'Separate with a comma or line break. Up to 50 at a time.',
    emailPlaceholder: "anna{'@'}firma.no, jonas{'@'}firma.no\nmari.berg{'@'}firma.no",
    roleForAll: 'Role for everyone',
    roleGroupAria: 'Role',
    roleAdvisor: 'Advisor',
    roleAdmin: 'Administrator',
    roleOwner: 'Owner',
    roleHintOwner:
      'Owners have full access including billing and branding. Choose this only for co-owners.',
    roleHintAdmin:
      'Administrators can manage meetings, customers, services and the team, but not billing.',
    roleHintAdvisor:
      'Advisors see only their own meetings and notes. The default choice for new colleagues.',
    foundCount: '1 email found | {n} emails found',
    conflictSelf: ', you cannot invite yourself.',
    conflictTaken: ', is already invited or a member.',
    sendCount: 'Send {n} invitation | Send {n} invitations',
    sendCta: 'Send invitation',
  },
  settingsMeetingLocationsCard: {
    title: 'Meeting locations',
    lede: 'Addresses where you meet customers. Used when you set up a service or a meeting.',
    loading: 'Loading meeting locations.',
    emptyBody: 'You have no saved meeting locations yet.',
    removeAriaLabel: 'Remove meeting location',
    nameLabel: 'Name',
    namePlaceholder: 'The clinic',
    addressLabel: 'Address',
    addCta: 'Add meeting location',
    confirmRemoveTitle: 'Remove meeting location?',
    confirmRemoveBody:
      'Do you want to remove {name} from your saved meeting locations? Meetings already booked here keep their address.',
    nameTakenError: 'A location with this name already exists',
    saveError: 'Could not save the meeting location. Try again.',
    removeError: 'Could not remove the meeting location. Try again.',
  },
  settingsReasonConfirmModal: {
    reasonLabel: 'Reason (visible in the audit log)',
    reasonPlaceholder: 'Why are you doing this? Write a few words for the log.',
    tooLong: 'The reason is too long.',
    charCount: '{count} / {max} characters',
  },
  settingsSecurityTab: {
    introTitle: 'Security',
    introBefore:
      'stemne uses passkeys (WebAuthn / FIDO2) as the only login method. There are no passwords to leak, and phishing sites cannot use the keys because they are bound to',
    coverageLabel: 'passkey coverage',
    coverageSubPrefix: 'users with at least one passkey · total',
    activeKeys: 'active key | active keys',
    loadingOverview: 'Loading overview...',
    allReadyBefore: 'All users are ready. Ask each of them to add',
    allReadyEmphasis: 'at least',
    allReadyAfter:
      'one backup under My account, that prevents being locked out if a device is lost.',
    missingPasskey: '{count} user is missing a passkey: | {count} users are missing a passkey:',
    completeSetupHint:
      'They complete the setup by clicking the invitation link and registering a key.',
    sessionLengthLabel: 'Session length',
    daysValue: '{count} days',
    sessionLengthBody:
      'Users are asked to confirm with a passkey after {count} days of inactivity, or when they log in from a new device.',
    allowedDomainsLabel: 'Allowed domains',
    allDomains: 'all',
    allowedDomainsBody: 'New users must have an email on an allowed domain.',
    noDomainRestrictionBody:
      'There is no domain restriction. Set this under company settings to limit who can be invited.',
    loginLogsLabel: 'Login logs',
    last90Days: 'Last 90 days',
    loginLogsBody: 'Download a CSV with all successful and aborted attempts.',
    logoutAllTitle: 'Log out everyone immediately',
    logoutAllBody:
      'Ends all active sessions. Users must log in again with a passkey. Use this if you think someone has gained unauthorized access.',
    logoutAllCta: 'Log out everyone',
    confirmTitle: 'Log out everyone?',
    confirmBody:
      'Ends all active sessions. Users must log in again with a passkey. This also logs you out.',
    sessionsEnded: '{count} sessions ended',
    logoutAllError: 'Could not log everyone out.',
  },
  settingsSettingsSaveBar: {
    unsavedChanges: 'Unsaved changes',
    discard: 'Discard',
  },
  settingsTreatmentTab: {
    title: 'Action chips',
    sub: 'These show up as quick buttons during treatment, so you can log what you did without typing. You can edit, delete or add whenever you like.',
    dpaHeading: 'Approve the data processing agreement',
    dpaBody:
      'Action chips are sensitive health data (what is done with the patient). To use them, you need to approve the data processing agreement between your organisation and Stemne. That unlocks action chips and the pain scale.',
    dpaReadLink: 'Read the full data processing agreement',
    dpaAccepted: 'The data processing agreement is accepted.',
    dpaDownloadPdf: 'Download as PDF',
    dpaUnlockCta: 'Accept and unlock',
    addonRequiredHeading: 'Requires the "Treatment journal" add-on',
    addonRequiredBody:
      'The data processing agreement is accepted. The clinical journal also requires the "Treatment journal" subscription add-on, which is not yet available to purchase.',
    lockedHeading: 'Requires a data processing agreement.',
    lockedBody:
      'Action chips are sensitive health data (what is done with the patient). They become available once Stemne has signed the data processing agreement with your organisation. Until then you can note procedures as plain text in "Plan for today" on the treatment itself.',
    removeAria: 'Delete {label}',
    empty: 'No actions yet. Add the ones you use most often.',
    newLabel: 'New action',
    newPlaceholder: 'soft tissue, traction, tape ...',
    addCta: 'Add',
    acceptModalTitle: 'Accept the data processing agreement',
    acceptModalBody:
      'By accepting you confirm the data processing agreement between your organisation and Stemne. Read the full agreement first. After accepting, action chips and the pain scale are unlocked.',
    acceptModalConfirm: 'I accept',
    acceptError: 'Could not save your acceptance. Try again.',
    dpaPartyLabel: 'Data controller',
    dpaPartyNameOrgnr: '{name}, company no. {orgnr}',
    ownerOnly:
      'Only the owner of the business can accept the data processing agreement. Ask the owner to sign in and accept.',
    removeConfirm: 'Delete this action? Historical journals keep the label.',
  },
  settingsUsersTab: {
    title: 'Team',
    memberCount: '{n} member | {n} members',
    withPasskey: '{n} with passkey',
    missingPasskey: '{n} missing',
    pendingCount: '{n} awaiting a reply',
    inviteCta: '+ Invite user',
    searchPlaceholder: 'Search the team',
    colMember: 'member',
    colRole: 'role',
    colStatus: 'status',
    editAria: 'Edit {name}',
    copyEmailAria: 'Copy email address {email}',
    statusSuspended: 'suspended',
    statusActive: 'active',
    statusMissing: 'missing',
    awaitingReply: 'Awaiting a reply',
    statusInvited: 'invited',
    resendTitle: 'Resend invitation to {email}',
    resending: 'sending...',
    resend: 'resend',
    deleteInviteTitle: 'Delete invitation to {email}',
    deleteInviteShort: 'delete',
    footnote: 'Users set up their own passkeys under My account → Passkeys.',
    emailCopied: 'Copied {email}',
    emailCopyFailed: 'Could not copy, are you on https?',
    seatsAvailable: '{n} available seat | {n} available seats',
    seatPrompt: 'You have {seats} on {plan}. Invite colleagues from your team.',
    inviteSentPartial: '{ok} sent · {failed} failed',
    invitesSent: '{n} invitation sent | {n} invitations sent',
    seatCount: '{used} of {max}',
    seatsFullOwnerCount: 'The seats are full ({count}). Upgrade to invite more.',
    seatsFullOwner: 'The seats are full. Upgrade to invite more.',
    seatsFullAdminCount: 'The seats are full ({count}). Ask the owner to upgrade.',
    seatsFullAdmin: 'The seats are full. Ask the owner to upgrade.',
    inviteSendError: 'Could not send the invitations. Try again.',
    roleChanged: "{name}'s role has been changed to {role}",
    roleChangeError: 'Could not change the role.',
    suspended: '{name} is suspended',
    suspendError: 'Could not suspend the user.',
    reactivated: '{name} has access again',
    reactivateError: 'Could not restore access.',
    removed: '{name} has been removed',
    removeError: 'Could not remove the user.',
    inviteResent: 'Invitation resent to {email}',
    inviteResendError: 'Could not resend the invitation.',
    inviteDeleted: 'Invitation for {email} deleted',
    inviteDeleteError: 'Could not delete the invitation.',
    passkeysReset: 'Passkeys removed for {name}',
    passkeysResetError: 'Could not remove passkeys.',
    roleModalTitle: 'Change role?',
    roleModalBody: "Change {name}'s role from {from} to {to}?",
    roleModalConfirm: 'Change role',
    removeModalTitle: 'Remove user?',
    removeModalBody: 'Remove {name} from the workspace? They lose access immediately.',
    removeModalConfirm: 'Remove user',
    suspendModalTitle: 'Suspend access?',
    suspendModalBody:
      'Suspend {name}? They lose access immediately, but the account is kept and can be reopened.',
    suspendModalConfirm: 'Suspend',
    reactivateModalTitle: 'Reopen access?',
    reactivateModalBody: 'Give {name} access to the workspace again, with the same role as before?',
    reactivateModalConfirm: 'Reopen',
    deleteInviteModalTitle: 'Delete invitation?',
    deleteInviteModalBody:
      'Delete the invitation to {email}? The link in the email stops working immediately.',
    deleteInviteModalConfirm: 'Delete invitation',
    resetPasskeysModalTitle: 'Remove passkeys?',
    resetPasskeysModalBody:
      'Remove all passkeys for {name}? They must set up new ones at the next login.',
    resetPasskeysModalConfirm: 'Remove passkeys',
    fabLabel: 'Invite user',
  },
  closeActionPointPrompt: {
    title: 'Close an action point?',
    lead: 'You sent {document} to {customer}. Mark an action point as completed, with the document as proof?',
    closeOne: 'Close with document',
    dismiss: 'No, later',
    overduePrefix: 'overdue {date}',
    closed: 'The action point is closed.',
    closeError: 'Could not close the action point. Try again.',
  },
  keepOwedPrompt: {
    title: 'Keep action points?',
    lead: 'The meeting with {customer} was cancelled. The points below will lapse. Keep any as a standalone follow-up?',
    keepOne: 'Keep',
    dismiss: 'Let them lapse',
    overduePrefix: 'overdue {date}',
    kept: 'The action point is kept.',
    keepError: 'Could not keep the action point. Try again.',
  },
  reassignPointsField: {
    label: 'Reassign action points',
    unassigned: 'Leave them without an owner',
    hint: 'Open action points move to whoever you pick. Leave empty to free them — the active colleagues are then notified.',
  },
  externalDone: {
    title: 'Mark the task as done?',
    lead: '{org} asked you to confirm that a task is done. Tap the button once it is handled.',
    cta: 'Mark as done',
    doneTitle: 'Thank you',
    doneLead: 'The task is recorded as done. {org} has been notified.',
    errorTitle: 'The link does not work',
    errorLead:
      'This link is no longer valid. Get in touch with {org} if you still need to confirm the task.',
  },
  customerDetailActivityFeed: {
    loading: 'Loading history ...',
    emptyBody: "No activity yet. Everything that happens with the customer's data shows up here.",
    chipAll: 'all',
    chipFiles: 'files',
    chipNotes: 'notes',
    chipMeetings: 'meetings',
    showOpens: 'Show opens',
    noChanges: 'No changes yet. Turn on "show opens" to see who has looked at something.',
    atTime: 'at {time}',
    today: 'Today',
    yesterday: 'Yesterday',
    actionFileUploaded: 'uploaded the file',
    actionFileShared: 'shared the file',
    actionFileRevoked: 'revoked the file',
    actionFileDeleted: 'deleted the file',
    actionFileOpened: 'opened the file',
    actionFileDownloaded: 'downloaded the file',
    actionNoteCreated: 'wrote the note',
    actionNoteEdited: 'edited the note',
    actionNoteDeleted: 'deleted the note',
    actionNoteViewed: 'opened the note',
    actionMeetingOpened: 'opened the meeting',
    actionMeetingSessionStopped: 'ended the meeting without marking it as completed',
    actionMeetingSummarySent: 'sent a summary',
    actionCustomerUpdated: 'changed the contact info',
    actionActionPointCreated: 'created a follow-up point',
    actionActionPointCompleted: 'completed a follow-up point',
    actionActionPointCancelled: 'cancelled a follow-up point',
    actionActionPointReassigned: 'reassigned a follow-up point',
    proofManual: 'manually',
    proofClickToken: 'via reminder',
    proofFile: 'document sent',
    actorCustomer: 'The customer',
    actorSystem: 'The system',
    actorTeamMember: 'A team member',
  },
  customerDetailCustomerSearchModal: {
    title: "Search {name}'s history",
    searchPlaceholder: "search {name}'s history ...",
    categoryFacetAria: 'Filter documents by category',
    facetAll: 'All',
    escKey: 'esc',
    hitSummary: '{count} hits with {name}',
    searching: 'searching ...',
    noHits: 'no hits with {name}',
    resultsLabel: 'Search results',
    notesInternal: 'never visible to the customer',
    footerKeys: '↑↓ browse · ↵ open · esc close',
    footerInternal: 'note hits are visible to your team, not to the customer',
    noteFallbackTitle: 'Note',
    openMeetingCta: 'Open the meeting',
    openDocumentCta: 'Open the document',
    openConversationCta: 'Open the conversation',
    sharedByYou: 'shared by you',
    fromCustomer: 'from {name}',
    fromYou: 'you',
    groupMeetings: 'meetings',
    groupNotes: 'notes',
    groupDocuments: 'documents',
    groupMessages: 'messages',
  },
  globalSearch: {
    title: 'Search',
    triggerLabel: 'Search',
    searchPlaceholder: 'Search meetings, customers, notes, services, team and tags …',
    searchPlaceholderMobile: 'Search everything …',
    placeholderPrefix: 'Search for',
    placeholderExamples: [
      'meeting about follow-up',
      'notes about shoulder',
      'customers with a diet plan',
      'customer Ingrid Hansen',
      'document about training',
      'note about nutrition',
    ],
    escKey: 'esc',
    cancel: 'Cancel',
    searching: 'searching …',
    errorGeneric: 'could not search right now',
    resultsLabel: 'Search results',
    hitSummary: '{count} hits',
    noResultsFor: 'No hits for «{query}». Try a name, a service or a tag.',
    didYouMean: 'Did you mean …?',
    noResultShortcuts: 'shortcuts',
    seeAllCustomers: 'See all customers',
    seeAllMeetings: 'See all meetings',
    fallbackOpen: 'open',
    emptyHeading: 'What are you looking for?',
    recentLabel: 'recent',
    contextLabel: 'on this page',
    internalBadge: 'never visible to the customer',
    footerKeys: '↑↓ browse · ↵ open · esc close',
    footerKeysMobile: '↵ open · tap for details',
    typeMoteLabel: 'meetings',
    typeMoteKicker: 'meeting',
    typeOppfolgingLabel: 'follow-ups',
    typeOppfolgingKicker: 'action point',
    typeKundeLabel: 'customers',
    typeKundeKicker: 'customer',
    typeNotatLabel: 'notes',
    typeNotatKicker: 'internal note',
    typeMeldingLabel: 'messages',
    typeMeldingKicker: 'message',
    messageFromYou: 'you',
    typeAgendaLabel: 'agenda',
    typeAgendaKicker: 'agenda item',
    typeTjenesteLabel: 'services',
    typeTjenesteKicker: 'service',
    typeRadgiverLabel: 'team',
    typeRadgiverKicker: 'advisor',
    typeTaggLabel: 'tags',
    typeTaggKicker: 'tag',
    typeDokumentLabel: 'documents',
    typeDokumentKicker: 'document',
    actionOpenMeeting: 'open meeting',
    actionWriteNote: 'write note',
    actionOpenPoint: 'open point',
    actionMarkDone: 'mark done',
    actionOpenCustomer: 'open customer',
    actionNewMeeting: 'new meeting',
    actionOpenNote: 'open note',
    actionOpenMessage: 'open conversation',
    actionOpenAgenda: 'open meeting',
    actionOpenService: 'open service',
    actionOpenProfile: 'open profile',
    actionShowTagged: 'show everything tagged',
    actionOpenDocument: 'open document',
    cardMeetingsToday: 'meetings today',
    cardNewCustomers: 'new customers',
    cardOverdue: 'overdue points',
    cardUpcomingMeetings: 'upcoming meetings',
    cardPendingReply: 'awaiting reply',
    cardWeekMeetings: 'meetings this week',
    cardActiveServices: 'active services',
    cardHiddenServices: 'hidden services',
    cardTotalCustomers: 'total customers',
    previewEmptyTitle: 'Select a result',
    previewEmptyBody: 'The preview shows details without leaving search.',
    previewInternal: 'internal note · never visible to the customer',
    metaWhen: 'when',
    metaService: 'service',
    metaStatus: 'status',
    metaDue: 'due',
    metaCustomer: 'customer',
    metaRole: 'role',
    metaUsage: 'used',
  },
  customerDetailCustomerSearchPanel: {
    searching: 'Searching...',
    noResults: 'No results for "{query}".',
    resultsLabel: 'Search results',
    untitledNote: 'Note',
    groupMeetings: 'Meetings',
    groupNotes: 'Notes',
    groupDocuments: 'Documents',
    groupMessages: 'Messages',
  },
  customerDetailDocumentDetailModal: {
    errLoadActivity: 'Could not load activity.',
    encryptedBadge: 'Encrypted',
    statusActive: 'active',
    statusExpired: 'expired',
    statusRevoked: 'revoked',
    statusReceived: 'received',
    directionOut: 'shared with',
    directionIn: 'received from',
    fileTypeSpreadsheet: 'Spreadsheet',
    fileTypeDocument: 'Document',
    fileTypePresentation: 'Presentation',
    fileTypeImage: 'Image',
    policyView: 'View only',
    policyDownload: 'Download',
    activityShared: 'Shared',
    activityNotified: 'Notified by email',
    activityOpened: 'Opened',
    activityDownloaded: 'Downloaded',
    activityRevoked: 'Access revoked',
    activityExtended: 'Expiry extended',
    activityExpired: 'Expired',
    activityReceived: 'Received from customer',
    activityPolicyChanged: 'Access changed',
    actorCustomer: 'the customer',
    actorSystem: 'the system',
    actorYou: 'you',
    activitySharedInvite: 'Shared in invitation',
    errOpenDocument: 'Could not open the document.',
    confirmRevoke: 'Revoke the share? The customer loses access immediately.',
    errRevoke: 'Could not revoke the share.',
    fallbackTitle: 'Document',
    glyphFallback: 'DOC',
    rowExpiry: 'expiry',
    rowAccess: 'access',
    activityHeading: 'activity',
    activityLoading: 'Loading activity...',
    activityEmpty: 'No activity recorded yet.',
    revokeCta: 'Revoke',
    downloadCta: 'Download a copy',
  },
  customerDetailFilerTab: {
    subAlle: 'All',
    subIntern: 'Internal',
    subDelt: 'Shared',
    subFraKunde: 'From customer',
    stripInternal:
      'Internal files are visible to the team, never to {name}. Tap Share when something should go out.',
    stripShared: 'These files are shared with {name} in the shared room.',
    stripReceived: 'Files {name} has sent you.',
    stripAll:
      'Everything you have about {name}, internal notes, shared files and what the customer has sent.',
    filterAria: 'Filter files',
    uploadCta: 'Upload',
    emptyBody:
      'No files here yet. Upload a document, image or video, it stays internal until you share it.',
    emptyCta: 'Upload file',
  },
  customerDetailMeetingModeChip: {
    video: 'Video',
    office: 'In person',
    treatment: 'Treatment',
    phone: 'Phone',
    trening: 'Training',
  },
  customerDetailNotaterTab: {
    guard:
      'A dated log of what happened. Keep it separate from the prep, which is what you plan to do. Notes are internal, visible to the team, not to {name}.',
    eyebrow: 'new note · {name}',
    composerPlaceholder: 'What happened? What do you want to remember for next time?',
    composerCaption:
      "Write as if {name} could ask to see it, keep to what's relevant to the engagement.",
    saveCta: 'Save note',
    emptyBody:
      'No notes yet. Jot down what you want to remember about {name}, or write a meeting note from a meeting.',
    internalChip: 'internal',
    prepPhase: 'preparation',
    livePhase: 'during the meeting',
    sharedWith: 'shared with {name} as a summary',
    visibleToTeam: 'Visible to the team',
    visibleToColleagues: 'Visible to you and colleagues with access',
    editOnMeeting: 'Edit on the meeting',
    changeSummary: 'Change summary',
    shareAsSummary: 'Share as summary',
    noteLabel: 'note',
    deleteNoteAria: 'Delete note',
    deleteTitle: 'Delete the note?',
    deleteBody: 'The note is deleted permanently.',
    deleteConfirm: 'Delete',
    summaryShared: 'Summary shared.',
    summaryShareFailed: "Couldn't share the summary.",
    unshareFailed: "Couldn't stop sharing.",
    noteSaved: 'The note is saved.',
    noteSaveFailed: "Couldn't save the note.",
    noteDeleteFailed: "Couldn't delete the note.",
  },
  customerDetailNoteShareSummaryModal: {
    kicker: 'note',
    titleEdit: 'Edit summary',
    titlePublish: 'Share summary with the customer',
    hint: 'Write a short summary the customer can read in their own view. You can edit or remove it later.',
    label: 'Summary',
    counter: '{count} / {max} characters',
    notifyLabel: 'Notify the customer by email',
    notifyDescription: 'The customer gets a link to the portal. Only sent on the first share.',
    unshareCta: 'Stop sharing',
    updateCta: 'Update',
    publishCta: 'Share',
  },
  customerDetailPillFilter: {
    lensAlle: 'all',
    lensMoter: 'booking | bookings',
    lensForberedelser: 'preparation | preparations',
    lensFiler: 'file | files',
    lensNotater: 'note | notes',
    lensDokumenter: 'document | documents',
    lensSamtaler: 'conversation | conversations',
    lensHistorikk: 'history',
    pillAriaBase: '{label}, {count}',
    pillAriaUnread: '{base}, {n} new',
    listLabel: 'Filter timeline',
  },
  customerDetailTimelineContextAction: {
    moterTitle: 'Book a new meeting with {name}',
    moterHint: 'click to open the calendar and pick a time',
    dokumenterTitle: 'Share a document with {name}',
    dokumenterHint: 'drag a file in here, or click to choose',
  },
  customerDetailTimelineList: {
    timelineAriaLabel: 'Timeline',
  },
  customerDetailTimelineMessageComposer: {
    sendFailed: 'Could not send the message.',
    removeAttachment: 'Remove attachment',
    placeholderWithFile: 'add a message (optional) ...',
    placeholder: 'write a message ...',
    attachAria: 'Attach a document or image',
    sending: 'Sending ...',
    send: 'Send',
  },
  itemsDocumentItem: {
    statusActive: 'Active',
    statusExpired: 'Expired',
    statusRevoked: 'Revoked',
    statusReceived: 'Received',
    directionOut: 'Shared with the customer',
    directionIn: 'Uploaded by the customer',
    sizeKb: '{n} kB',
    sizeMb: '{n} MB',
    policyView: 'View only',
    policyDownload: 'Download',
    kind: 'Document',
    opened: 'Opened {n}×',
  },
  itemsMeetingChatLog: {
    eyebrow: 'log · chat during the meeting',
    note: 'not shared again',
    customerFallback: 'Customer',
    operator: 'you',
    sharedDocument: 'Shared document',
    retainedBadge: 'retained',
    pinnedBadge: 'pinned',
  },
  itemsMeetingItem: {
    kommende: 'upcoming',
    notat: 'note',
    emptyNote: '(empty note)',
    editSummaryCta: 'edit summary',
    shareSummaryCta: 'share summary',
    forberedelser: 'preparations',
    loading: 'loading...',
    noQuestions: 'no questions',
    motechat: 'meeting chat',
    messageCount: '{n} message | {n} messages',
    retainedLinkCount: '{n} link kept | {n} links kept',
    hide: 'hide',
    showLog: 'show log',
    motechatCaption: 'meeting chat does not count as "conversations", it belongs to the meeting',
    summaryAriaLabel: 'Summary shared with the customer',
    summaryEyebrow: 'summary',
    sharedWith: 'shared with {name}',
    customerFallback: 'the customer',
    forberedelserLoadError: 'Could not load preparations',
    motechatLoadError: 'Could not load the meeting chat',
  },
  itemsMessageItem: {
    senderYou: 'You',
    senderAdvisor: 'The advisor',
    senderCustomer: 'The customer',
    senderMessage: 'Message',
    refLabel: 'about:',
    sharedFile: 'Shared a file',
  },
  prepPrepCanvas: {
    sectionAria: 'Preparation',
    title: 'About {name}',
    sub: 'standing preparation · follows the customer',
    chip: 'survives every meeting',
    lede: 'The standing read on {name} at the top. Below: open threads you follow up across meetings. Points you move up from a meeting land here.',
    notePlaceholder: 'What is important to remember about {name}?',
    threadsLabel: 'to follow up',
    threadsLede: 'Open threads you track across meetings, check them off when resolved.',
    markUndone: 'Mark as unresolved',
    markDone: 'Mark as resolved',
    movedFromMeeting: 'Moved up from a meeting',
    inCard: 'in the customer card',
    addPlaceholder: 'Add something to follow up ...',
    addCta: 'Add',
    attachLabel: 'attachments',
    upload: 'Upload',
    attachEmpty:
      'No files yet. Upload images, video or documents. They stay internal until you share.',
    errSaveNote: 'Could not save the preparation.',
    errAddPoint: 'Could not add the point.',
    errTogglePoint: 'Could not update the point.',
    errRemovePoint: 'Could not remove the point.',
  },
  treatmentActionChips: {
    label: 'actions taken',
    empty: 'No action chips are set up yet. Add them in settings.',
  },
  treatmentHealthDataWarning: {
    trainingAriaLabel: 'Visibility',
    trainingLead: 'Internal, not visible to the customer.',
    trainingBody:
      'Note what you need to follow up on the session, like reps, weight and observations. The customer never sees it.',
    clinicalAriaLabel: 'Privacy',
    clinicalLead: 'Do not write health data or diagnoses.',
    clinicalBody:
      'The plan items are for procedures, "massage left foot", "stretch hamstring", not patient assessments.',
    clinicalCta: 'Accept the data processing agreement to record health data',
    unlockedLead: 'Health data allowed',
  },
  treatmentLastSessionCard: {
    painDelta: 'pain {before} → {after}',
    kicker: 'last time · {date}',
    kickerWithDelta: 'last time · {date} · {delta}',
    showCue: 'show',
    notesShow: 'show notes',
    notesHide: 'hide notes',
    notesLoading: 'loading notes …',
    notesError: 'could not load notes',
    notesEmpty: 'No notes from the previous meeting',
    notesPanelAria: 'Notes from the previous meeting',
    openPrevMeeting: 'open previous meeting',
  },
  treatmentMeetingWrapModal: {
    slotShort: '{date} at {time}',
    closeConfirmTitle: 'Close the summary?',
    closeDirtyBody: 'You have unsaved changes. Save them before closing, or discard them.',
    closeSavingBody: 'The summary is saving now. Wait for it to finish before closing.',
    closeFailedBody: 'The last save failed. Try saving again, or discard your changes.',
    closeSavedBody: 'The summary is saved. You can close it now.',
    closeConfirmAction: 'Close summary',
    closeSaveAction: 'Save and close',
    closeDiscardAction: 'Discard changes and close',
    closeConfirmCancel: 'Keep editing',
    alreadySentNotice: 'The summary was sent {when} to {to}.',
    alreadySentNoticeNoRecipient: 'The summary was sent {when}.',
    ctaResendToCustomer: 'Send again to {name} →',
    ctaResendToParticipants: 'Send again to 1 participant → | Send again to {n} participants →',
    resendConfirmTitle: 'Send the summary again?',
    resendConfirmBody:
      'It was sent {when}. The recipients get a new email with the summary as it reads now.',
    resendConfirmBodyNoDate:
      'It has already been sent. The recipients get a new email with the summary as it reads now.',
    resendConfirmAction: 'Send again',
    resendConfirmCancel: 'Cancel',
    closeSaveError: 'Could not save the summary. Try again before closing.',
    saveFailed: 'Could not save',
    autoIntroTraining: 'Thanks for the session. A short summary:',
    autoExercisesHeading: 'Exercises today:',
    kindCohost: 'co-host',
    kindInvitee: 'participant',
    kindCustomer: 'customer',
    meetingIndexOfTotal: 'meeting {index} of {total}',
    meetingIndex: 'meeting {index}',
    qaMetaLabel: '{answered} of {total} answered · stored encrypted',
    summaryTitleDefault: 'Summary · {service}',
    autoGreetingTeam: 'Hi,',
    autoGreeting: 'Hi {name},',
    autoIntro: 'Thanks for the conversation. A short summary:',
    autoClosing: 'Let me know if anything is unclear.',
    autoSignoff: 'Best regards',
    ctaArchive: 'Archive',
    ctaPickTime: 'Pick a time →',
    ctaSendConfirm: 'Send + invite for {slot} →',
    ctaSendToParticipants: 'Send to 1 participant → | Send to {n} participants →',
    ctaSendToCustomer: 'Send to {name} →',
    errSave: 'Could not save the summary. Try again.',
    errSend: 'Could not send the summary.',
    errNoEmail: 'The customer has no email address.',
    errPickServiceTime: 'Pick a service and a time.',
    errBook: 'Could not book a new time.',
    errFollowup: 'Could not send the summary and invitation. Try again.',
    editSummary: 'Back to summary',
    errFollowupRecovery:
      'We could not check whether the summary and invitation were sent. Try again to retrieve their status.',
    moreActions: 'More actions',
    downloadPdf: 'Download PDF',
    errExportPdf: 'Could not create the PDF.',
    serviceOptionLabel: '{name} · {mins} min',
    modeOffice: 'meeting at the office',
    modePhone: 'phone',
    modeVideo: 'video',
    modeTreatment: 'the clinic',
    modeTraining: 'training',
    modalTitle: 'What do you take forward from this?',
    modalKicker: 'after the meeting',
    qaKicker: 'ask about this, from the prep',
    qaTitle: 'Questions and answers',
    qaEmpty:
      'No questions were prepared. Use the prep notes from the meeting to remember what you talked about.',
    summaryKicker: 'to the customer, freely editable',
    summaryTitle: 'Summary',
    saving: 'saving ...',
    savedEncrypted: 'saved · encrypted',
    autoFilled: 'auto-filled from answers',
    summaryTitlePlaceholder: 'Summary, {service}',
    summaryTitleAria: 'Summary title',
    editorPlaceholder: 'Write a summary ...',
    hintTeam: 'The participants see this if you send it.',
    hintCustomer: '{name} sees this if you send it.',
    includeNotesLabel: 'Include the meeting notes',
    includeNotesDescription: 'Adds the notes when you send. Off by default.',
    includeNotesFormatLegend: 'Format',
    includeNotesTextLabel: 'In the email',
    includeNotesTextDescription: 'Shown at the bottom as plain text.',
    includeNotesPdfLabel: 'PDF file',
    includeNotesPdfDescription: 'Attached as motenotater.pdf.',
    nextKicker: 'what happens now',
    nextTitle: 'Pick one',
    optionSendDesc: 'Email to the customer with a summary.',
    optionSendTitle: 'Send summary',
    optionRescheduleDesc:
      'Send the summary and a separate invitation to a new meeting. The customer must confirm the invitation.',
    optionRescheduleTitle: 'Send + new time',
    optionArchiveDesc: 'Store everything internally, no email. You can send later.',
    optionArchiveTitle: 'Just archive',
    reschedKicker: 'pick service and time',
    reschedTitle: 'Pick a new meeting time',
    reschedServiceLabel: 'Service',
    reschedServicePlaceholder: 'Pick a service ...',
    reschedDateLabel: 'Date',
    slotsLabel: 'Available times',
    slotsLoading: 'Loading ...',
    slotsEmpty: 'No available times this day.',
    slotsUnavailableAhead:
      'No available times in the next 60 days. Choose a later date to keep looking.',
    errLoadAvailability: 'Could not load available times. Try again.',
    recipientsKicker: 'who should get the summary',
    recipientsTitle: 'Recipients',
    recipientsEmpty: 'No participants to send to. Add co-hosts or invitees to the meeting first.',
    recipientDescription: '{email} · {kind}',
    archiveNote:
      'Everything is stored on the meeting without sending anything. You find it again under Earlier and can send the email later, without losing the answers.',
    archiveNoteStrong: '{name} gets no reminder that there is a note.',
    sentKickerReschedule: 'summary and invitation sent',
    sentKickerArchive: 'archived',
    sentKickerSend: 'sent',
    sentTitleReschedule: 'Two things on the way.',
    sentTitleArchive: 'Stored on the meeting.',
    sentTitleTeam: 'Sent to the participants.',
    sentTitleCustomer: 'Sent to {name}.',
    sentRowMailTeamPrefix: 'Summary sent to',
    participantWord: 'participant | participants',
    sentRowMailPrefix: 'Summary sent to',
    sentRowBookedPrefix: 'Invitation sent, awaiting customer confirmation ·',
    sentRowArchived: 'Summary and answers stored encrypted on the meeting.',
    sentCopyReschedule:
      '{name} gets the summary and a separate email to confirm the new time. The meeting is awaiting customer confirmation.',
    sentCopyArchive:
      'You can send the email later from the meeting overview without losing the answers.',
    sentCopyTeam:
      'The participants have received an email with the summary. Your answers are stored encrypted on the meeting.',
    sentCopyCustomer:
      '{name} has received an email with the summary. Your answers are stored encrypted on the meeting.',
    backToMeeting: 'Back to meeting',
    finishWithoutSummary: 'Mark as completed without a summary',
    stopWithoutCompleting: 'End without marking as completed',
  },
  tagChipInput: {
    add: 'tag',
    remove: 'Remove tag',
    suggestions: 'Suggestions',
  },
  externalOwnerState: {
    notSent: 'Not sent',
    sent: 'Reminder sent',
    overdue: 'Overdue',
    done: 'Done',
    markDone: 'Mark done',
  },
  treatmentPlanChecklist: {
    askKicker: 'ask about this',
    askTitle: 'What do we need answers to?',
    askAddLabel: 'add question',
    askEmpty: 'No questions yet. Write down what you want answered.',
    askEmptyLive: 'No questions yet.',
    askPlaceholder: 'Write a question',
    planKicker: 'treat this',
    planTitle: 'Plan for today.',
    planAddLabel: 'add',
    planEmpty: 'No plan points yet. Add what you need to remember.',
    planEmptyLive: 'No plan points yet.',
    planPlaceholder: 'Write a plan point',
    ownerMe: 'me',
    ownerClient: 'customer',
    ownerColleague: 'colleague',
    ownerExternal: 'external',
    ownerLabel: 'owner',
    externalPrefix: 'email',
    externalPlaceholder: "name{'@'}example.com",
    externalFor: 'Email of the external owner for "{text}"',
    duePrefix: 'due',
    dueTimePrefix: 'time',
    dueFor: 'Due date for "{text}"',
    cancelPoint: 'Cancel point',
    cancelPointFor: 'Cancel "{text}"',
    reopen: 'Reopen',
    reopenFor: 'Reopen "{text}"',
    cancelled: 'cancelled',
    answered: 'answered',
    done: 'done',
    markUndone: 'Mark as not done',
    markDone: 'Mark as done',
    editItem: 'Edit "{text}"',
    removeTag: 'Remove tag',
    addTag: 'Add tag',
    tag: 'tag',
    tagPlaceholder: 'tag',
    targetPrefix: 'for',
    targetAll: 'everyone',
    targetFor: 'Who "{text}" is for',
    targetForNew: 'Who the question is for',
    assigneeFor: 'Who follows up "{text}"',
    assigneeForNew: 'Who follows up the point',
    assigneePrefix: 'who',
    resultPrefix: 'result',
    resultFor: 'Result for "{text}"',
    resultPlaceholder: 'What was the outcome',
    moveUp: 'Move up',
    moveDown: 'Move down',
    inCardTitle: 'Saved in the customer card',
    inCard: 'in the customer card',
    promoteTitle: 'Move to the customer card (survives the meeting)',
    promote: 'Move to the customer card',
    delete: 'Delete',
  },
  meetingActionPointsSummary: {
    sectionAria: 'Action points from the meeting',
    planLabel: 'Points',
    askLabel: 'Questions and answers',
    unanswered: 'not answered',
    ownerClient: 'customer',
    ownerExternal: 'external',
  },
  treatmentPriorSessionHistory: {
    resultsLabel: 'Results from last time',
    sectionAria: 'Last time',
    kicker: 'last time',
    noteLabel: 'Note from last time',
    openItemsLabel: 'Open follow-up points from last time',
    canvasLabel: 'From the customer card',
    assigneeClient: 'customer',
    assigneeMe: 'me',
  },
  treatmentQACard: {
    editQuestionAria: 'Edit the question "{text}"',
    answerPlaceholder: 'Write the answer here ...',
    saving: 'saving ...',
    savedEncrypted: 'saved · encrypted',
    answerBlocked: 'You can only answer your own points',
  },
  treatmentTrainingExerciseLog: {
    kicker: 'training log',
    title: 'Log exercises',
    priorHead: 'Last time · {date}',
    removeAria: 'Remove {name}',
    emptyBody: 'No exercises logged yet.',
    nameLabel: 'exercise',
    namePlaceholder: 'E.g. squats',
    setsLabel: 'sets',
    repsLabel: 'reps',
    weightLabel: 'weight (kg)',
    addCta: 'Add',
    loadLabel: '{sets} × {reps}',
    loadLabelWeight: '{sets} × {reps} · {weight} kg',
  },
  treatmentTreatmentMetaCard: {
    statusConfirmed: 'Confirmed',
    statusRescheduled: 'Rescheduled',
    statusPending: 'Pending',
    statusCancelled: 'Cancelled',
    statusCompleted: 'Completed',
    statusNoShow: 'No show',
    slotDayTime: '{day} at {time}',
    seriesNounDefault: 'Treatment',
    seriesOfTotal: '{noun} {index} of {total}',
    seriesIndex: '{noun} {index}',
    subLine: '{service} · {mins} min · {slot}',
    moreActions: 'More actions',
  },
  treatmentTreatmentPrep: {
    customerMessageLabelTraining: 'From the customer: goals and background',
    empty: 'empty',
    planCount: '1 item | {n} items',
    asksCount: '1 question | {n} questions',
    wordCount: '1 word | {n} words',
    notesVisibility: 'you and colleagues with access',
    writeNotesTitle: 'Write the notes.',
    cancelledTitleTraining: 'The session is cancelled.',
    cancelledTitleTherapy: 'The treatment is cancelled.',
    cancelledLede: 'Anything already written is preserved as history and cannot be changed here.',
    cancelledPlanTitle: 'Planned for the appointment',
    cancelledNotesTitle: 'Preserved notes',
    asksTitle: 'What do we need answers to?',
    planTitleTraining: "Today's plan",
    planTitleTherapy: 'Plan for today',
    notesPastTraining: 'Notes from the session',
    notesPastTherapy: 'Notes from the treatment',
    notes: 'Notes',
    notePlaceholder: 'E.g. "remember to ask about parking" or "talk about the cat, Mille".',
  },
  treatmentWherebyEmbed: {
    prestartTitle: 'Ready for the video meeting?',
    prestartBody: 'When you start, the video room opens before you enter the meeting.',
    prestartAction: 'Start meeting',
    kicker: 'video room',
    popoutAriaLabel: 'Open the video room in a new window',
    popoutLabel: 'Open in new window',
    frameTitle: 'Whereby video room',
    poppedTitle: 'The meeting opened in its own window',
    poppedBody: 'This room disconnected, so you only join once.',
    poppedBack: 'Bring the meeting back here',
    poppedFallback: 'Open directly in Whereby',
  },
  treatmentStemneVideoRoom: {
    roomAccessClosed: 'The included meeting time is over. You cannot rejoin this room.',
    roomLabel: 'Stemne video room',
    eyebrow: 'Stemne video meeting',
    readyTitle: 'Ready for the video meeting',
    readyBody: 'Check that your camera and microphone work before joining.',
    checkDevices: 'Check camera and microphone',
    checkingDevices: 'Checking camera and microphone',
    previewTitle: 'Does everything look and sound right?',
    previewBody:
      'Choose the equipment you want to use. You can turn off your camera or microphone before entering.',
    previewYou: 'Preview',
    previewControls: 'Preview controls',
    noCamera: 'No camera found',
    noMicrophone: 'No microphone found',
    noSpeaker: 'No speaker found',
    cameraNumber: 'Camera {number}',
    microphoneNumber: 'Microphone {number}',
    speakerNumber: 'Speaker {number}',
    deviceChangeError: 'Could not switch device. Check that it is still connected.',
    speakerChangeError: 'Could not change speaker. Check that it is still connected.',
    join: 'Join',
    joinWithoutCamera: 'Join without camera',
    joining: 'Preparing the video room',
    permissionError:
      'Stemne could not access your camera or microphone. Check your browser settings and try again.',
    noDevicesError: 'Stemne could not find the camera or microphone. Connect it and try again.',
    joinError: 'The video room could not be opened. Try again.',
    providerFallback: 'Open the backup video room',
    tryAgain: 'Try again',
    cancelJoin: 'Cancel',
    leftTitle: 'You left the video room',
    leftBody: 'The meeting in Stemne is still open.',
    rejoin: 'Join again',
    participantCount: '{count} in the room',
    you: 'You',
    waitingForOthers: 'Waiting for others',
    roomLocked: 'The room is locked.',
    askToJoin: 'Ask to join',
    waitingForHost: 'Waiting for the host',
    reconnecting: 'Reconnecting to the video room',
    kicked: 'The host removed you from the video room.',
    knockRejected: 'The host declined your request.',
    waitingRoom: 'Waiting to join',
    guest: 'Guest',
    admit: 'Admit',
    reject: 'Reject',
    yourScreen: 'Your screen',
    sharedScreen: 'Shared screen',
    controls: 'Video meeting controls',
    devices: 'Devices',
    deviceChoices: 'Camera, microphone and speaker choices',
    camera: 'Camera',
    cameraOn: 'Turn camera on',
    cameraOff: 'Turn camera off',
    microphone: 'Microphone',
    microphoneOn: 'Turn microphone on',
    microphoneOff: 'Mute microphone',
    speaker: 'Speaker',
    share: 'Share screen',
    stopSharing: 'Stop sharing',
    leave: 'Leave',
  },
  widgetsDeltRomCard: {
    title: 'Shared room',
    activeShares: 'active shares',
    receivedFromCustomer: 'received from customer',
    lastMessage: 'last message',
    senderYou: 'You',
  },
  widgetsForberedelserCard: {
    title: 'Preparation',
    kicker: 'preparation',
    openCta: 'open',
    followsSub: 'follows {name} · survives every meeting',
    empty: 'No preparation yet.',
    points: 'points',
    internalFiles: 'internal files',
  },
  widgetsKontaktCard: {
    title: 'Contact',
    company: 'company',
    email: 'email',
    emailPending: 'awaiting confirmation: {email}',
    since: 'since',
    deleteCta: 'Delete customer',
  },
  widgetsOutstandingPaymentsCard: {
    title: 'Payments',
    loading: 'Loading payments …',
    paid: 'Paid',
    outstanding: 'Outstanding',
    documents: 'Receipts, invoices and credit notes',
    requests: 'Outstanding payment requests',
    sale: 'Receipt',
    credit_note_reversal: 'Credit note reversal',
    invoice: 'Invoice',
    credit_note: 'Credit note',
    corrects: 'Corrects {number}',
    remaining: '{amount} outstanding',
    download: 'Download',
    downloadLabel: 'Download document {number}',
    empty: 'No payments yet.',
    summary:
      '{count} outstanding payment · {amount} total | {count} outstanding payments · {amount} total',
    openMeeting: 'Open meeting',
  },
  widgetsCustomerActionPointsCard: {
    title: 'Action points',
    openSummary: '{count} open action point | {count} open action points',
    overdueSummary: '{count} overdue',
    empty: 'No open action points.',
    owner: 'Owner: {name}',
    ownerCustomer: 'the customer',
    ownerExternal: 'external owner',
    ownerUnassigned: 'unassigned',
    noDue: 'no due date',
    due: 'due {date}',
    overdueDue: 'overdue {date}',
    history: 'See completed and cancelled history',
  },
  widgetsNotesPreviewCard: {
    title: 'Internal notes',
    addToggle: 'add',
    hint: 'Visible to the team. The customer can request access',
    composePlaceholder: 'Write a short observation about the customer.',
    saving: 'Saving ...',
    editAria: 'Edit note from {date}',
    deleteAria: 'Delete note from {date}',
    emptyNote: '(empty note)',
    empty: 'No internal notes yet.',
    noteLabel: '(note)',
    errSaveEdit: 'Could not save the change.',
    errSaveNote: 'Could not save the note.',
    errDeleteNote: 'Could not delete the note.',
  },
  editorLinkPrompt: {
    dialogLabel: 'Insert link',
    urlLabel: 'URL (https:// or mailto:)',
    insertCta: 'Insert',
  },
  editorSuggestionMenu: {
    ariaLabel: 'Suggestions',
  },
  sharedAttendeeStack: {
    ariaMore: '{name} and {count} more attendees: {list}',
    attendeeCount: '{count} attendees',
  },
  sharedAvatarStack: {
    you: 'You',
  },
  sharedCandidateTimeChip: {
    primaryMarker: 'suggested',
    promoteLabel: 'Make this the suggested time',
    pendingMarker: 'awaiting approval',
    removeLabel: 'Remove time',
  },
  sharedCookieConsentBanner: {
    ariaLabel: 'Cookie consent',
    body: 'We use cookies to measure how our ads are doing. That means sharing your visit with Google, Meta and OpenAI. Is that okay with you?',
    accept: 'Accept',
    decline: 'Decline',
    currentGranted: 'You have chosen to accept.',
    currentDenied: 'You have chosen to decline.',
  },
  sharedCookieSettingsLink: {
    label: 'Cookie settings',
    labelLower: 'cookie settings',
  },
  sharedParticipantRow: {
    rsvpConfirmed: 'confirmed',
    rsvpPending: 'awaiting reply',
    rsvpDeclined: 'declined',
  },
  sharedPollTally: {
    voterCount: '{n} participant | {n} participants',
    allVoted: 'everyone has voted',
    empty: 'No votes yet.',
    badgeWinner: 'chosen',
    badgeLeading: 'leading',
    worksCount: '{count} works',
    doesntCount: "{count} doesn't work",
  },
  sharedTypewriterRole: {
    roleAdvisors: 'advisors',
    roleTherapists: 'therapists',
    roleConsultants: 'consultants',
    roleStrategists: 'strategists',
    roleCoaches: 'coaches',
    roleMentors: 'mentors',
    roleGuides: 'guides',
    rolePsychologists: 'psychologists',
  },
  sharedVoteRow: {
    suggestedBadge: 'Suggested',
    voteOn: 'Vote on {time}',
    works: 'Works',
    doesntWork: 'Does not work',
  },
  uiAddressAutocomplete: {
    placeholder: 'Search for an address',
  },
  uiAddressBookPicker: {
    searchPlaceholder: 'Search for a place',
    addAddress: 'Add a new address',
    clearAria: 'Remove meeting place',
    nameLabel: 'Name of the place',
    namePlaceholder: 'The clinic',
    addressLabel: 'Address',
    addressPlaceholder: 'Search for an address',
    duplicateNameErr: 'A place with this name already exists',
    saveErr: 'Could not save the place. Try again in a moment.',
  },
  uiConflictWarningDialog: {
    title: '{n} meeting affected | {n} meetings affected',
  },
  uiStemneBirthdateInput: {
    hint: 'For example 3 5 1962',
    dayLabel: 'Day',
    monthLabel: 'Month',
    yearLabel: 'Year',
    errorPrefix: 'Error:',
    errors: {
      digitsOnly: 'Use digits only for the date of birth',
      missingDay: 'Date of birth must include a day',
      missingMonth: 'Date of birth must include a month',
      missingYear: 'Date of birth must include a year',
      dayRange: 'Day must be a number between 1 and 31',
      monthRange: 'Month must be a number between 1 and 12',
      yearDigits: 'The year must have four digits, for example 1962',
      impossible: 'Date of birth must be a real date. {month} {year} has {days} days.',
      future: 'Date of birth must be in the past',
      tooOld: 'Check the year. Date of birth cannot be before {year}.',
      saveRejected: 'The date of birth was rejected. Check that the date is correct.',
    },
  },
  uiStemneDatePicker: {
    placeholder: 'Choose a date',
    dialogLabel: 'Choose a date',
    dialogLabelFor: 'Choose a date for {label}',
    prevMonth: 'Previous month',
    prevYear: 'Previous year',
    prevPeriod: 'Previous period',
    nextMonth: 'Next month',
    nextYear: 'Next year',
    nextPeriod: 'Next period',
    chooseMonthYear: 'Choose month and year',
    chooseYear: 'Choose year',
    weekdayMon: 'mo',
    weekdayTue: 'tu',
    weekdayWed: 'we',
    weekdayThu: 'th',
    weekdayFri: 'fr',
    weekdaySat: 'sa',
    weekdaySun: 'su',
  },
  uiStemneEditor: {
    editorAriaLabel: 'Note editor',
    toolbarLabel: 'Text formatting',
    boldTitle: 'Bold',
    boldGlyph: 'B',
    italicTitle: 'Italic',
    italicGlyph: 'I',
    heading2Title: 'Heading 2',
    heading3Title: 'Heading 3',
    bulletListTitle: 'Bullet list',
    orderedListTitle: 'Numbered list',
    inlineCodeTitle: 'Inline code',
    codeBlockTitle: 'Code block',
    addImageTitle: 'Add image',
    imageAnnotationPrefix: 'Image: ',
    imageUploading: 'Uploading image...',
    imagePasteFailed: 'Could not paste the image. Try again.',
  },
  uiStemneImageCropModal: {
    title: 'Crop the image',
    eyebrow: 'profile photo',
    hint: 'Drag to move, pinch or use the wheel to zoom. The image is saved as a square.',
    saveCta: 'Save the image',
  },
  uiStemneImageDropzone: {
    browse: 'or browse',
  },
  uiStemneImageUpload: {
    imageAlt: 'Image',
    noImage: 'No image',
    replaceCta: 'Replace image',
    uploadCta: 'Upload image',
    finalizeFailed: 'The image was uploaded but could not be saved. Try again.',
  },
  uiStemneModal: {
    discardTitle: 'Unsaved changes',
    discardBody: 'You have unsaved changes. Are you sure you want to close?',
    discardConfirm: 'Discard changes',
    discardCancel: 'Keep editing',
  },
  uiStemnePasskeySudoModal: {
    title: 'Confirm with your passkey',
    body: 'To change passkeys you need to confirm it is you. Use your passkey now to continue.',
  },
  uiStemneSearchAndTag: {
    searchPlaceholder: 'Search',
    tagAriaLabel: 'Filter',
  },
  uiStemneSearchCombobox: {
    placeholder: 'Search',
    createLabel: 'Add new',
    searchFailed: 'The search failed',
    noResults: 'No results',
  },
  uiStemneSearchInput: {
    placeholder: 'Search',
    clearAria: 'Clear {label}',
  },
  uiStemneSegmentedControl: {
    defaultAriaLabel: 'Pick one of several',
  },
  uiStemneSelect: {
    placeholder: 'Choose...',
    countSelected: '{n} selected',
    searchPlaceholder: 'Search...',
    noResults: 'No matches',
    close: 'Close',
  },
  uiStemneTabs: {
    defaultAriaLabel: 'Tabs',
    badgeAria: '{n} unread',
  },
  uiStemneToastContainer: {
    regionLabel: 'Notifications',
    closeLabel: 'Close notification',
  },
  uiTrustStrip: {
    lead: 'Passwordless, phishing-resistant sign-in.',
    body: 'stemne uses passkeys (WebAuthn / FIDO2). No passwords are sent or stored. You sign in with Face ID, Touch ID, Windows Hello or a security key.',
  },
  billingBillingCycleToggle: {
    ariaLabel: 'Billing period',
    monthly: 'Monthly',
    yearly: 'Yearly',
    yearlyWithHint: 'Yearly, {hint}',
  },
  billingPlanUpgradedModal: {
    title: 'Welcome to {plan}',
    kicker: 'your plan has been upgraded',
    featuresTitle: 'What you get now',
    inviteTitle: 'Invite the team',
    inviteLead:
      'You have {seatCap} seats in total. Add up to {inviteCapacity} colleagues now, or skip and do it from Settings.',
    emailPlaceholder: "colleague{'@'}company.no",
    emailAriaLabel: 'Email address {n}',
    removeRowAriaLabel: 'Remove row {n}',
    addRow: 'Add row',
    roleLabel: 'Role',
    roleAdvisor: 'Advisor',
    roleAdmin: 'Administrator',
    skip: 'Skip',
    sendCta: 'Send invitation',
    toastPartial: '{ok} sent · {failed} failed',
    toastSent: '1 invitation sent | {n} invitations sent',
    errorGeneric: 'Could not send the invitations. Try again.',
  },
  bookingBookingMonthCalendar: {
    weekdayMon: 'mo',
    weekdayTue: 'tu',
    weekdayWed: 'we',
    weekdayThu: 'th',
    weekdayFri: 'fr',
    weekdaySat: 'sa',
    weekdaySun: 'su',
  },
  bookingOmOssBand: {
    kicker: 'about us',
  },
  documentsDocumentInlineReader: {
    closePreview: 'Close preview',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    downloadCopy: 'Download a copy',
    detailsAndActivity: 'Details and activity',
    fetchingDocument: 'Fetching the document ...',
    renderingPdf: 'Rendering PDF ...',
    previewUnsupported: 'Preview is not supported for this file type.',
    openError: 'Could not open the document.',
    pdfError: 'Could not display the PDF.',
    fileGlyphFallback: 'FILE',
  },
  documentsFileCard: {
    share: 'Share',
    manage: 'Manage',
    open: 'Open',
    openAria: 'Open {name}',
    privateChip: 'only me',
    meetingChip: 'meeting',
    encryptedBadge: 'Encrypted',
    moreOptionsAria: 'More options for {name}',
    deleteFile: 'Delete file',
  },
  documentsFileLightbox: {
    visibilityInternal: 'visible to the team, not {name}',
    visibilitySharedWith: 'shared with {name}',
    visibilityReceivedFrom: 'received from {name}',
    download: 'Download',
    shareWith: 'Share with {name}',
  },
  documentsFileShelfModals: {
    deleteTitle: 'Delete the file?',
    deleteBody: '"{name}" will be deleted permanently. This cannot be undone.',
    deleteConfirm: 'Delete',
  },
  documentsInternalUploadModal: {
    titleOne: 'Upload a file.',
    titleMany: 'Upload files.',
    kicker: 'upload · internal',
    bannerTeam: 'Kept internal, visible to the team, never to {name} until you share it.',
    bannerPrivate: 'Kept internal, only you see it, never {name} until you share it.',
    dropTitle: 'Choose files',
    dropHint: 'PDF, Word, spreadsheet, image or video · max 50 MB',
    carouselLabel: 'Selected images',
    removeFile: 'Remove {name}',
    previewFile: 'Preview {name}',
    captionLabel: 'Caption (optional)',
    captionPlaceholder: 'Short text for the image',
    addMore: 'Add more',
    whoSeesLabel: 'Who sees it',
    whoSeesAria: 'Who sees the file',
    wholeTeam: 'The whole team',
    onlyMe: 'Only me',
    pinLabel: 'Link to a meeting (optional)',
    pinOnCustomer: 'On the customer',
    tagsLabel: 'Tags (optional)',
    tagsSearchPlaceholder: 'Search or type a new tag and press enter',
    tagsAria: 'Tags',
    noteLabel: 'Internal note (optional)',
    notePlaceholder: 'Why are you saving this?',
    encryptLabel: 'Encrypt file',
    encryptDesc: 'The contents are stored encrypted in cloud storage.',
    dpaAckTitle: 'Upload without encryption',
    dpaAckBody:
      'The data processing agreement must be accepted again before files can be encrypted. Do you still want to upload the file unencrypted?',
    dpaAckConfirm: 'Upload unencrypted',
    uploadCta: 'Upload',
    closePreview: 'Close preview',
    uploadSuccess: 'Uploaded internally.',
    uploadError: 'Could not upload the files.',
    errEncryptionRequired: 'This file must be encrypted. Turn encryption on and try again.',
  },
  documentsManageShareDrawer: {
    actorOp: 'advisor',
    actorCust: 'recipient',
    actorSystem: 'system',
    activityShared: 'Shared by {actor}',
    activityNotified: 'Email sent',
    activityOpened: 'Opened by {actor}',
    activityDownloaded: 'Downloaded by {actor}',
    activityRevoked: 'Withdrawn by {actor}',
    activityExtended: 'Expiry changed by {actor}',
    activityExpired: 'Expired automatically',
    activityReceived: 'Received from {actor}',
    policyChangedView: 'Changed to view only',
    policyChangedDownload: 'Changed to download allowed',
    policyChangedGeneric: 'Access level changed',
    errorNotFound: "Couldn't find the share. Reload the archive.",
    errorConflict: "This action isn't possible now, the share is no longer active.",
    errorGeneric: 'Something went wrong. Try again.',
  },
  documentsPromoteShareModal: {
    title: 'Share with the customer.',
    kicker: 'share internal attachment',
    banner: 'This file is internal now. Pick settings, then it gets shared with {name}.',
    shareWith: 'Share with {name}',
    sharedWith: 'Shared with {name}.',
    shareError: 'Could not share the file.',
  },
  documentsShareDocumentModal: {
    title: 'Share document',
    kicker: 'new share',
    filesLabel: 'Files',
    removeFileAria: 'Remove {name}',
    dropzoneAddMore: 'Add more files',
    dropzonePick: 'Click to choose files or drag and drop',
    dropzoneMeta: 'PDF, Word, spreadsheet, presentation or image. Max 25 MB per file.',
    customerLabel: 'To customer',
    customerLoading: 'Loading customers...',
    customerPlaceholder: 'Search or add a new customer',
    customerCreateLabel: 'Add new customer',
    newCustomerKicker: 'new customer',
    newCustomerNameLabel: 'Name',
    newCustomerNamePlaceholder: 'Enter name',
    newCustomerEmailLabel: 'Email',
    newCustomerEmailPlaceholder: "name{'@'}company.com",
    policyLabel: 'Access',
    policyHint: '"View only" blocks downloading and printing.',
    policyDownload: 'View and download',
    policyView: 'View only',
    expiryLabel: 'Expiry',
    expiryHint: '"No expiry" keeps access until you revoke it.',
    expiry7d: 'In 7 days',
    expiry30d: 'In 30 days',
    expiry90d: 'In 90 days',
    expiryNever: 'No expiry',
    requireLoginLabel: 'Require login',
    requireLoginDesc: 'The customer must open a magic link before the document is shown.',
    notifyEmailLabel: 'Notify by email',
    notifyEmailDesc: 'The customer gets a notice and a link to the room.',
    messageLabel: 'Message (optional)',
    messagePlaceholder: 'Write a short bit of context, it lands in the room above the document.',
    encryptLabel: 'Encrypt file',
    encryptDesc: 'The contents are stored encrypted in cloud storage.',
    uploading: 'Uploading',
    sharing: 'Sharing...',
    submitCta: 'Share document',
    errUnsupportedType:
      'That file type is not supported. Choose PDF, Word, spreadsheet, presentation or image.',
    errEmptyFile: 'The file is empty or corrupted.',
    errTooLarge: 'The file is too large. Max 25 MB.',
    errLoadCustomers: 'Could not load the customer list.',
    errInvalidEmail: 'Invalid email address.',
    errCustomerNotFound: 'Could not find the customer. Reload.',
    errReauth: 'You need to log in again.',
    errEncryptionRequired: 'This file must be encrypted. Turn encryption on and try again.',
    errUploadExpired: 'The upload has expired. Try sharing again.',
    errDuplicate: 'This file is already registered.',
    dpaAckTitle: 'Share without encryption',
    dpaAckBody:
      'The data processing agreement must be accepted again before files can be encrypted. Do you still want to share the file unencrypted?',
    dpaAckConfirm: 'Share unencrypted',
    errShareFailed: 'Something went wrong with the share.',
  },
  documentsShareSettingsFields: {
    accessKicker: 'access',
    downloadTitle: 'Viewing and download',
    downloadDesc: 'The customer can open the document and download it.',
    viewTitle: 'View only',
    viewDesc: 'The customer can read it in the browser, but not download.',
    expiryKicker: 'sharing expires',
    expiry7d: '7 days',
    expiry30d: '30 days',
    expiry90d: '90 days',
    expiryNever: 'No expiry',
    requireLoginLabel: 'Require login',
    requireLoginDesc: 'The customer must be logged in via a magic link to open it.',
    notifyEmailLabel: 'Notify by email',
    notifyEmailDesc: 'The customer gets an email with a link to the shared room.',
    messageKicker: 'message to the customer (optional)',
    messagePlaceholder: 'Write a few words ...',
  },
  documentsShareStatusPill: {
    internal: 'internal',
    received: 'received',
    expired: 'expired',
    revoked: 'withdrawn',
    shared: 'shared',
  },
  meetingMeetingLiveWorkspace: {
    actionsKicker: 'agreed during the meeting',
    actionsTitle: 'Action points',
    notesKicker: 'while the meeting runs',
    notesTitle: 'During the meeting',
    encBadge: 'stored encrypted',
    notesPlaceholder:
      'Jot down what is said, what you agree on, and what you want to remember next time.',
  },
  meetingTopBar: {
    back: 'meetings',
    remaining: '{n} min left',
    untilClose: 'closes in {n} min',
    encrypted: 'stored encrypted',
    finish: 'Finish →',
    participants: '{n} in the room',
  },
  meetingDeadline: {
    includedRemaining: '{n} min left of included meeting time.',
    overtime: 'The included meeting time is over. The video room closes in {n} min.',
    expiredKicker: 'video room',
    expiredTitle: 'The video meeting has ended',
    expiredOperator: 'The video room has ended. You can still summarize the meeting.',
    expiredParticipant: 'The scheduled meeting time is over. You can now close the window.',
  },
  meetingRail: {
    tabsAria: 'Meeting tools',
    noteTitle: 'During the meeting',
    notePlaceholder:
      'Jot down what is said, what you agree on, and what you want to remember next time.',
    pointsTitle: 'Action points',
    asksTitle: 'Ask about this',
    encBadge: 'stored encrypted',
    participantsTitle: 'Participants',
    participantsEmpty: 'No participants in the room yet.',
    unnamedParticipant: 'Participant without a name',
    participantRoleHost: 'host',
    participantRoleCustomer: 'customer',
    participantRoleParticipant: 'participant',
  },
  meetingLogPanel: {
    quickTitle: 'Quick log',
    painBefore: 'Pain before',
    painAfter: 'Pain after',
  },
  meetingMiniCard: {
    restore: 'Back to the meeting',
    restoreAria: 'Back to the meeting with {name}',
    finish: 'Finish the meeting',
  },
  meetingOverlay: {
    teamMeeting: 'Internal meeting',
    durationMins: '{mins} min',
    seriesIndex: 'meeting {n}',
    remaining: '{n} min left',
    videoRunning: 'video running',
    videoPreview: 'before meeting',
    showVideo: 'show',
    hideVideo: 'Hide video',
    resizePanels: 'Resize meeting view and side menu',
    mode: {
      video: 'video',
      office: 'in person',
      treatment: 'treatment',
      trening: 'training',
    },
    tab: {
      notat: 'note',
      punkter: 'points',
      chat: 'chat',
      logg: 'log',
      folk: 'people',
    },
  },
  meetingMeetingChatPanel: {
    statusConnected: 'connected',
    statusConnecting: 'connecting ...',
    empty: 'No messages yet. Write something to get started.',
    pinned: 'pinned',
    pin: 'pin',
    liftToNote: 'lift to note',
    liftToTask: 'lift to task',
    seen: 'Seen',
    foot: 'pinned to this meeting · links and documents are kept',
    sharerYou: 'You',
    sharerAdvisor: 'The advisor',
    sharedFiles: '{sharer} shared {count} files',
    sharedDocument: 'Shared document',
  },
  messagingConversationBubble: {
    tombstone: 'The message was deleted by the advisor.',
    editAria: 'Edit the message',
    actionsAria: 'Message actions',
    edit: 'Edit',
    delete: 'Delete',
    editedLabel: '(edited)',
    seenLabel: 'seen',
  },
  messagingConversationComposer: {
    placeholderNamed: 'write a message to {name} ...',
    placeholder: 'write a message ...',
    deliveryHint: '{name} gets the message by email and can reply to you there.',
    removeAttachment: 'Remove attachment',
    attachLabel: 'Attach a document or image',
    enterHint: '↵ to send',
    sendLabel: 'Send message',
    send: 'Send',
  },
  messagingConversationThread: {
    eventBooked: 'Meeting booked',
    eventRescheduled: 'The meeting was moved',
    eventCancelled: 'The meeting was cancelled',
    eventNoShow: 'The customer did not show up',
    document: 'Document',
    sharedADocument: 'Shared a document',
    batchDocumentsShared: '{n} documents shared',
    statusRevoked: 'Revoked',
    statusExpired: 'Expired',
    emptyBody: 'No messages yet. Write the first one below.',
    conversationAriaLabel: 'Conversation',
    openToSeeFiles: 'Open to see the files',
    seenReceipt: 'seen',
  },
  // See nb.ts for the why. Keys mirror the backend's dot-path exactly.
  notifications: {
    title: {
      newTimeProposed: 'New time proposed',
      pollVoteCast: 'New vote in the poll',
      bookingConfirmed: 'Booking confirmed',
      meetingStartingSoon: 'Meeting starting soon',
      modeChanged: 'Format changed',
      locationChanged: 'Location changed',
      bookingMoved: 'Booking moved',
      newBooking: 'New booking',
      bookingCancelled: 'Booking cancelled',
      bookingNoShow: 'The customer did not show up',
      lateCancellationBillable: 'Late cancellation, consider billing the customer',
      lateRescheduleBillable: 'Late reschedule, consider billing the customer',
      meetingUpdated: 'The meeting has been updated',
      proposalAccepted: 'Your proposed time was accepted',
      proposalDeclined: 'Your proposed time was declined',
      reassignedToYou: 'You got the meeting with {name}',
      reassignedToYouByActor: '{actorName} gave you the meeting with {name}',
      reassignedFromYou: 'The meeting with {name} was transferred to {newAdminName}',
      reassignedFromYouByActor: '{actorName} transferred the meeting with {name} to {newAdminName}',
      newMessageFrom: 'New message from {who}',
      newDocumentFrom: 'New document from {who}',
      newDocumentFromCustomer: 'New document from a customer',
      paymentDisputed: 'Chargeback from the customer',
      refundFailed: 'The refund did not go through',
      refundedExternally: 'Refund recorded from Stripe',
      paymentLinkSent: 'Payment link sent',
      paymentRefunded: 'Payment refunded',
      paymentReminderSent: 'Payment reminder sent',
      paymentSettled: 'Payment received',
      paymentAttemptFailed: 'Payment attempt failed',
      paymentFailed: 'Payment failed',
      paymentExpired: 'Payment expired',
      actionPointDue: 'Action point due',
      mentioned: 'You were mentioned in a note',
      noteComment: 'New comment in a note',
      subscriptionUpdated: 'Subscription updated',
      subscriptionCanceled: 'Subscription ended',
      subscriptionCanceledScheduled: 'Cancellation registered',
    },
    detail: {
      slotTime: '{time}',
      subscriptionUpdated: 'Your plan or add-ons changed.',
      subscriptionCanceled: 'You cannot make changes until you pick a plan again.',
      subscriptionCanceledScheduled: 'You keep access for the rest of the period you paid for.',
    },
  },
  notificationsBuySMSModal: {
    title: 'Buy SMS package',
    kicker: 'SMS balance',
    balancePrefix: 'You have',
    balanceSuffix: 'SMS left.',
    balanceLoading: 'Fetching balance.',
    packName: '{count} SMS',
    packNote:
      "The SMS messages are used for reminders before meetings. They don't expire. When the balance runs out we skip SMS, but the email reminder is sent as usual.",
    buyMoreCta: 'Buy {count} more SMS',
    buyCta: 'Buy {count} SMS',
  },
  notificationsNotificationBell: {
    ariaLabel: 'Notifications',
    ariaLabelUnread: 'Notifications, {count} unread',
    title: 'Notifications',
  },
  notificationsNotificationGroupRow: {
    andMore: '· and {n} more | · and {n} more',
    unreadCount: '{n} new | {n} new',
  },
  notificationsNotificationPanel: {
    dialogLabel: 'Notifications',
    tabAll: 'All',
    tabUnread: 'Unread',
    groupByCustomer: 'Group by customer',
    markAllRead: 'Mark all as read',
    emptyTitle: "You're all caught up",
    emptyBody: "No new notifications. We'll let you know when something happens.",
    byCustomer: 'by customer',
    settings: 'Settings',
    allSeen: 'all seen',
    unreadMeta: '{n} unread',
    dayToday: 'today',
    dayYesterday: 'yesterday',
  },
  notificationsNotificationRow: {
    markReadTitle: 'Mark as read',
    markReadAria: 'Mark {title} as read',
    archiveTitle: 'Archive',
    archiveAria: 'Archive {title}',
    kind: {
      booking: 'booking',
      cancel: 'cancellation',
      reschedule: 'reschedule',
      reminder: 'reminder',
      payment: 'payment',
      payment_settled: 'payment received',
      message: 'message',
      noshow: 'no-show',
      system: 'system',
      late_change: 'late change',
      mention: 'mention',
      document: 'document',
      note_comment: 'comment',
      sms_low: 'sms',
      poll: 'poll',
      action_point_due: 'due',
    },
  },
  notificationsSMSBalanceCard: {
    title: 'SMS balance',
    hint: "SMS reminders are sent before meetings to customers who have given a mobile number. You buy SMS in packages, and they don't expire.",
    unit: 'SMS left',
    meterLabel: '{remaining} of {total} purchased SMS left',
    bannerEmpty:
      "You're out of SMS. Email reminders are sent as usual, but we skip SMS until you top up.",
    bannerLow: "You're starting to run low. Top up before the balance is empty.",
    buyMore: 'Buy more SMS',
    buyPackage: 'Buy SMS package',
    historyTitle: 'Usage',
    reasonTopup: 'Purchased',
    reasonSend: 'Sent',
    reasonRefund: 'Refunded',
    reasonSegment: 'Extra segment',
    reasonAdjustment: 'Adjusted',
  },
  portalBookingCard: {
    summaryRegion: 'Summary from the advisor',
    expandSummary: 'Show the full summary',
    collapseSummary: 'Show less',
  },
  portalBookingDocumentsPanel: {
    openDocument: 'Open {name}',
    chipViewOnly: 'view only',
    chipDownloadable: 'downloadable',
  },
  portalBookingMessagesPanel: {
    advisorFallback: 'the advisor',
    title: 'Chat',
    hint: 'about this meeting',
    loading: 'Loading messages ...',
    emptyBody: 'Write a message to {advisor}. It lands in their inbox.',
    privacy: 'attached to this meeting · {advisor} gets notified',
  },
  portalMeetingCard: {
    lifecycle: {
      upcoming: 'upcoming meeting',
      pending: 'awaiting confirmation',
      paymentPending: 'awaiting payment',
      completed: 'completed meeting',
      cancelled: 'cancelled meeting',
      expired: 'expired meeting',
      noShow: 'did not take place',
      unresolved: 'meeting time has passed',
    },
    messageAriaLabel: 'Message from your advisor',
    agendaAriaLabel: 'Agenda',
    summaryAriaLabel: 'Summary from your advisor',
    paymentAriaLabel: 'Payment',
    outstandingAriaLabel: 'To pay',
  },
  portalPortalConversation: {
    advisorFallback: 'The advisor',
    backAria: 'Back to the room',
    roleLabel: 'advisor',
    conversationAria: 'Conversation',
    emptyBody: 'No conversations yet. Write the first one below.',
    lockedBody: 'You need a meeting with the advisor to start a conversation.',
    bookCta: 'Book a meeting',
    privacyNote: 'only you and {name} see this room',
  },
  portalPortalDocumentsPanel: {
    advisorFallback: 'your advisor',
    sendCta: 'Send a document',
    privacyNote:
      'The documents are only visible to you and {advisor}. Download them if you want to keep them.',
    uploading: 'Uploading...',
    successNote: '{name} sent to {advisor}.',
    sendAnother: 'Send another',
  },
  portalPortalHome: {
    advisorFallbackCap: 'Your advisor',
    advisorFallback: 'your advisor',
    summaryBannerTitleSubject: '{who} shared a summary from {subject}',
    summaryBannerTitle: '{who} shared a summary',
    emptyMeetings: 'No meetings to show.',
    emptyDocuments: 'No documents to show.',
    emptyConversations: 'No conversations yet. Write the first one below.',
    emptyAll:
      "It's quiet here so far. When you have a meeting or share something, it shows up here.",
    statMeetings: 'meeting held | meetings held',
    statSummaries: 'summary | summaries',
    statDocuments: 'document | documents',
    loggedInAs: 'logged in as {email}',
    heroEyebrow: 'shared room',
    heroTitleWithAdvisor: 'You and {name}.',
    heroTitle: 'Your room.',
    heroLeadWithAdvisor:
      "The whole journey with {advisor}{tenant}, gathered in one place: the meetings you've had, the summaries, the documents and the messages, safe behind login.",
    heroLead: 'Your meetings, documents and messages, gathered in one place and safe behind login.',
    summaryAriaLabel: 'New summary',
    markRead: 'Mark as read',
    streamEyebrow: 'timeline',
    streamOrder: 'oldest first',
    timelineAriaLabel: 'Timeline',
    composerLocked: 'You need a meeting with the advisor to start a conversation.',
    bookTime: 'Book a meeting',
    privacyNote: 'only you and {name} see this room',
    forlopEyebrow: 'the journey',
    inContactSince: 'in contact since {date}',
    sendDocumentAriaLabel: 'Send a document',
  },
  portalPortalNextMeetingCard: {
    modeVideo: 'online',
    modeOffice: 'at the office',
    modeTreatment: 'treatment',
    modeTrening: 'training',
    clock: 'at {time}',
    meetingFallback: 'Meeting',
    withAdvisor: 'with {name}',
    today: 'today',
    tomorrow: 'tomorrow',
    inDays: 'in {days} days',
    durationMins: '{mins} min',
    linkOpensBefore: 'the link opens 15 min before',
    eyebrow: 'next meeting',
    placeLabel: 'Location',
    joinMeeting: 'Join the meeting',
    addToCalendar: 'Add to calendar',
    requestNewTime: 'Request a new time',
    cancelMeeting: 'Cancel',
    emptyTitle: 'No upcoming meetings.',
    emptyBody: 'When you schedule a new conversation, it will show up here.',
  },
  portalReschedule: {
    backToPortal: 'Back to my meetings',
  },
  portalPortalPillFilter: {
    alle: 'all',
    moter: 'meetings',
    dokumenter: 'documents',
    samtaler: 'conversations',
    listLabel: 'Filter',
  },
  portalPortalTimelineView: {
    advisorFallbackLower: 'your advisor',
    advisorFallback: 'Your advisor',
    emptyMeetings: 'No meetings to show.',
    emptyDocuments: 'No documents to show.',
    emptyConversations: 'No conversations yet. Send a message below to start.',
    emptyAppointments: 'You have no meetings yet.',
    composerTitle: 'Send a message',
    composerLocked: 'You need a meeting with your advisor to start a conversation.',
    bookTime: 'Book a meeting',
    composerPrivacy: 'only you and {name} see this room',
    uploadTitle: 'Send a document back',
    dropPrimary: 'Click to choose a file or drag and drop',
    dropMeta: 'PDF, Word, spreadsheet, presentation or image. Max 25 MB.',
    uploading: 'Uploading...',
    uploadSuccess: '{name} sent to your advisor.',
    uploadAnother: 'Send another',
  },
  composablesCalendar: {
    blokkert: 'Blocked',
  },
  composablesNorwegianHolidays: {
    nyttarsdag: "New Year's Day",
    offentligHoytidsdag: 'Public holiday',
    grunnlovsdag: 'Constitution Day',
    forsteJuledag: 'Christmas Day',
    andreJuledag: 'Boxing Day',
    palmesondag: 'Palm Sunday',
    skjaertorsdag: 'Maundy Thursday',
    langfredag: 'Good Friday',
    forstePaskedag: 'Easter Sunday',
    andrePaskedag: 'Easter Monday',
    kristiHimmelfartsdag: 'Ascension Day',
    forstePinsedag: 'Whit Sunday',
    andrePinsedag: 'Whit Monday',
  },
  composablesNotificationMappers: {
    today: 'today',
    yesterday: 'yesterday',
    system: 'System',
  },
  composablesUseActiveSessions: {
    minutes: '{m}m',
    hoursMinutes: '{h}h {m}m',
    daysCount: '{n} day | {n} days',
  },
  composablesUseAddPasskey: {
    registrationAborted:
      'Registration cancelled. If you already have a passkey for stemne in iCloud Keychain or 1Password, delete it first and try again.',
    addFailed: 'Could not add passkey.',
  },
  composablesUseAddons: {
    apiIntegrationsTitle: 'API integrations',
    apiIntegrationsDescription:
      'Connect Stemne to CRM, accounting or your own systems. Full access to bookings, customers and invoice data via a REST API.',
    apiIntegrationsTagRestApi: 'REST API',
    apiIntegrationsTagWebhooks: 'Webhooks',
    apiIntegrationsTagApiKeys: 'API keys',
    apiIntegrationsTagCalls: '10,000 calls / mo',
    loadFailed: 'Could not load add-ons.',
    purchaseFailed: 'Could not activate the add-on.',
    cancelFailed: 'Could not cancel the add-on.',
    waitlistFailed: 'Could not add you to the waitlist.',
    errorNoSubscription: 'No active subscription. Choose a plan first.',
    errorRequiresHigherPlan: 'The add-on requires a higher plan. Upgrade first.',
    errorUnavailable: 'The add-on is not available right now. Try again later.',
    errorAlreadyActive: 'The add-on is already active.',
    errorNotFound: 'The add-on does not exist.',
    errorGeneric: 'Something went wrong. Try again.',
  },
  composablesUseAsyncData: {
    defaultError: 'Something went wrong. Try again.',
    defaultForbidden: 'You do not have access to this page.',
  },
  composablesUseAuthRouting: {
    b5Primary: 'Create a new passkey here',
    b5LoginSecondary: 'Log in with your passkey instead',
    loginPrimary: 'Log in with passkey',
    b1AddDeviceSecondary: 'Add this device instead',
    crossDeviceQr: 'Use the passkey from your phone instead (log in)',
    sendMagicLink: 'Send me a login link',
    signupTouchId: 'Create with Touch ID',
    signupFaceId: 'Create with Face ID',
    signupWindowsHello: 'Create with Windows Hello',
    signupSecurityKey: 'Create with a security key',
    securityKeyHelp: 'What is a security key?',
    signupFingerprint: 'Create with fingerprint',
    signupPasskey: 'Create passkey',
    b2TouchIdMac: 'Add Touch ID on this Mac',
    b2FaceId: 'Add Face ID on this device',
    b3WindowsHello: 'Add Windows Hello here',
    b3SecurityKey: 'Add a security key here',
    b4Fingerprint: 'Add fingerprint on this device',
    b5AddPasskey: 'Add passkey here',
    b6AddPasskey: 'Add passkey on this device',
  },
  composablesUseBackNav: {
    overview: 'Back to overview',
    overviewCrumb: 'Overview',
    meetings: 'Back to meetings',
    meetingsCrumb: 'Meetings',
    notater: 'Back to notes',
    notaterCrumb: 'Notes',
    customers: 'Back to customers',
    customersCrumb: 'Customers',
    customerDetail: 'Back to the customer',
    customerDetailCrumb: 'The customer',
    meetingDetail: 'Back to the meeting',
    meetingDetailCrumb: 'The meeting',
    services: 'Back to services',
    servicesCrumb: 'Services',
    availability: 'Back to availability',
    availabilityCrumb: 'Availability',
    calendar: 'Back to calendar',
    calendarCrumb: 'Calendar',
    settings: 'Back to settings',
    settingsCrumb: 'Settings',
    account: 'Back to the account',
    accountCrumb: 'My account',
    help: 'Back to help',
    helpCrumb: 'Help',
  },
  // Behandlingsgrunnlag-fence locked-panel strings, keyed by the booking's
  // service kind (behandling / trening / neutral fallback). Consumed only via
  // useBasisLockCopy — the shared catalog for the meeting page, TreatmentPrep
  // and the wrap modal, so the copy can't drift between surfaces.
  composablesUseBasisLockCopy: {
    titleTreatment: 'Treatment data is locked',
    titleTraining: 'Training data is locked',
    titleNeutral: 'Meeting data is locked',
    bodyBasisTreatment:
      'Plans, notes and summaries become available once you have a treatment basis with this customer.',
    bodyBasisTraining:
      'Plans, notes and summaries become available once you have your own sessions with this athlete.',
    bodyBasisNeutral:
      'Plans, notes and summaries become available once you have your own meetings with this customer.',
    bodyAssignedTreatment:
      'The meeting is assigned to {name}. Treatment data is only visible to practitioners with their own appointments with this customer.',
    bodyAssignedTraining:
      'The session is assigned to {name}. Training data is only visible to coaches with their own sessions with this athlete.',
    bodyAssignedNeutral:
      'The meeting is assigned to {name}. Notes and preparations are only visible to colleagues with their own meetings with this customer.',
    bodySharedNote:
      'Notes from this meeting are shared with you. Other meeting data is only visible to colleagues with their own meetings with this customer.',
    wrapBodyTreatment:
      'The summary becomes available once you have a treatment basis with this customer.',
    wrapBodyTraining:
      'The summary becomes available once you have your own sessions with this athlete.',
    wrapBodyNeutral:
      'The summary becomes available once you have your own meetings with this customer.',
  },
  composablesUseBillingPlanChange: {
    nowOnPlan: 'You are now on {plan}',
    planSolo: 'Solo',
    planTeam: 'Team',
    planVekst: 'Growth',
    planTrial: 'Trial',
    planComp: 'Agreed exemption',
    soloFeatureBookingPage: 'Your own booking page',
    soloFeatureSharedWithCustomers: 'Shared with customers',
    soloFeatureEmailReminders: 'Email reminders',
    teamFeatureSeats: '5 users in the same workspace',
    teamFeatureAssignMeetings: 'Assign meetings to colleagues',
    teamFeatureNotes: "Notes with {'@'}-mentions and shared threads",
    teamFeatureSharedAvailability: 'Shared availability and calendar',
    vekstFeatureSeats: '15 users in the same workspace',
    vekstFeatureRoles: 'Role management: administrator and advisor',
    vekstFeatureJournal: 'Treatment journal with PainScale',
    vekstFeaturePrioritySupport: 'Priority support',
  },
  composablesUseBooking: {
    fetchNotFound: 'The meeting was not found. The link may have expired.',
    fetchFailed: 'Could not load the meeting. Try again.',
    confirmFailed: 'Could not confirm the meeting. Try again.',
    rescheduleSlotTaken: 'This time is no longer available. Pick another time.',
    rescheduleNotDirect:
      'This meeting cannot be changed directly. Contact the advisor to change the time.',
    rescheduleFailed: 'Could not change the time. Try again.',
    cancelFailed: 'Could not cancel the meeting. Try again.',
    proposeAlreadyOpen: 'You already have an open proposal for this meeting.',
    proposeNotChangeable: 'The meeting cannot be changed right now.',
    proposeFailed: 'Could not send the proposal. Try again.',
    cancelSeriesFailed: 'Could not cancel the series. Try again.',
    cancelSeriesNothingLeft: 'The series has no upcoming meetings left. Nothing was cancelled.',
  },
  composablesUsePortalManage: {
    rescheduleSlotTaken: 'This time is no longer available. Pick another time.',
    rescheduleNotDirect:
      'This meeting cannot be changed here. Contact the advisor to change the time.',
    reschedulePastSlot: 'That time is invalid or has passed. Pick another time.',
    rescheduleFailed: 'Could not change the time. Try again.',
    cancelAlreadyGone: 'The meeting has already been cancelled.',
    cancelFailed: 'Could not cancel the meeting. Try again.',
  },
  composablesUseBookingFlow: {
    invalidData: 'Invalid data. Check the form and try again.',
    nameMaxLength: 'The name can be at most {max} characters.',
    emailMaxLength: 'The email address can be at most {max} characters.',
    emailInvalid: 'The email address is invalid.',
    nameRequired: 'Enter your name.',
    emailRequired: 'Enter your email address.',
    slotRequired: 'Pick a time.',
    billingAddressRequired: 'Enter the billing address.',
    subjectMaxLength: 'The subject can be at most {max} characters.',
    messageMaxLength: 'The message can be at most {max} characters.',
    addressMaxLength: 'The address can be at most {max} characters.',
    postCodeMaxLength: 'The post code can be at most {max} characters.',
    postPlaceMaxLength: 'The post town can be at most {max} characters.',
    slotUnavailable: 'This time is no longer available. Pick another time.',
    genericError: 'Something went wrong. Try again later.',
    networkError: 'Could not create the meeting. Check your internet connection.',
  },
  composablesUseBookingMessages: {
    linkExpired: 'The link has expired. Ask for a new one.',
    loadFailed: "We couldn't load the messages. Try again.",
    sendFailedTooLong: "The message couldn't be sent. Try a shorter text.",
    sendFailed: "We couldn't send the message. Try again in a moment.",
    sendEmpty: "You can't send an empty message.",
  },
  composablesUseBrand: {
    roleAdmin: 'Administrator',
    roleAdvisor: 'Advisor',
  },
  composablesUseBulkImport: {
    fileTooLarge: 'The file is too large, max 5 MB.',
    fileEmpty: 'The file looks empty. Check that it has headers and at least one row.',
    fileNoRows: 'The file has headers but no rows.',
    fileTooManyRows: 'The file has too many rows ({count}). Max {max}.',
  },
  composablesUseCalendarDrag: {
    pastSlot: "Can't move the meeting to a time that's already passed.",
    moved: 'Moved to {label}',
    moveFailed: "Couldn't move the meeting.",
  },
  composablesUseCalendarSlotInteractions: {
    reasonBooked: 'Booked',
    meeting: 'meeting',
    busy: 'busy',
    reasonBlocked: 'Blocked',
    reasonBuffer: 'Buffer around a meeting',
    reasonAllDay: 'All day',
    reasonPast: "Can't create meetings in the past",
    reasonOutsideHours: 'Outside working hours',
    reasonUnavailable: 'Not available',
    statusPending: 'Pending',
    statusCompleted: 'Completed',
    statusUnresolved: 'Unresolved',
    statusNoShow: 'No show',
  },
  composablesUseCalendarViewState: {
    viewModeDay: 'Day',
    viewModeWeek: 'Week',
    viewModeMonth: 'Month',
    weekHeading: 'Week {n} · {range}',
  },
  composablesUseConversation: {
    loadError: "We couldn't load the conversation, try again.",
    sendError: "Couldn't send the message.",
    editError: "Couldn't edit the message.",
    deleteError: "Couldn't delete the message.",
  },
  composablesUseCurrentAdmin: {
    roleOwner: 'Owner',
    roleAdmin: 'Administrator',
    roleAdvisor: 'Advisor',
    loadProfileError: 'Could not load the profile',
  },
  composablesUseAnalytics: {
    periodWeek: 'Last 7 days',
    periodMonth: 'Last 30 days',
    periodQuarter: 'Last quarter',
    deltaSame: 'same as previous period',
    deltaChange: '{sign}{n} from previous period',
  },
  composablesUseCustomerAccess: {
    rateLimited: 'Too many attempts. Wait a moment and try again.',
    sendLinkFailed: "We couldn't send the link. Try again in a moment.",
    genericError: 'Something went wrong. Try again.',
  },
  composablesUseCustomerDocuments: {
    linkExpired: 'The link has expired.',
    loadFailed: 'Could not load documents.',
  },
  composablesUseCustomerTimelineV2: {
    weekdaySunday: 'Sunday',
    weekdayMonday: 'Monday',
    weekdayTuesday: 'Tuesday',
    weekdayWednesday: 'Wednesday',
    weekdayThursday: 'Thursday',
    weekdayFriday: 'Friday',
    weekdaySaturday: 'Saturday',
    monthJanuary: 'january',
    monthFebruary: 'february',
    monthMarch: 'march',
    monthApril: 'april',
    monthMay: 'may',
    monthJune: 'june',
    monthJuly: 'july',
    monthAugust: 'august',
    monthSeptember: 'september',
    monthOctober: 'october',
    monthNovember: 'november',
    monthDecember: 'december',
    todayLabel: 'Today · {weekday} {day} {month}',
    customerSince: 'Customer since {date}',
    loadCustomerError: 'Could not load the customer.',
    ghostBookMeeting: 'book a meeting',
    ghostShareDocument: 'share a document',
    shareSummaryError: 'Could not share the summary.',
    unshareSummaryError: 'Could not stop sharing.',
    searchNotConfigured: 'Search is not configured for this installation.',
    searchError: 'Could not search.',
  },
  composablesUseCustomerUpload: {
    unsupportedType:
      'That file type is not supported. Choose a PDF, Word, spreadsheet, presentation or image.',
    emptyFile: 'The file is empty or damaged.',
    tooLarge: 'The file is too large. Max 25 MB.',
    linkExpired: 'The link has expired. Ask for new access.',
    accountNotFound: 'We could not find your account. Ask for new access.',
    unsupportedTypeShort: 'That file type is not supported.',
    uploadUnavailable: 'Uploading is temporarily unavailable. Try again.',
    uploadFailed: 'Something went wrong with the upload.',
  },
  composablesUseInviteeMeeting: {
    meetingNotFound: 'The meeting was not found. The link may have expired.',
    loadFailed: 'Could not load the meeting. Try again.',
    confirmFailed: 'Could not confirm your attendance. Try again.',
    declineFailed: 'Could not save your response. Try again.',
    proposalPending: 'You already have an open proposal for this meeting.',
    meetingNotChangeable: 'The meeting cannot be changed now.',
    proposalFailed: 'Could not send the proposal. Try again.',
  },
  composablesUseMeetingChat: {
    loadFailed: 'Could not load the messages.',
    sendFailed: 'The message was not sent.',
    pinFailed: 'Could not update the pin.',
    liftFailed: 'Could not lift the message.',
  },
  composablesUseMeetingEditSession: {
    modeVideo: 'Video meeting',
    modeOffice: 'At the office',
    modeTreatment: 'Treatment',
    modeTrening: 'Training',
    weekdaySunday: 'Sunday',
    weekdayMonday: 'Monday',
    weekdayTuesday: 'Tuesday',
    weekdayWednesday: 'Wednesday',
    weekdayThursday: 'Thursday',
    weekdayFriday: 'Friday',
    weekdaySaturday: 'Saturday',
    monthJanuary: 'January',
    monthFebruary: 'February',
    monthMarch: 'March',
    monthApril: 'April',
    monthMay: 'May',
    monthJune: 'June',
    monthJuly: 'July',
    monthAugust: 'August',
    monthSeptember: 'September',
    monthOctober: 'October',
    monthNovember: 'November',
    monthDecember: 'December',
    slotFallback: '{date} at {time}',
    slotFull: '{weekday} {month} {day} at {time}',
    joinLast: '{head} and {last}',
    titleTime: 'New time',
    titleDuration: 'Changed duration',
    durationMinutes: '{mins} min',
    titleMode: 'Changed meeting format',
    titleLocation: 'Changed location',
    titleAgenda: 'Changed agenda',
    titleDocuments: 'Changed documents',
    titleGreeting: 'Changed greeting',
    defaultAddress: 'Default address',
    multiSummary: 'We have updated the meeting: {list}.',
    addedAgenda: 'added {n} item | added {n} items',
    removedAgenda: 'removed {n} item | removed {n} items',
    addedDocuments: 'added {n} document | added {n} documents',
    removedDocuments: 'removed {n} document | removed {n} documents',
    sentenceTime: 'We have moved the meeting to {summary}.',
    sentenceDuration: 'The duration has changed to {summary}.',
    sentenceMode: 'The meeting format has changed to {mode}.',
    sentenceLocation: 'The location has changed to {summary}.',
    sentenceLocationDefault: 'The location has been updated.',
    sentenceAgenda: 'We have updated the agenda for the meeting.',
    sentenceDocuments: 'We have updated the documents for the meeting.',
    sentenceGreeting: 'We have updated the greeting in the invitation.',
    sentenceDefault: 'We have updated the meeting.',
    phraseTime: 'new time',
    phraseDuration: 'new duration',
    phraseMode: 'new meeting format',
    phraseLocation: 'new location',
    phraseAgenda: 'updated agenda',
    phraseDocuments: 'updated documents',
    phraseGreeting: 'changed greeting',
    phraseDefault: 'changes',
  },
  composablesUseMeetingNotes: {
    loadFailed: 'Could not load notes',
    saveStandaloneFailed: 'Could not save standalone note',
    saveFailed: 'Could not save note',
    saveDraftFailed: 'Could not save draft',
    conflict: 'A newer version was saved by another session. Reload and try again.',
    saveRevisionFailed: 'Could not save version',
    deleteFailed: 'Could not delete note',
  },
  composablesUseNoteComments: {
    loadFailed: 'Could not load comments.',
    commentAccessDenied: 'You have read access, but not comment access to this note.',
    postFailed: 'Could not send comment.',
    deleteFailed: 'Could not delete comment.',
  },
  composablesUseNoteDuplicate: {
    duplicateError: 'Could not duplicate the note. Try again.',
  },
  composablesUseNoteLink: {
    loadError: 'Could not load the meetings, try again.',
    saveError: 'Could not change the link, try again.',
  },
  composablesUseNoteRename: {
    renameFailed: 'Could not change the title, try again.',
  },
  composablesUseNotesTips: {
    live1Body:
      'Paste a screenshot straight into the note, or use the camera icon. The image stays in the note and follows it into the PDF you can download afterwards.',
    live1Link: 'Writing meeting notes',
    live2Body:
      'Bold, subheadings and bullet lists survive all the way into the PDF. Use them on what you will want to find again in six months.',
    live2Link: 'Formatting in notes',
    live3Body:
      'Type an at-sign and a colleague name to give them access to the note without leaving the meeting.',
    live3Link: 'Sharing notes with colleagues',
    live4Body:
      'Under "points" you can record who does what, and by when. It lands on the summary the customer receives.',
    live4Link: 'Action points after the meeting',
    live5Body:
      'This note is internal. What the customer sees is the summary you write when you end the meeting.',
    live5Link: 'What the customer sees',
    tip1Body:
      'Paste in links, earlier emails, or clips from the last meeting. The notes follow you into the meeting and stay in the archive afterwards.',
    tip1Link: 'How to use prep notes',
    tip2Body:
      'Use short question lists, the things you want to remember to ask about, rather than a fully written script. You get into the flow faster during the meeting.',
    tip2Link: 'What fits in prep',
    tip3Body:
      'Prep and meeting notes are two separate tracks. Keep "things I planned to ask about" apart from "things the customer actually said".',
    tip3Link: 'Why kept apart from meeting notes',
    tip4Body:
      'Write "Remember to ask if the child is well again", the personal touch that makes follow-ups genuine. The notes are only for you and colleagues with access.',
    tip4Link: 'What fits in prep',
    tip5Body:
      "You can {'@'}-mention a colleague in a note, they get a notification and read access, even if they don't normally see your meetings.",
    tip5Link: "How to {'@'}-mention a colleague",
    tip6Body:
      'Type `/` in the middle of a note for a quick formatting menu, headings, lists, code, bold.',
    tip6Link: 'The formatting shortcuts',
    tip7Body:
      "As an administrator you can see others' meetings, but private services are edited only by the owner. Access openings are logged.",
    tip7Link: "What an administrator can and can't see",
    tip8Body:
      "Does the customer need to see the context? That's deliberate friction, the notes are internal. Use the message thread if you want to share something.",
    tip8Link: 'Send a message to the customer',
    tip9Body:
      'The notes sync as you write. You can close the tab mid-sentence and continue on another, the last character is in place.',
    tip9Link: 'Where the meeting notes are stored',
    tip10Body:
      "The service can be set as private. Then other advisers don't see the name, email, or notes, just that the time slot is taken.",
    tip10Link: 'How the private flag works',
  },
  composablesUseOrgActivity: {
    weekdaySunday: 'Sunday',
    weekdayMonday: 'Monday',
    weekdayTuesday: 'Tuesday',
    weekdayWednesday: 'Wednesday',
    weekdayThursday: 'Thursday',
    weekdayFriday: 'Friday',
    weekdaySaturday: 'Saturday',
    monthJanuary: 'January',
    monthFebruary: 'February',
    monthMarch: 'March',
    monthApril: 'April',
    monthMay: 'May',
    monthJune: 'June',
    monthJuly: 'July',
    monthAugust: 'August',
    monthSeptember: 'September',
    monthOctober: 'October',
    monthNovember: 'November',
    monthDecember: 'December',
    dayToday: 'Today · {base}',
    dayYesterday: 'Yesterday · {base}',
    verbBooked: 'booked {title}',
    verbCancelled: 'cancelled {title}',
    verbRescheduled: 'moved {title}',
    verbCompleted: 'completed {title}',
    verbNoShow: 'did not show up for {title}',
    verbMessage: 'sent a message',
    verbDocumentIn: 'sent a document',
    verbDocumentShared: 'shared a document in {title}',
    verbDocumentGot: 'got a document',
    verbCustomer: 'became a customer',
    verbNoteShared: 'shared a note with you',
    verbBookingShared: 'shared {title} with you',
    meetingFallback: 'meeting',
    aMeetingFallback: 'a meeting',
    metaCancelled: '{meta} · cancelled',
    metaNoShow: '{meta} · no show',
    metaMessageExcerpt: '"{excerpt}"',
    policyView: 'view only',
    policyDownload: 'download',
    metaDocumentReceived: '{name} · received',
    customerViaBookingLink: 'via their booking link',
    customerAddedManually: 'added manually',
    modeOffice: 'in person',
    modeTreatment: 'treatment',
    modeVideo: 'video',
    loadError: 'Could not load activity.',
  },
  composablesUsePasskeyLogin: {
    hintNoPasskeyForDevice:
      "We can't find a passkey for this email on this device. Send yourself a login link, it also works from a new browser window.",
    hintDeviceDeclined:
      'The device found no passkey for this account. Send yourself a login link instead.',
    hintFinishRejected: 'Login failed. Send yourself a login link, or try the passkey again.',
    hintUnknown: 'We found no passkey for this device. Send yourself a login link to log in.',
    hintStaleRpId:
      "You need to enroll a new passkey on this account. We'll send you a login link instead.",
    errorGeneric: "We can't reach the login service right now. Try again in a moment.",
  },
  composablesUsePasskeySudoReauth: {
    accountNotFound: 'We could not find your account. Log in again.',
    noPasskey: 'No passkey registered on this device. Log in again to continue.',
    staleRpId:
      'Your passkey needs to be renewed. Log in again with a login link and enroll a new passkey.',
    cancelled: 'You cancelled. Try again to continue.',
    authFailed: 'Authentication failed. Try again.',
    genericError: 'Something went wrong. Try again.',
  },
  composablesUsePlanFeatures: {
    loadError: 'Could not load plan features.',
  },
  composablesUsePortalMessages: {
    linkExpired: 'The link has expired. Ask for a new one.',
    loadFailed: "We couldn't load the conversations. Try again.",
    needBooking: 'You need a meeting with the advisor to start a conversation.',
    sendTooLong: "The message couldn't be sent. Try a shorter text.",
    sendFailed: "We couldn't send the message. Try again in a moment.",
  },
  composablesUsePriorBookings: {
    loadFailed: 'Could not load past meetings.',
  },
  composablesUsePrivacy: {
    rateLimited: 'Too many requests. Try again shortly.',
    generic: 'Something went wrong. Try again.',
    exportFailed: 'Could not fetch your data.',
    deleteFailed: 'Deletion failed.',
  },
  // Customer-facing role noun, resolved from the service kind (#2014). English
  // has no definite noun form, so `definite` carries the article the Norwegian
  // suffix supplies ("the trainer" for "treneren").
  composablesUseRoleNoun: {
    advisorLabel: 'Advisor',
    advisorIndefinite: 'advisor',
    advisorDefinite: 'the advisor',
    therapistLabel: 'Therapist',
    therapistIndefinite: 'therapist',
    therapistDefinite: 'the therapist',
    trainerLabel: 'Trainer',
    trainerIndefinite: 'trainer',
    trainerDefinite: 'the trainer',
  },
  composablesUseRoom: {
    loadFailed: 'Could not load the room.',
  },
  composablesUseSMSCredits: {
    loadBalanceError: 'Could not fetch SMS balance',
    startPurchaseError: 'Could not start the purchase',
  },
  composablesUseActionPoints: {
    loadError: "Couldn't load action points.",
  },
  composablesUseCustomerActionPoints: {
    loadError: "Couldn't load this customer's action points.",
  },
  composablesUseServicesList: {
    loadError: "Couldn't load services.",
    created: 'The service is created.',
    saved: 'The changes are saved.',
    removed: 'The service is deleted.',
  },
  composablesUseSlots: {
    fetchSlotsError: 'Could not load available times. Try again.',
  },
  composablesUseSlotsRange: {
    fetchFailed: 'Could not fetch availability.',
  },
  composablesUseStripePortal: {
    openPortalError: 'Could not open the customer portal.',
  },
  composablesUseSudoAction: {
    somethingWrong: 'Something went wrong.',
    cancelled: 'You cancelled, try again if you want to.',
    authFailed: 'Authentication failed.',
    registrationFailed: 'Registration failed.',
  },
  composablesUseTeamMeetings: {
    busyDefault: 'Some participants are already booked',
  },
  composablesUseTenant: {
    loadError: 'Could not load settings. Try reloading the page.',
  },
  composablesUseTimePoll: {
    pollNotFound: 'The poll could not be found. The link may have expired.',
    loadFailed: 'Could not load the poll. Try again.',
  },
  composablesUseTreatment: {
    loadFailed: 'Could not load treatment data',
    startFailed: 'Could not start the treatment',
    finishFailed: 'Could not finish',
    addFailed: 'Could not add',
    updateFailed: 'Could not update',
    deleteFailed: 'Could not delete',
    saveAnswerFailed: 'Could not save answer',
    saveExerciseFailed: 'Could not save the exercise',
    deleteExerciseFailed: 'Could not delete the exercise',
    saveJournalFailed: 'Could not save journal',
    loadChipsFailed: 'Could not load action chips',
    addChipFailed: 'Could not add chip',
    updateChipFailed: 'Could not update chip',
    deleteChipFailed: 'Could not delete chip',
  },
  composablesUseTreatmentFraming: {
    trainingPrepTitle: 'Prepare the session.',
    trainingPrepLede:
      'The notes are only visible to you and colleagues with access. See what worked last time, and write down what you need to follow up on today.',
    trainingCtaStart: 'Start the session →',
    trainingCtaResume: 'Continue the session →',
    trainingServiceFallback: 'Training session',
    trainingSeriesNoun: 'Session',
    trainingPersonNoun: 'athlete',
    trainingPlanKicker: 'do this',
    trainingPlanTitle: "Today's plan.",
    trainingPinnedLabel: 'Session actions',
    trainingFinishTitle: 'End the session?',
    trainingClientFollowupTitle: 'Your plan for the next session',
    trainingClientVisibilityHint: 'Visible to the athlete',
    therapyPrepTitle: 'Prepare the treatment.',
    therapyPrepLede:
      'The notes are only visible to you and colleagues with access. Check what worked last time, and write down what you need to follow up on today.',
    therapyCtaStart: 'Start the treatment →',
    therapyCtaResume: 'Continue the treatment →',
    therapyServiceFallback: 'Treatment',
    therapySeriesNoun: 'Treatment',
    therapyPersonNoun: 'patient',
    therapyPlanKicker: 'treat this',
    therapyPlanTitle: 'Plan for today.',
    therapyPinnedLabel: 'Treatment actions',
    therapyFinishTitle: 'End the treatment?',
    therapyClientFollowupTitle: 'Your plan for next time',
    therapyClientVisibilityHint: 'Visible to the customer',
  },
  composablesUseTurnstile: {
    verifyFailedWithCode:
      'We could not complete the bot check (code {code}). Reload the page, or try another browser.',
    verifyFailed: 'We could not complete the bot check. Reload the page, or try another browser.',
    loadFailed: 'The bot check could not load. Reload the page, or try another browser.',
    unsupported: 'Your browser does not support the bot check. Try another browser.',
  },
  composablesUseUpcomingMeeting: {
    startsNow: 'starting now',
    underOneMin: 'in under 1 min',
    inMins: 'in {mins} min',
  },
  composablesUseUpload: {
    aborted: 'The upload was cancelled',
    networkError: 'Network error. Check your connection and try again.',
    unavailable: 'Image upload is temporarily unavailable',
    mustReLogin: 'You need to log in again',
    genericError: 'Something went wrong with the upload',
    fileTooLarge: 'The file is too large. Max 10 MB.',
    videoTooLarge: 'The video is too large. Max 300 MB.',
    contentTypeNotAllowed: 'The file type is not supported. Try another format.',
    rejected: 'The upload was rejected. Try again.',
    fileEmptyOrCorrupt: 'The file is empty or corrupt. Try another file.',
  },
  composablesUseWebhookEndpoints: {
    unknownError: 'Unknown error',
  },
  utilsCustomerCsv: {
    colName: 'Name',
    colEmail: 'Email',
    colMeetingCount: 'Number of meetings',
    colLastMeeting: 'Last meeting',
    colNextMeeting: 'Next meeting',
    colValue: 'Total price',
    colTags: 'Tags',
    filename: 'customers.csv',
  },
  utilsHelpActions: {
    titleInviteAdvisor: 'Open Settings → Team and start inviting an advisor',
    titleInviteAdmin: 'Open Settings → Team and start inviting an administrator',
    titleInviteOwner: 'Open Settings → Team and start inviting an owner',
    titleManageTeam: 'Open Settings → Team',
    titleEditBrand: 'Open Settings → Profile and booking',
    titleEditCompany: 'Open Settings → Company',
    titleEditPersonalProfile: 'Open My account',
    titleConnectCalendar: 'Open Calendar and connect Google or Outlook',
    titleManageCalendar: 'Manage connected calendar',
    titleViewAvailability: 'Open Availability',
    titleMagicLinkLogin: 'Request a magic login link',
    titleAddPasskey: 'Add a new passkey to your account',
    titleChangeEmail: 'Change the email address on your account',
    titleViewNotifications: 'Open My account, the Notifications tab',
    titleViewAuditLog: 'Open Settings → Activity log',
    titleManageBilling: 'Open Settings → Billing',
    titleViewInvoices: 'View past invoices',
    titleConnectFiken: 'Connect Stemne to Fiken',
    titleManagePayments: 'Open Payments in settings',
    titleViewMeetings: 'Open Meetings',
    titleNewMeeting: 'Create a new meeting on behalf of a customer',
    titleInstantMeeting: 'Start a "Meet now" room',
    titleViewCustomers: 'Open Customers',
    titleExportCustomers: 'Export the customer list as CSV',
    titleImportCustomers: 'Import multiple customers from CSV',
    titleViewServices: 'Open Services',
    titleAddService: 'Add a new service',
    titleAddTimeOff: 'Add a time-off period',
    titleAddDayOverride: 'Add a one-time change for a single day',
  },
  utilsInvoiceBasisCsv: {
    colCustomer: 'Customer',
    colEmail: 'Email',
    colService: 'Service',
    colCategory: 'Category',
    colStatus: 'Status',
    colCount: 'Count',
    colMinutes: 'Minutes',
    statusHeld: 'Completed',
    statusNoShow: 'No-show',
    filenamePrefix: 'invoice-basis',
  },
  utilsMessageTemplates: {
    greetingNamed: 'Hi {name},',
    greeting: 'Hi,',
    proposal: 'unfortunately I have to move our meeting. Does {day} {time} work for you?',
    instruction:
      'Please confirm whether the new time works, or use the calendar picker to suggest another.',
    daySunday: 'Sunday',
    dayMonday: 'Monday',
    dayTuesday: 'Tuesday',
    dayWednesday: 'Wednesday',
    dayThursday: 'Thursday',
    dayFriday: 'Friday',
    daySaturday: 'Saturday',
  },
  utilsPasskeyLabel: {
    icloudKeychain: 'iCloud Keychain',
    googlePasswordManager: 'Google Password Manager',
    synced: 'Synced passkey',
    deviceBound: 'Device-bound passkey',
  },
  utilsRefundPolicy: {
    short:
      'You can cancel anytime from the dashboard and further charges stop immediately. The current period is not refunded, not even for unused days.',
    bulletPeriod: 'The subscription runs period by period, monthly or yearly.',
    bulletCancel: 'You can cancel anytime from the dashboard. Further charges stop immediately.',
    bulletNoRefund:
      'The current period is not refunded, regardless of when in the period you cancel.',
    bulletNoProration: 'Unused days are not refunded on cancellation, upgrade or downgrade.',
    bulletSupport:
      "If you have questions about a specific invoice, write to support{'@'}stemne.no.",
  },
  // Group departures (stories/09-group-departures.md).
  departures: {
    service: {
      toggleLabel: 'Scheduled departure',
      toggleDesc: 'Customers book seats on departures you schedule, instead of free times.',
      capacityLabel: 'Seats per departure',
      capacityHint: 'The default for new departures. You can change it per departure.',
      capacityError: 'Enter a number from {min} to {max}.',
      help: 'A scheduled departure always takes place in person. The meeting point is the address below.',
    },
    admin: {
      kicker: 'departures',
      title: 'Departures',
      sub: 'Scheduled departures with seats. Open a departure to see the guest list.',
      listLabel: 'Upcoming departures',
      loadError: 'We could not load the departures.',
      rosterError: 'We could not load the guest list.',
      rosterLoading: 'Loading the guest list',
      unavailableTitle: 'Departures are not available',
      unavailableBody: 'Scheduled departures are not switched on for you yet.',
      emptyTitle: 'No upcoming departures',
      emptyBody: 'Schedule the first departure so customers can book seats.',
      createCta: 'New departure',
      createTitle: 'New departure',
      createDesc: 'Date and time are in {zone}.',
      createError: 'We could not create the departure.',
      createMissing: 'Choose a service, date, time and guide.',
      createInPast: 'The departure must start in the future.',
      created: 'Departure added',
      noServices:
        'No service is set up as a scheduled departure. Turn on “Scheduled departure” on a service first.',
      serviceLabel: 'service',
      servicePlaceholder: 'Choose a service',
      dateLabel: 'date',
      timeLabel: 'time',
      guideLabel: 'guide',
      guidePlaceholder: 'Choose a guide',
      capacityLabel: 'seats',
      seatsOf: '{held} of {capacity} seats',
      bookingCount: '{count} booking | {count} bookings',
      total: 'Total',
      backToList: 'All departures',
      cancelledBadge: 'Cancelled',
      cancelledWithReason: 'Cancelled: {reason}',
      cancelledHeading: 'Cancelled',
      rosterEmptyTitle: 'No guests yet',
      rosterEmptyBody: 'Bookings on this departure show up here.',
      cancelCta: 'Cancel departure',
      cancelTitle: 'Cancel departure',
      cancelBody:
        '1 guest gets an email with the reason and a link to book another departure. | All {count} guests get an email with the reason and a link to book another departure.',
      cancelBodyEmpty: 'No guests have booked this departure yet.',
      cancelKeep: 'Keep departure',
      cancelledGuestsHeading: 'Guests who were booked',
      guestSeatsHint: 'The guest and their party, up to {n}.',
      reasonLabel: 'Reason',
      reasonHint: 'Shown in the email to the guests, for example “Road closed due to bad weather”.',
      cancelError: 'We could not cancel the departure.',
      cancelled: 'Departure cancelled',
      addGuestCta: 'Add guest',
      addGuestTitle: 'Add guest',
      addGuestError: 'We could not add the guest.',
      guestAdded: 'Guest added',
      guestName: 'Name',
      guestEmail: 'Email',
      guestPhone: 'Phone (optional)',
    },
    public: {
      zoneLabel: 'Times are shown in local time for {zone} ({abbrev}).',
      loading: 'Loading departures',
      loadError: 'We could not load the departures.',
      emptyTitle: 'No departures available',
      emptyBody: 'There are no upcoming departures for this tour right now.',
      seatsLeft: '{count} seat left | {count} seats left',
      full: 'Sold out',
      tooFewSeats: 'Not enough seats',
      seatsLabel: 'Number of seats',
      seatsHint: 'You and everyone travelling with you, up to {max}.',
      seatsCount: '{count} seat | {count} seats',
      departureLabel: 'departure',
      meetingPoint: 'Meeting point',
      when: '{date} at {time} ({zone})',
      rebookInvalid:
        'The link to book again has expired or has already been used. You can still pick a departure and book as usual.',
      gone: 'We can no longer find this departure. Pick another departure.',
      // Departure-service variants of the booking-flow copy (stories/09).
      pickHeading: 'Choose a departure',
      pickSub: 'Each departure lasts {duration}.',
      durationHours: '{h} h',
      durationHoursMins: '{h} h {m} min',
      perPerson: '{price} per person',
      perPersonShort: 'per person',
      meetingPointLine: 'Meeting point: {place}',
      meetingPointPending: 'Meeting point is given in the confirmation',
      guide: 'guide',
      guideLabel: 'Guide',
      phoneHint: 'Used only for an SMS reminder 24 hours before departure, if it is switched on.',
      noteLabel: 'Anything we should know?',
      notePlaceholder: 'For example allergies or accessibility needs.',
      confirmSubmit: 'Confirm booking',
      terms:
        'You can cancel the booking. The time cannot be changed, but you can book a new departure.',
      confirmedMeta: 'booking confirmed',
      confirmedSub:
        'A confirmation has been sent to {email}. You can manage the booking from here at any time.',
      confirmedManage: 'Manage booking',
      rebookArrival:
        'Your tour was cancelled. Pick a new departure, we have filled in your details.',
    },
    manage: {
      noReschedule:
        'This is a scheduled departure, so the time cannot be changed. To travel on another day, cancel and book a new departure.',
      eyebrow: 'your departure',
      heading: 'Departure with {name}',
    },
  },
}
